import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts'
import './studio/studio.css'
import {
  Btn, Callout, Modal, NumberField, Panel, PillGroup, RangeField, SelectField,
  SettingsDrawer, StatCard, SubHead, TimeField, Toggle,
} from './studio/ui'
import {
  BacktestPanel, CostPanel, DepEditor, DiagnosticsPanel,
  DiagramsPanel, FleetPanel, SetRoster,
} from './studio/panels'
import type {
  CarriageSet, ClassDemand, CostModel, Departure, DemandRow, EngineParams, FleetCaps, FleetLoco, Network, SetFormation,
  Scenario, SetPlan, Slot, SlotDailyRow, StudioMode, StudioPayload, Traction, UntimedDailyRow, UntimedRow,
} from './studio/types'
import {
  DEFAULT_AVERSION_SHARE, DEFAULT_INTERCHANGE_PENALTY_MIN, DEFAULT_MCT_MIN,
  DEFAULT_SHUTTLE_COACHES, DEFAULT_SHUTTLE_TURNAROUND_MIN, DEFAULT_TRANSFER_PLATFORMS,
  DEFAULT_WAIT_WEIGHT, buildNetwork, journeyMiles, journeyMin, observedNrTraversals, routeLabel,
  turnaroundAt, withPathBudget,
} from './studio/network'
import {
  automaticSource, baselineProbeDate, blendParts, chooseSalesBasis, chooseSeedSource,
  datesInSeason, groupRunningDates, readBankedView, seasonMonths, unscorableColour,
  type BankedView, type SeasonCandidate, type SeasonDayGroup,
} from './studio/season'
import {
  buildMonth, colourIssues, planDemand, rescoreMonth, scoreColourPlan, scoreMonthCurrent,
  type BuildEnv, type GroupDemand, type MonthWtt, type SeasonDayPlan,
} from './studio/seasonBuild'
import {
  BASIC_CLASSES, CLASS_LABELS, FINE_CLASSES, buildClassDemand, classWeights, fmtMin,
  timedRunningDays, toMin, totalWeight, type CensoringReport,
} from './studio/demand'
import {
  ELASTICITY, baselineDeps, capOf, depSeats, gbp, runClasses, runScenario, setLabel,
} from './studio/engine'
import { chainNext as chainNextOf, legLocoCodes } from './studio/diagrams'
import { DEFAULT_SETS, applySetPlan, modeSets, planSets, setSeats } from './studio/stock'
import { crossingConflicts, pathUsage, stockImbalances } from './studio/constraints'
import { DEFAULT_FLEET, DEFAULT_COST_MODEL, costFromDeps, costLines, costModelRates } from './studio/costs'
import {
  SeasonPlanPanel, depsToPlanRows, PLAN_COLOURS,
  type SeasonPlanData, type SeasonPlanMonth, type SeasonPlanDay, type SeasonPlanPattern,
  type SeasonDayDetail, type PlanSideDetail, type TicketSplitRow,
} from './studio/SeasonPlanPanel'

/** Deep clone so nested Rate edits never mutate the shared default model.
 *  Per-rate editing now lives in the Cost model stage (CostModelStage). */
const cloneCostModel = (m: CostModel): CostModel => JSON.parse(JSON.stringify(m))
import { bestFit, rescueTrains, type FitConfig } from './studio/bestfit'
import { ilpBuild } from './studio/ilpEngine'
import { runBacktest, type BacktestResult } from './studio/backtest'
import {
  findCandidate, findResult, runAdvisor, type AdvisorCandidate, type AdvisorRunLog,
  type AdvisorSettings, type CandidateResult,
} from './studio/advisorClient'
import { ExploreChat } from './studio/ExploreChat'
import { ResearchSchedule, buildDayModels, type DayModel } from './ResearchSchedule'
import { buildTtPatterns, slotTimes } from './ResearchTimetable'
import { yearTotals, type YearRow, type YearSideSnap } from './studio/YearComparePanel'
import {
  buildExecReport, type ExecReportData, type ExecSide,
  type SeasonPoster, type PosterPattern, type PosterMonth, type PosterWork,
  type CutGroupRow, type SeasonSummary, type PlanWttPattern, type MonthPnl, type MonthPnlRow,
  type ExecMonthCatCosts, type ExecExampleDay, type ExecExampleDayService,
  type ExecDaySide, type ExecWssMonth, type ExecWssWeek, type ExecWssCell, type ExecAppendixMonth,
} from './studio/execPdf'
import { CurrentTurns } from './studio/CurrentTurns'
import { StudioShell } from './studio/StudioShell'
import { InterventionsMenu, type ReviewMonth } from './studio/BriefReviewPanel'
import { MonthFinancialPanel } from './studio/MonthFinancialPanel'
import { DEFAULT_TTI, normaliseTti, ttiLabel, withTti, type TtiConfig } from './studio/tti'
import { ProgrammeNav, type ProgrammeSliceRow } from './studio/ProgrammeNav'
import { BriefStage } from './studio/stages/BriefStage'
import { CostModelStage, catPerDay } from './studio/stages/CostModelStage'
import { ZERO_ACTIVITY, buildDayCostModel, buildLegacySeed, buildSim, pnlDayCost as pnlDayCostFn, pnlDayLines, pnlDayVariable, pnlDayFixedShare, pnlMonthFixed, pnlServiceVariable, type Activity, type CostConfig, type StudioCostsResponse } from './studio/costLines'
import { CalendarStage, type SavedSetRow } from './studio/stages/CalendarStage'
import { BaselineStage } from './studio/stages/BaselineStage'
import {
  STAGE_DEFS,
  type StageKey, type StageDef,
} from './studio/shellTypes'
import { listSessions, loadSession, createSession, updateSession, deleteSession, renameSession, saveSlice, seasonRollup, builtSlice } from './studio/session'
import { applyOneWayToday, type MonthFixChange, type MonthIssue } from './studio/seasonBuild'
import { oneWayShares, stampOneWay } from './studio/engine'
import { listRunningSets, loadRunningSet, createRunningSet, updateRunningSet, deleteRunningSet, setDefaultRunningSet } from './studio/runningSets'
import { loadBaseline, patternForSlice, wttToDeps, pullmanWttDeps, pullmanServices, type BaselineWtt } from './studio/baseline'
import { mapDateActuals, pairLikeDates, type DateActual, type DateActualsResult } from './studio/dateActuals'
import { valueDepsOnDay, daySide, addDay, emptyTotals, isNoStandardServiceDay, type DayPlanShape, type DaySideFigures, type SideTotals } from './studio/perDateValue'
import { fleetCapsFrom, withEngineActivity, type EngineActivity } from './studio/fleetRegister'
import { listLocos } from './studio/locoApi'
import { DEFAULT_FIRECON, FIRECON_MONTHS, fireconDays, fireconScale, fireconTotalDays, normaliseFirecon, type FireconConfig } from './studio/firecon'
import { availableOn, engineOutOn } from './studio/fleetAvailability'
import { FleetPlannerPanel } from './studio/stages/FleetPlannerPanel'
import type { ExecFleetEngine, ExecFleetMonth } from './studio/execPdf'
import { buildRoster, patternTurns, rosterLabelsFor, turnKeyOf, type EnginePin, type RosterDate, type RosterDay } from './studio/engineRoster'
import { estimateDiagrams } from './studio/diagrams'
import { engineLabel } from './studio/fleetRegister'
import { RollingStockPanel } from './studio/stages/RollingStockPanel'
import { buildStudioWorkbook, type StudioXlsxFleet, type StudioXlsxMonth } from './studio/studioXlsx'
import { availabilityGrid } from './studio/fleetAvailability'
import { LOCO_CATEGORY, ROLLING_STOCK_CATEGORY } from './studio/fleetRegister'
import type {
  CostBreakdown, ModellingSessionMeta, RunningDay, SessionSlice, SessionStatus, SliceStatus,
} from './studio/types'

// Timetable Studio V3 — docs/RESEARCH_STUDIO_V3_PLAN.md.
//
// This component is the orchestrator: state, the network build, and composition.
// The model lives in studio/*, the chrome in studio/ui.tsx + studio.css, and the
// screen furniture in studio/panels.tsx. V2 had all of it in one file with forty
// inline-styled controls in a single wrapping row, which is why it had become
// hard to read and impossible to extend.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const num0 = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })

/** Which day-of-week columns a class covers, for the Weekly Service Schedule. */
const CLASS_DOWS: Record<string, number[]> = {
  Sat: [6], Sun: [7], Wkdy: [1, 2, 3, 4, 5], WkdyH: [1, 2, 3, 4, 5], WkdyT: [1, 2, 3, 4, 5],
}

/** The model-settings block of the railway defaults — Chris's hard barriers
 *  on the algorithm, saved so a slice can never silently change them. */
interface EngineDefaults {
  tolerance: number
  recapturePct: number
  dwellMin: number
  farePct: number
  maxStay: boolean
  forceEarly: boolean
  spillNearest: boolean
  returnMode: 'latest' | 'observed'
  maxUnreturned: number
  minRevPerDep: number
  /** LEGACY — migrated by adoptSources(). */
  disrupted?: DisruptedPeriod
  /** Where each month's data comes from (Chris, 2026-08-25). */
  monthSources?: MonthSources
  /** Firecon days — steam stood down for fire risk (Chris, 2026-09-11; firecon.ts). */
  firecon?: FireconConfig
  /** The operating window (Chris, 2026-10-28: "I endlessly keep resetting the
   *  Operating window and every time I come back I find it has not changed" —
   *  it was never saved, and every load re-derived it from the real WTT). */
  window?: { firstTrain: string; lastTrain: string }
  /** Empty-stock moves run diesel; correct demand for censoring. */
  ecsDiesel?: boolean
  censoring?: boolean
  /** Pullman dining modelled by day / by night. */
  pullman?: { day: boolean; night: boolean }
  /** TTI — on-train ticket sales uplift (studio/tti.ts). */
  tti?: TtiConfig
}

/**
 * Months whose OWN sales must not be used, because something happened that the
 * railway does not intend to repeat.
 *
 * Chris, 2026-08-25: **"it isn't going this year, that is a one off because
 * Bridge 42 was closed."** March to May 2026 sold as a Pickering-Goathland
 * shuttle — zero Whitby bookings in March, fourteen passengers in April — so
 * modelling those months on their own sales models a severed railway.
 *
 * This is DECLARED, never inferred. Automatically preferring last season
 * whenever it happened to be better would quietly rewrite genuinely poor
 * trading as good trading, which is the opposite of law 8. The planner names
 * the months and the reason; the reason is printed wherever the figures are.
 */
interface DisruptedPeriod {
  /** 'YYYY-MM' keys excluded from their own sales. LEGACY — migrated on load
   *  into `monthSources` (each excluded month simply points at the season
   *  before it). Kept only so a saved default from before 2026-08-25 reads. */
  months: string[]
  /** Why — printed on screen and in the board paper. */
  reason: string
}

/**
 * WHERE EACH MONTH'S DATA COMES FROM — the planner's explicit choice.
 *
 * Chris, 2026-08-25: "for each month give me a drop down which lists the running
 * season I can take that months data from — that is the data, the sales, the
 * capacity, the turns, the timetable you will use to generate data."
 *
 * Month key -> season label. An absent or empty entry means "automatic": let
 * `chooseSalesBasis` pick the most recent season that finished trading and
 * describes the railway we intend to run. `reason` is printed wherever the
 * figures appear.
 */
interface MonthSources {
  by: Record<string, string>
  reason: string
}


interface PerCls {
  untimedNet: number
  untimedPax: number
  prevRev: number | null
  prevPax: number | null
  prevUntimedNet: number | null
  hasPrev: boolean
}

export function ResearchStudio({ enabled, upn }: { enabled: boolean; upn?: string }) {
  // ---- source data --------------------------------------------------------
  // The sales payload as fetched; `data` is it WITH the TTI uplift applied
  // (studio/tti.ts) — ONE place, so every reader sees the same demand.
  const [rawData, setData] = useState<StudioPayload | null>(null)
  /** TTI — on-train ticket sales (rebuild item 9, Chris 2026-10-28). */
  const [tti, setTti] = useState<TtiConfig>(DEFAULT_TTI)
  const data = useMemo(() => (rawData ? withTti(rawData, tti) : null), [rawData, tti])
  const [error, setError] = useState<string | null>(null)
  const [monthKey, setMonthKey] = useState('')
  const [dayClass, setDayClass] = useState('Week')
  /** Where each month's data comes from — the planner's explicit choice. */
  const [monthSources, setMonthSources] = useState<MonthSources>({ by: {}, reason: '' })

  // ---- mode + network -----------------------------------------------------
  const [mode, setMode] = useState<StudioMode>('through')
  const [mctMin, setMctMin] = useState(DEFAULT_MCT_MIN)
  const [platforms, setPlatforms] = useState(DEFAULT_TRANSFER_PLATFORMS)
  const [penaltyMin, setPenaltyMin] = useState(DEFAULT_INTERCHANGE_PENALTY_MIN)
  const [waitWeight, setWaitWeight] = useState(DEFAULT_WAIT_WEIGHT)
  const [aversionPct, setAversionPct] = useState(Math.round(DEFAULT_AVERSION_SHARE * 100))
  const [nrPaths, setNrPaths] = useState(0) // 0 = use today's observed usage
  const [steamOverNR, setSteamOverNR] = useState(false)
  const [shuttleTurn, setShuttleTurn] = useState(DEFAULT_SHUTTLE_TURNAROUND_MIN)
  const [shuttleCoaches, setShuttleCoaches] = useState(DEFAULT_SHUTTLE_COACHES)

  // ---- demand + operating assumptions ------------------------------------
  const [tolerance, setTolerance] = useState(75)
  const [recapturePct, setRecapturePct] = useState(70)
  const [farePct, setFarePct] = useState(100)
  // Lock fares (Chris, 2026-08-20): he set 100% and the advisor "just started
  // increasing them" — the model's job is to serve the same demand at HIS
  // parameters with fewer engines, turns and crews, not to reprice. ON by
  // default; while locked no candidate may move farePct and applying one
  // cannot change it.
  const [faresLocked, setFaresLocked] = useState(true)
  const [dwellMin, setDwellMin] = useState(180)
  const [maxStay, setMaxStay] = useState(false)
  // Which optimiser builds the timetable: 'claude' = the greedy Best fit (the
  // default, proven engine), 'ilp' = the Advanced ILP engine (Chris, 2026-08-27).
  const [engineMode, setEngineMode] = useState<'claude' | 'ilp'>('claude')
  // ECS (empty-stock) positioning moves run on DIESEL by default — a light-engine
  // move does not need a steam turn (Chris, 2026-08-27).
  const [ecsDiesel, setEcsDiesel] = useState(true)
  // FIRECON DAYS (Chris, 2026-09-11): steam stood down on N consecutive running
  // days of May–August, diesel only, sales % down by day of the run. Applied
  // per DATE on the modelled side (firecon.ts::fireconDays) — never a rebuild.
  const [firecon, setFirecon] = useState<FireconConfig>(DEFAULT_FIRECON)
  // The planner's engine swaps on the Modelling stage (engineRoster.ts::EnginePin):
  // this engine takes that turn from that date on, for as long as it is available.
  const [enginePins, setEnginePins] = useState<EnginePin[]>([])
  const [pickEngine, setPickEngine] = useState<{ depId: string; turnKey: string; label: string } | null>(null)
  // Pullman dining runs THROUGH the designed timetable (Chris, 2026-09-02).
  // Daytime = lunch services (dep before 17:00); Night = evening dining.
  // Both on = model dining across the day and night wherever the basis season
  // ran it; night only = evening Pullmans in June/July/August only.
  const [pullmanDay, setPullmanDay] = useState(true)
  const [pullmanNight, setPullmanNight] = useState(true)
  // ON by default — the railway's rule (Chris, 2026-08-20): a return booking
  // ALWAYS comes home, even if a late outbound means barely any time off the
  // train. The minimum stay shapes which train they'd LIKE, never whether they
  // return; "unreturned" means literally no seat on any train home.
  const [forceEarly, setForceEarly] = useState(true)
  const [spillNearest, setSpillNearest] = useState(false)
  const [firstTrain, setFirstTrain] = useState('09:30')
  const [lastTrain, setLastTrain] = useState('18:00')
  // True once the window was set by a person (edit or loaded scenario) — the
  // measured WTT default (W5) never overrides a human's choice.
  const windowTouched = useRef(false)
  /** The settings as last loaded or saved — "Save model settings" lights up when they differ. */
  const [savedSig, setSavedSig] = useState<string | null>(null)
  /** Bumped when the saved railway defaults have been applied. */
  const [defaultsTick, setDefaultsTick] = useState(0)
  // The designer's timetable: has the user (or Best fit / a loaded scenario)
  // changed it? While untouched, the arrival of the real WTT may re-seed it;
  // once touched it is the author's and is only reset by a new slice/mode.
  const depsTouched = useRef(false)
  const depsSeedKey = useRef<string | null>(null)
  const [returnMode, setReturnMode] = useState<'latest' | 'observed'>('latest')
  // How many passengers we accept being unable to get home. ZERO by default,
  // and the railway treats it as a hard rule — raising it is a deliberate act,
  // which is why it is a slider rather than a checkbox.
  const [maxUnreturned, setMaxUnreturned] = useState(0)
  const [showPrev, setShowPrev] = useState(true)
  const [censoring, setCensoring] = useState(true)

  // ---- fleet, sets, costs -------------------------------------------------
  const [setsRoster, setSetsRoster] = useState<CarriageSet[]>(DEFAULT_SETS.map((s) => ({ ...s })))
  // Set formation for the modelled day (D6): null = derive from the month
  // (school-summer months run the strengthened peak rakes) until Chris chooses.
  const [formationChoice, setFormationChoice] = useState<SetFormation | null>(null)
  const formationMonth = Number(monthKey.split('-')[1]) || 0
  const formation: SetFormation = formationChoice ?? (formationMonth === 7 || formationMonth === 8 ? 'peak' : 'offpeak')
  // The planner's plain fleet COUNTS. With engines ticked "Use in simulation" in
  // the rolling-stock register (app.StudioLoco, db/83), `fleet` derives its
  // counts from them and carries the engines, so every working turn is given a
  // named engine (Chris, 2026-09-11). Sessions and the railway default store
  // the counts only — the register is its own table, never copied.
  const [fleetCounts, setFleetCounts] = useState<FleetCaps>({ ...DEFAULT_FLEET })
  const [locos, setLocos] = useState<FleetLoco[]>([])
  const [locosErr, setLocosErr] = useState<string | null>(null)
  const [locosLoading, setLocosLoading] = useState(false)
  const fleet = useMemo<FleetCaps>(() => fleetCapsFrom(locos, fleetCounts) ?? stripLocos(fleetCounts), [locos, fleetCounts])
  const setFleet = useCallback((f: FleetCaps | ((prev: FleetCaps) => FleetCaps)) => {
    setFleetCounts((p) => stripLocos(typeof f === 'function' ? f(p) : f))
  }, [])
  /** The register in use — every date is engined by the season roster (engineRoster.ts). */
  const registerInUse = (fleet.locos?.length ?? 0) > 0
  const reloadLocos = useCallback(() => {
    setLocosLoading(true)
    listLocos()
      .then((rows) => { setLocos(rows); setLocosErr(null) })
      .catch((e: unknown) => setLocosErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLocosLoading(false))
  }, [])
  const [rates, setRates] = useState<CostModel>(() => cloneCostModel(DEFAULT_COST_MODEL))
  /** The saved railway default (app.StudioDefaults). It OUTRANKS every other
   *  cost-model source: a session's frozen model and a slice's captured state
   *  stay in the record, but the LIVE rates are always the railway's — a save
   *  that can be silently overwritten by opening a slice is not a save
   *  (Chris, 2026-08-20: "I still don't think this is saving"). */
  const railwayDefault = useRef<CostModel | null>(null)
  /** The saved MODEL-SETTINGS defaults — Chris's hard barriers on the
   *  algorithm (tolerance, recapture, dwell, returns, floors). Same rule as
   *  the cost model: a slice's frozen state must never overwrite them
   *  (Chris, 2026-08-21: "I keep setting this at 60 and something keeps
   *  resetting it — these need not to be changing randomly"). */
  const railwayEngine = useRef<EngineDefaults | null>(null)
  /** The saved railway fleet caps and carriage sets — like the model settings,
   *  they outrank a slice's frozen copy (Chris, 2026-10-28: saves "not changed"
   *  every time he came back). */
  const railwayFleet = useRef<Partial<FleetCaps> | null>(null)
  const railwaySets = useRef<CarriageSet[] | null>(null)

  /**
   * Read the saved month-source choices, migrating the pre-2026-08-25 shape.
   *
   * The old setting was a list of months "excluded from their own sales". That
   * was incoherent as soon as the season changed: the months were absolute
   * ('2026-03'), so selecting 2027/28 lit no pill yet still claimed three
   * exclusions. Each excluded month simply becomes a source pointing at the
   * season before it — same effect, stated the way Chris asked for.
   * ONE place does this, so the migration cannot disagree with itself.
   */
  const adoptSources = (e: EngineDefaults | null | undefined): MonthSources => {
    if (e?.monthSources) return { by: { ...(e.monthSources.by ?? {}) }, reason: e.monthSources.reason ?? '' }
    const legacy = e?.disrupted
    if (!legacy?.months?.length) return { by: {}, reason: '' }
    const by: Record<string, string> = {}
    for (const mk of legacy.months) {
      const yr = Number(mk.slice(0, 4))
      if (!Number.isFinite(yr)) continue
      by[mk] = `${yr - 1}/${yr}`
    }
    return { by, reason: legacy.reason ?? '' }
  }
  const [dieselLoco, setDieselLoco] = useState(0)
  // W4: profit is the ONLY objective — the railway does not optimise for
  // revenue. Constant, not a knob; saved scenarios that said 'revenue' are
  // scored at profit like everything else.
  const objective = 'profit' as const
  const [minRevPerDep, setMinRevPerDep] = useState(500)

  // ---- timetable state ----------------------------------------------------
  const [deps, setDeps] = useState<Departure[]>([])
  /** The banked design for a month, and a signature that changes when it is
   *  rebuilt — both read by the single deps-seeding effect below. */
  const slicesRef = useRef<SessionSlice[]>([])
  const bankedSliceFor = (mk: string): SessionSlice | null => {
    const mm = Number(mk.split('-')[1])
    if (!Number.isFinite(mm)) return null
    const list = slicesRef.current
    return list.find((x) => x.month_no === mm && x.mode === mode && builtSlice(x))
      ?? list.find((x) => x.month_no === mm && x.mode === mode)
      ?? null
  }
  const bankedSig = (mk: string): string => {
    const sl = bankedSliceFor(mk)
    return sl ? `${sl.id}@${sl.updated_at}` : 'none'
  }
  /** What a month's banked build shows on screen. The rules live in
   *  studio/season.ts (`readBankedView`) so they can be gated headlessly —
   *  PRs #224 and #225 both shipped because nothing tested this read-back. */
  const bankedViewFor = (mk: string): BankedView | null => {
    const sl = bankedSliceFor(mk)
    return sl ? readBankedView(sl.state_json, CLASS_LABELS) : null
  }
  /** Bumped by newSession() so the seeding effect re-runs even when the
   *  month/mode selection hasn't changed — a fresh session must start from
   *  the REAL working timetable, never the previous session's design. */
  const [seedNonce, setSeedNonce] = useState(0)
  const [dayOverrides, setDayOverrides] = useState<Record<string, Departure[]>>({})
  const [dayDraft, setDayDraft] = useState<Departure[] | null>(null)
  const [draftDirty, setDraftDirty] = useState(false)
  const [classView, setClassView] = useState<string | null>(null)
  const [fitLog, setFitLog] = useState<string[] | null>(null)

  // ---- UI -----------------------------------------------------------------
  const [strandedOpen, setStrandedOpen] = useState(false)
  const [staysOpen, setStaysOpen] = useState(false)
  const [svcOpen, setSvcOpen] = useState<string | null>(null)
  const [pendingDisable, setPendingDisable] = useState<{ id: string; chain: string[] } | null>(null)
  const [editDep, setEditDep] = useState<{ id: string; time: string; o: number; d: number } | null>(null)
  const [addTime, setAddTime] = useState('13:30')
  const [addO, setAddO] = useState(0)
  const [addD, setAddD] = useState(3)

  // ---- calibration, advisor (saved scenarios retired from the UI; these
  // two survive only as labels on the exported board paper) ----------------
  const [scenarioId] = useState<number | null>(null)
  const [scenarioName] = useState('')
  const [backtest, setBacktest] = useState<BacktestResult | null>(null)
  const [backtestBusy, setBacktestBusy] = useState(false)
  const [backtestAuto, setBacktestAuto] = useState(false)
  const [advisorLog, setAdvisorLog] = useState<AdvisorRunLog | null>(null)
  const [advisorRunning, setAdvisorRunning] = useState(false)
  const [narrative, setNarrative] = useState('')
  // "What's changed" — Claude-written executive bullets (one per line here),
  // editable exactly like the narrative (Chris, 2026-08-20).
  const [whatsChanged, setWhatsChanged] = useState('')
  // Lock the steam:diesel mix: steam is the railway's primary tourist draw, and
  // the advisor's first live run recommended dieselising both diagrams on cost
  // grounds (Chris, 2026-08-20). ON by default.
  const [tractionLocked, setTractionLocked] = useState(true)
  // Explore chat (§5h): the planner's plain-English steer + a handle to Stop a run.
  const [userSteer, setUserSteer] = useState('')
  const advisorAbort = useRef<AbortController | null>(null)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [xlsxBusy, setXlsxBusy] = useState(false)
  // Which board paper to export. B = the original report; A = the report Chris
  // specified (2026-08-27): season-by-month table on its own page, a Costs &
  // Modelling section with a worked August day, and a per-month appendix.
  const [reportVariant, setReportVariant] = useState<'A' | 'B'>('A')
  const [defaultsBusy, setDefaultsBusy] = useState(false)
  const [defaultsNote, setDefaultsNote] = useState<string | null>(null)

  // ---- StudioShell: stage + modelling session + running set --------------
  const [stage, setStage] = useState<StageKey>('brief')
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [sessionName, setSessionName] = useState('Untitled session')
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('draft')
  const [sessionQuestion, setSessionQuestion] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [slices, setSlices] = useState<SessionSlice[]>([])
  slicesRef.current = slices
  const [sessionBusy, setSessionBusy] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [sessionsList, setSessionsList] = useState<ModellingSessionMeta[]>([])
  const [openModal, setOpenModal] = useState(false)
  // Cost model must be explicitly LOCKED for the session before a slice can be
  // designed (MASTER_PLAN §6.1). Frozen with the session so reopening resumes.
  const [costLocked, setCostLocked] = useState(false)
  /** Session row awaiting its inline "Delete for good" confirmation. */
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  /** The Brief stage's "Reset to railway defaults" two-step confirm. */
  const [resetConfirm, setResetConfirm] = useState(false)
  /** Working turns panel: the built timetable, or what the railway runs today. */
  const [turnsView, setTurnsView] = useState<'modelled' | 'current'>('modelled')
  // The applied running set: its days give the roll-up its REAL running-day
  // count per slice (never assumed — §6.3). null = none applied.
  const [runningSetId, setRunningSetId] = useState<number | null>(null)
  const [runningSetName, setRunningSetName] = useState<string | null>(null)
  const [appliedDays, setAppliedDays] = useState<RunningDay[] | null>(null)
  const [runningSetMetas, setRunningSetMetas] = useState<{ id: number; name: string; count: number; isDefault: boolean }[]>([])
  const [runningBusy, setRunningBusy] = useState(false)
  // Results tabs (Chris, 2026-10-28): Financial Impact first; Recommendation and
  // What's changed folded into Edit PDF Summaries.
  const [resultTab, setResultTab] = useState<'money' | 'ops' | 'case' | 'appx' | 'edit'>('money')
  // The real thebigplan WTT (§5e). null until fetched; .available=false = fallback.
  const [baselineWtt, setBaselineWtt] = useState<BaselineWtt | null>(null)
  // True while the real WTT (and with it the NR path budget and operating
  // window) is still loading — Best fit must not run against half-loaded
  // ground truth: in the live-race case it built to a wrong path budget.
  const [baselineLoading, setBaselineLoading] = useState(true)
  // Modelling period (Brief setup). chosenSeason=null → the API's current season.
  const [chosenSeason, setChosenSeason] = useState<string | null>(null)
  const [periodMode, setPeriodMode] = useState<'season' | 'range'>('season')
  const [rangeFrom, setRangeFrom] = useState('')
  const [rangeTo, setRangeTo] = useState('')
  // Stage sub-tabs (Chris, 2026-10-28). Timetable = the running calendar plus
  // the baseline (was stages 3 and 4); Cost model opens on the rolling stock in
  // use; the Modelling stage opens on the SEASON, and a month opens on its
  // Financial tab.
  const [ttTab, setTtTab] = useState<'calendar' | 'baseline'>('calendar')
  const [costTab, setCostTab] = useState<'rolling' | 'fleet' | 'pre' | 'results'>('rolling')
  const [modelView, setModelView] = useState<'season' | 'month'>('season')
  const [monthTab, setMonthTab] = useState<'fin' | 'turns' | 'wss' | 'tt'>('fin')
  /** SEASON's tabs (Chris, 2026-10-28): the figures, or the proposed season timetable. */
  const [seasonTab, setSeasonTab] = useState<'fin' | 'tt'>('fin')
  /** New session: named (and its question set) as it is created. */
  const [newDraft, setNewDraft] = useState<{ name: string; question: string } | null>(null)
  /** Open… list: the row whose name/question is being edited (the pen). */
  const [editRow, setEditRow] = useState<{ id: number; name: string; question: string } | null>(null)

  // Explore is hidden from the spine (Chris, 2026-09-02) — a session saved on
  // that stage, or any stray navigation to it, lands on Results instead.
  // Baseline is a sub-tab of Timetable now (2026-10-28) — a session saved on
  // it lands there.
  useEffect(() => {
    if (stage === 'explore') setStage('results')
    if (stage === 'baseline') { setStage('calendar'); setTtTab('baseline') }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage])

  // =========================================================================
  //  Load
  // =========================================================================
  useEffect(() => {
    if (!enabled) return
    let live = true
    // chosenSeason (from the Brief's modelling period) re-loads demand for that
    // season; null = the current season the API resolves by default.
    const q = chosenSeason ? `?season=${encodeURIComponent(chosenSeason)}` : ''
    fetch(`/api/research/timetable-studio${q}`)
      .then(async (r) => {
        if (!r.ok) {
          const b = await r.json().catch(() => ({}))
          throw new Error((b as { error?: string }).error || `${r.status}`)
        }
        return r.json() as Promise<StudioPayload>
      })
      .then((d) => { if (live) { setData(d); setError(null) } })
      .catch((e) => { if (live) setError(String((e as Error)?.message ?? e)) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, chosenSeason])

  // The accounts-anchored cost-line config (include/behaviour/driver/flex overrides
  // + basis season). Lives in the shared Studio defaults alongside the rate cards.
  const [costConfig, setCostConfig] = useState<CostConfig>({})
  // ★ The P&L does NOT steer the optimiser (Chris, 2026-09-02: "the build logic
  // should not have changed just because we changed the source of the costs").
  // The greedy decides on the engine's true incremental cost; the P&L model
  // prices the FINISHED design (pnlCost below). PR #252's pnlRates steering —
  // which charged the accounts AVERAGE per turn and collapsed the season to
  // 1–2 turns a day — is removed.
  const [studioCostsData, setStudioCostsData] = useState<StudioCostsResponse | null>(null)
  const [studioCostsLoading, setStudioCostsLoading] = useState(false)
  const [studioCostsErr, setStudioCostsErr] = useState<string | null>(null)

  // ── Pullman dining actuals (agg.Pullman_Services via the existing dashboard
  // endpoint) — every service the aggregate holds, per date, with its season.
  // The designed season's Pullman programme for a month is the MOST RECENT
  // season with Pullman data for that month (the aggregate only reaches back to
  // 2026-03), mapped like-for-like day by day (pairLikeDates — the same rule
  // the unoptimised sales use) and filtered by the Daytime/Night-time toggles.
  // Fetch failures are labelled, never silent (law 8).
  type PullmanSvcRow = { season: string; service_date: string; departure_time: string; service_description: string; service_type: string; people_total: number; revenue_net: number | null }
  const [pullmanRows, setPullmanRows] = useState<PullmanSvcRow[]>([])
  const [pullmanErr, setPullmanErr] = useState<string | null>(null)
  useEffect(() => {
    if (!enabled) return
    let live = true
    fetch('/api/dashboards/pullman/services')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: { services?: PullmanSvcRow[] }) => { if (live) setPullmanRows(j.services ?? []) })
      .catch((e) => { if (live) setPullmanErr(`Pullman services could not be loaded: ${String((e as Error)?.message ?? e)}`) })
    return () => { live = false }
  }, [enabled])
  /** Is a Pullman departure modelled to run, under the Daytime/Night toggles?
   *  Night-only = evening dining in June/July/August only (Chris, 2026-09-02). */
  const pullmanModelled = (mm: number, departureTime: string): boolean => {
    const hour = Number(String(departureTime ?? '').slice(0, 2))
    const night = Number.isFinite(hour) && hour >= 17
    if (night) {
      if (!pullmanNight) return false
      return pullmanDay ? true : mm === 6 || mm === 7 || mm === 8
    }
    return pullmanDay
  }
  /** The month's modelled Pullman programme: the most recent season with Pullman
   *  data for this calendar month, its service days paired like-for-like onto
   *  the design dates, toggle-filtered. One owner for screen, PDF and workbook. */
  const pullmanForMonth = (mm: number, designDates: string[]): { byDate: Record<string, { services: number; covers: number; net: number }>; source: string | null } => {
    const monthRows = pullmanRows.filter((r) => Number(r.service_date.slice(5, 7)) === mm && pullmanModelled(mm, r.departure_time))
    if (monthRows.length === 0) return { byDate: {}, source: null }
    const seasons = [...new Set(monthRows.map((r) => r.season))].sort()
    const source = seasons[seasons.length - 1]
    const byService = new Map<string, { services: number; covers: number; net: number }>()
    for (const r of monthRows) {
      if (r.season !== source) continue
      const c = byService.get(r.service_date) ?? { services: 0, covers: 0, net: 0 }
      c.services += 1
      c.covers += r.people_total ?? 0
      c.net += r.revenue_net ?? 0
      byService.set(r.service_date, c)
    }
    const pairing = pairLikeDates(designDates, [...byService.keys()])
    const byDate: Record<string, { services: number; covers: number; net: number }> = {}
    for (const [d, s] of Object.entries(pairing.byDate)) byDate[d] = byService.get(s)!
    return { byDate, source }
  }
  // Basis defaults to the last COMPLETED operating season (Mar–Feb).
  const defaultBasis = (() => {
    const now = new Date()
    const y = now.getUTCFullYear()
    const startYear = now.getUTCMonth() >= 2 ? y : y - 1
    return `${startYear - 1}/${startYear}`
  })()
  const costBasis = costConfig.basis ?? defaultBasis
  const basisOptions = [0, 1, 2].map((k) => {
    const y = Number(defaultBasis.slice(0, 4)) - k
    const v = `${y}/${y + 1}`
    return { value: v, label: k === 0 ? `${v} (last completed)` : v }
  })
  const setCostBasis = (b: string) => setCostConfig((c) => ({ ...c, basis: b }))
  // The basis-season Management P&L — one fetch for the cost tables AND the PDF.
  useEffect(() => {
    if (!enabled || typeof fetch !== 'function') return
    let live = true
    setStudioCostsLoading(true); setStudioCostsErr(null)
    fetch(`/api/research/studio-costs?basis=${encodeURIComponent(costBasis)}`)
      .then(async (r) => { if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? `HTTP ${r.status}`); return r.json() })
      .then((j: StudioCostsResponse) => { if (live) setStudioCostsData(j) })
      .catch((e) => { if (live) setStudioCostsErr(String((e as Error)?.message ?? e)) })
      .finally(() => { if (live) setStudioCostsLoading(false) })
    return () => { live = false }
  }, [enabled, costBasis])

  // Shared cost rates / fleet / roster (app.StudioDefaults, db/65). These
  // describe the railway rather than the person with the tab open, so they come
  // from the database — a 404 just means nobody has saved one yet.
  useEffect(() => {
    if (!enabled) return
    let live = true
    fetch('/api/research/studio-defaults')
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (!live || !b) return
        const d = (b as { defaults?: { rates?: CostModel; fleet?: Partial<FleetCaps>; sets?: CarriageSet[]; engine?: EngineDefaults; costConfig?: CostConfig }; updated_by?: string }).defaults
        if (!d) return
        if (d.costConfig && typeof d.costConfig === 'object') setCostConfig(d.costConfig)
        // Only adopt a saved model of the current (structured) shape; ignore legacy flat rates.
        if (d.rates && Array.isArray(d.rates.locos)) { railwayDefault.current = d.rates; setRates(d.rates) }
        if (d.fleet) { railwayFleet.current = d.fleet; setFleet((prev) => ({ ...prev, ...d.fleet })) }
        // Model settings — the saved hard barriers (Chris, 2026-08-21).
        if (d.engine && Number.isFinite(d.engine.tolerance)) {
          railwayEngine.current = d.engine
          setTolerance(d.engine.tolerance)
          setRecapturePct(d.engine.recapturePct)
          setDwellMin(d.engine.dwellMin)
          setFarePct(d.engine.farePct)
          setMaxStay(d.engine.maxStay)
          setForceEarly(d.engine.forceEarly)
          setSpillNearest(d.engine.spillNearest)
          setReturnMode(d.engine.returnMode)
          setMaxUnreturned(d.engine.maxUnreturned)
          setMinRevPerDep(d.engine.minRevPerDep)
          setMonthSources(adoptSources(d.engine))
          if (d.engine.firecon) setFirecon(normaliseFirecon(d.engine.firecon))
          if (d.engine.window) {
            windowTouched.current = true // the saved window outranks the WTT's
            setFirstTrain(d.engine.window.firstTrain)
            setLastTrain(d.engine.window.lastTrain)
          }
          if (d.engine.ecsDiesel != null) setEcsDiesel(d.engine.ecsDiesel)
          if (d.engine.censoring != null) setCensoring(d.engine.censoring)
          if (d.engine.pullman) { setPullmanDay(d.engine.pullman.day); setPullmanNight(d.engine.pullman.night) }
          if (d.engine.tti) setTti(normaliseTti(d.engine.tti))
        }
        setDefaultsTick((n) => n + 1)
        // A roster saved before D6 has no measured seats — the shipped MEASURED
        // capacities beat it; adopting it would resurrect the 64/coach guess.
        if (d.sets && d.sets.length > 0 && d.sets.some((x) => x.seats != null)) { railwaySets.current = d.sets.map((x) => ({ ...x })); setSetsRoster(d.sets.map((x) => ({ ...x }))) }
        setDefaultsNote(`Using the saved railway defaults${(b as { updated_by?: string }).updated_by ? ` (set by ${(b as { updated_by?: string }).updated_by!.split('@')[0]})` : ''}.`)
      })
      .catch(() => { /* shipped constants stand */ })
    return () => { live = false }
  }, [enabled])

  // The rolling-stock register — the railway's named engines (db/83).
  useEffect(() => { if (enabled) reloadLocos() }, [enabled, reloadLocos])

  // Named running-timetable sets for this season (db/76). The applied set gives
  // the season roll-up its REAL running-day count per slice (§5d / §6.3).
  useEffect(() => {
    if (!enabled || !data) return
    let live = true
    const season = data.season
    // A calendar whose dates all fall outside this season is another season's
    // (the season was switched with a set applied): drop it, then apply this
    // season's own default below. A set applied for THIS season — e.g. by
    // opening a session — has dates in it and is kept.
    const foreign = appliedDays != null && appliedDays.length > 0 &&
      datesInSeason(appliedDays, data.season_from, data.season_to).length === 0
    if (foreign) { setRunningSetId(null); setRunningSetName(null); setAppliedDays(null) }
    listRunningSets(season)
      .then(async (sets) => {
        if (!live) return
        setRunningSetMetas(sets.map((s) => ({ id: s.id, name: s.name, count: 0, isDefault: !!s.is_default })))
        if (runningSetId != null && !foreign) return
        // On open, apply the season's DEFAULT set if one is marked (Chris,
        // 2026-09-01: "when applied that is what it always loads by default").
        // Otherwise fall back to the calendar last applied this season, stashed in
        // localStorage (the plain-reload restore, Chris 2026-08-27).
        const defaultSet = sets.find((s) => s.is_default)
        let toApply: number | null = defaultSet ? defaultSet.id : null
        if (toApply == null) {
          try {
            const raw = localStorage.getItem(`nymr-studio-applied-set-${season}`)
            const id = raw ? Number(raw) : NaN
            if (Number.isFinite(id) && sets.some((s) => s.id === id)) toApply = id
          } catch { /* localStorage blocked — the default/standard calendar stands */ }
        }
        if (toApply != null) {
          try {
            const set = await loadRunningSet(toApply)
            if (live) { setRunningSetId(set.id); setRunningSetName(set.name); setAppliedDays(set.days) }
          } catch { /* the standard calendar stands */ }
        }
      })
      .catch(() => { /* none saved yet */ })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rawData])

  // The real thebigplan working timetable (§5e). Degrades to the sales-derived
  // baseline (flagged on screen) when thebigplan is unreachable — never blocks.
  useEffect(() => {
    if (!enabled || !data || !monthKey) return
    let live = true
    // Mid-month of the modelled month + day-class: the API serves the
    // timetable whose effective window covers it AND that month's majority-
    // colour pattern, in one round trip. The cleanup flag also kills the
    // stale-response race — a slow earlier fetch must never overwrite a newer
    // month's baseline.
    setBaselineLoading(true)
    loadBaseline(undefined, probeDateFor(monthKey), dayClass)
      // NEVER downgrade: a transient failure (cold pool, network blip) must not
      // replace a good loaded baseline with "unavailable" — that silently
      // degraded the whole session (W0/W3/W5 and the season design run).
      .then((b) => { if (live) setBaselineWtt((prev) => (b.available || !prev?.available ? b : prev)) })
      .catch(() => { /* fallback stands */ })
      .finally(() => { if (live) setBaselineLoading(false) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rawData, monthKey, dayClass])

  // Accepts an explicit model so a calibration save can persist the NEW rates
  // immediately (setRates hasn't re-rendered yet when this is called).
  // Every save also stores the CURRENT model settings — Chris's hard barriers
  // travel with the railway default (2026-08-21).
  const engineNow = (): EngineDefaults => ({
    tolerance, recapturePct, dwellMin, farePct, maxStay, forceEarly, spillNearest, returnMode, maxUnreturned, minRevPerDep,
    monthSources: { by: { ...monthSources.by }, reason: monthSources.reason },
    firecon,
    window: { firstTrain, lastTrain },
    ecsDiesel, censoring,
    pullman: { day: pullmanDay, night: pullmanNight },
    tti,
  })
  /** Everything "Save model settings" stores, as one comparable string. */
  const settingsSig = (model?: CostModel) => JSON.stringify({ engine: engineNow(), rates: model ?? rates, fleet: stripLocos(fleet), sets: setsRoster, costConfig })
  const saveDefaults = async (model?: CostModel) => {
    setDefaultsBusy(true)
    try {
      const engine = engineNow()
      const sig = settingsSig(model)
      const r = await fetch('/api/research/studio-defaults', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ defaults: { rates: model ?? rates, fleet: stripLocos(fleet), sets: setsRoster, engine, costConfig } }),
      })
      if (!r.ok) {
        const b = await r.json().catch(() => ({}))
        throw new Error((b as { error?: string }).error || `${r.status}`)
      }
      railwayDefault.current = model ?? rates
      railwayEngine.current = engine
      railwayFleet.current = stripLocos(fleet)
      railwaySets.current = setsRoster.map((x) => ({ ...x }))
      setSavedSig(sig)
      setDefaultsNote('Saved as the railway default — every Studio session will load these rates and model settings from now on.')
    } catch (e) {
      setDefaultsNote(`Couldn't save the default: ${String((e as Error)?.message ?? e)}`)
    } finally {
      setDefaultsBusy(false)
    }
  }

  // The loaded defaults are the baseline the Save button compares against —
  // taken a render after they were applied, so the state has settled.
  useEffect(() => {
    if (defaultsTick > 0) setSavedSig(settingsSig())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsTick])

  // Calibration runs BY ITSELF, once, as soon as the data is in. It is the
  // difference between a number and a defensible number, it costs a few hundred
  // milliseconds of engine time, and it was previously hidden behind a tab
  // nobody would think to press. The error band it produces feeds the headline
  // card and the board paper.
  const runCalibration = useCallback((auto: boolean) => {
    if (!data) return
    setBacktestBusy(true)
    setTimeout(() => {
      try {
        setBacktest(runBacktest(data, params, data.prev_season ? 'previous' : 'current', net0))
        if (auto) setBacktestAuto(true)
      } finally {
        setBacktestBusy(false)
      }
    }, 30)
    // params is deliberately excluded: re-calibrating on every slider nudge
    // would be noise, and the calibration is about the model's structure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (data && !backtest && !backtestBusy && !backtestAuto) runCalibration(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // THE MONTHS TO MODEL COME FROM THE SEASON, not from its ticket sales. A
  // season being DESIGNED has sold nothing, so the old sales-derived list left
  // the Studio with no months at all and nothing to design (Chris, 2026-08-25).
  // A month that runs no trains is the calendar's business and already says so.
  const monthOptions = useMemo(() => {
    const sales = [...new Set((data?.demand ?? []).map((r) => `${r.yr}-${String(r.month_no).padStart(2, '0')}`))]
    return seasonMonths({ from: data?.season_from, to: data?.season_to, salesMonths: sales })
      .map((k) => {
        const [y, m] = k.split('-').map(Number)
        return { value: k, label: `${MONTHS[m - 1]} ${y}` }
      })
  }, [rawData])

  useEffect(() => {
    // W1: default to the CURRENT month when it has data — never "the latest
    // month with data", which kept opening the Studio on Nov 2026.
    // A month outside the selected season is the PREVIOUS season's (the season
    // was switched): move to the same month of this season. Leaving it showed
    // March 2026's Yellow shuttle as "today" on a 2027/28 screen (2026-10-27).
    if (!monthOptions.length) return
    if (monthKey && monthOptions.some((o) => o.value === monthKey)) return
    const sameMonth = monthKey ? monthOptions.find((o) => o.value.slice(5) === monthKey.slice(5)) : undefined
    const now = new Date()
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    setMonthKey(sameMonth?.value ?? (monthOptions.some((o) => o.value === cur) ? cur : monthOptions[monthOptions.length - 1].value))
  }, [monthOptions, monthKey])

  const hasFine = useMemo(
    () => {
      // The grain follows the DATA the season is modelled on. A designed season
      // has no demand of its own, so look at the source seasons too — otherwise
      // a speculation off a fine-grained past season would silently model at the
      // coarse Sat/Sun/Wkdy grain (§5 item 4).
      const fine = (arr?: DemandRow[]) => (arr ?? []).some((r) => r.day_class === 'WkdyH' || r.day_class === 'WkdyT')
      return fine(data?.demand) || fine(data?.prev_demand) || fine(data?.prev2_demand)
    },
    [data]
  )
  // W8: the modelling grain is DATA, not a choice — model at the fine grain
  // (term vs school-holiday weekdays) automatically wherever the data splits;
  // "Full week" stays available as the aggregate VIEW (the 'Week' day type).
  const grain: 'basic' | 'fine' = hasFine ? 'fine' : 'basic'

  const isWeek = dayClass === 'Week'
  const classesInGrain = grain === 'fine' ? FINE_CLASSES : BASIC_CLASSES

  // =========================================================================
  //  Demand selection (with the censoring correction)
  // =========================================================================
  /**
   * ★ ONE OWNER of "which season's sales describe this month" (law 6).
   *
   * The season being designed NEVER has sales of its own — it does not exist;
   * it is a speculation built from a past season (Chris, 2026-08-26: "I'm
   * ALWAYS building a non-existent season ... based off an existing past
   * season"). So there is one rule, `chooseSalesBasis`, and BOTH the live
   * screen (`selection`) and the season build (`dayClassDemand`) read demand
   * through THIS helper — never straight off `data.demand`, which is empty for
   * a designed season and left every downstream stage blank.
   *
   * Declared above `selection` deliberately: a helper a memo calls must be
   * declared first, or it throws in the memo's TDZ and the page goes blank
   * (CLAUDE.md §4, the `tdz` gate).
   */
  const sourceTierFor = (yy: number, mm: number): {
    rows: DemandRow[]; untimed: UntimedRow[]; slots: SlotDailyRow[]; untimedDaily: UntimedDailyRow[]
    srcYr: number; own: boolean; basis: string; basisSeason: string
  } | null => {
    if (!data) return null
    const mKey = `${yy}-${String(mm).padStart(2, '0')}`
    const tiers = [
      { season: data.season, rows: data.demand.filter((r) => r.yr === yy && r.month_no === mm), untimed: data.untimed, slots: data.slot_daily ?? [], untimedDaily: data.untimed_daily ?? [], yr: yy },
      { season: data.prev_season ?? 'last season', rows: data.prev_demand.filter((r) => r.month_no === mm), untimed: data.prev_untimed, slots: data.prev_slot_daily ?? [], untimedDaily: data.prev_untimed_daily ?? [], yr: yy - 1 },
      { season: data.prev2_season ?? 'the season before', rows: (data.prev2_demand ?? []).filter((r) => r.month_no === mm), untimed: data.prev2_untimed ?? [], slots: data.prev2_slot_daily ?? [], untimedDaily: data.prev2_untimed_daily ?? [], yr: yy - 2 },
    ]
    const candidates: SeasonCandidate[] = tiers.map((t) => ({ season: t.season, yr: t.rows[0]?.yr ?? t.yr, pax: t.rows.reduce((a, r) => a + r.pax, 0) }))
    const picked = chooseSalesBasis({
      monthKey: mKey, nowKey: new Date().toISOString().slice(0, 7),
      candidates, monthSources: monthSources.by, disruptedMonths: [], monthNames: MONTHS,
    })
    if (!picked.chosen) return null
    const idx = candidates.findIndex((c) => c.season === picked.chosen!.season && c.yr === picked.chosen!.yr)
    const tier = tiers[idx] ?? tiers[0]
    const basis = !picked.own && monthSources.reason ? `${picked.basis} (${monthSources.reason})` : picked.basis
    return { rows: tier.rows, untimed: tier.untimed, slots: tier.slots, untimedDaily: tier.untimedDaily, srcYr: picked.chosen.yr, own: picked.own, basis, basisSeason: tier.season }
  }
  /** The month's sales for the Weekly Service Schedule and the board paper's
   *  month view — the SAME source season the build reads (sourceTierFor). A
   *  season still to trade (2027/28) has no sales of its own: its months take
   *  the planner's chosen season's, so its schedule is never all "no service"
   *  (Chris, 2026-10-28: "It's no longer building the timetable for a month"). */
  const scheduleSalesFor = (mk: string): { data: StudioPayload; year: number } | null => {
    if (!data || !mk) return null
    const [yy, mm] = mk.split('-').map(Number)
    const tier = sourceTierFor(yy, mm)
    if (!tier || tier.own) return { data, year: yy }
    return { data: { ...data, demand: tier.rows, untimed: tier.untimed }, year: tier.srcYr }
  }
  // Memoised for the on-screen schedule: a fresh payload object every render
  // would rebuild its seven day-models on every keystroke.
  const scheduleSales = useMemo(() => scheduleSalesFor(monthKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, monthKey, monthSources])

  /**
   * A month's REAL per-day sales, mapped like-for-like from the sales-basis
   * season (studio/dateActuals.ts is the one owner of the mapping; the basis
   * choice stays with sourceTierFor — law 6). Feeds the unoptimised side of the
   * Weekly Service Schedule (screen, PDF and workbook), so every date shows the
   * day's ACTUAL passengers and takings, never a class average (Chris,
   * 2026-09-02). `unmatched` dates fall back to the average and are labelled.
   */
  const dateActualsFor = (mk: string, designDates: string[]): (DateActualsResult & { basisSeason: string; basis: string }) | null => {
    const [yy, mm] = mk.split('-').map(Number)
    const tier = sourceTierFor(yy, mm)
    if (!tier) return null
    return { ...mapDateActuals({ designDates, monthNo: mm, slots: tier.slots, untimedDaily: tier.untimedDaily }), basisSeason: tier.basisSeason, basis: tier.basis }
  }

  const selection = useMemo(() => {
    if (!data || !monthKey) return null
    const [y, m] = monthKey.split('-').map(Number)
    const wanted = isWeek ? [...classesInGrain] : [dayClass]
    // Demand comes from the month's chosen source season, not `data.demand`
    // (which is empty for the season being designed). ONE owner: sourceTierFor.
    const tier = sourceTierFor(y, m)
    if (!tier) return null
    const srcYr = tier.srcYr
    const weights = classWeights(tier.untimed, srcYr, m, classesInGrain)

    const classes: ClassDemand[] = []
    const prevClasses: ClassDemand[] = []
    const perCls: Record<string, PerCls> = {}
    const reports: Record<string, CensoringReport> = {}
    let untimedNet = 0, untimedPax = 0
    let prevRev = 0, prevPax = 0, prevUntimedNet = 0
    let havePrev = false, havePrevUntimed = false

    for (const cls of wanted) {
      const weight = isWeek ? (weights[cls] ?? 0) : 1
      if (isWeek && weight <= 0) continue
      // Fine classes (WkdyH/WkdyT) fall back to plain Wkdy where the chosen
      // source season did not split them — the same rule the build applies in
      // dayClassDemand, so the on-screen slice and the season build agree.
      const tryClasses = cls.startsWith('Wkdy') && cls !== 'Wkdy' ? [cls, 'Wkdy'] : [cls]
      let rows: DemandRow[] = []
      let srcCls = cls
      for (const cc of tryClasses) { const r = tier.rows.filter((x) => x.day_class === cc); if (r.length > 0) { rows = r; srcCls = cc; break } }
      const info: PerCls = { untimedNet: 0, untimedPax: 0, prevRev: null, prevPax: null, prevUntimedNet: null, hasPrev: false }
      if (rows.length > 0) {
        const un = tier.untimed.find((u) => u.yr === srcYr && u.month_no === m && u.day_class === srcCls)
        // The slice's running-day count — pours part-month services correctly (D5).
        const clsDays = timedRunningDays(rows)
        const built = buildClassDemand(net0, rows, tier.slots, cls, weight, srcYr, m, censoring, clsDays)
        classes.push(built.demand)
        Object.assign(reports, built.reports)
        if (un && un.running_days > 0) {
          info.untimedNet = un.net / un.running_days
          info.untimedPax = un.pax / un.running_days
          untimedNet += info.untimedNet * weight
          untimedPax += info.untimedPax * weight
        }
      }
      const prevRows = data.prev_demand.filter((r) => r.month_no === m && r.day_class === cls)
      if (prevRows.length > 0) {
        havePrev = true
        info.hasPrev = true
        const prevUn = data.prev_untimed.find((u) => u.month_no === m && u.day_class === cls)
        const prevDays = timedRunningDays(prevRows)
        const builtPrev = buildClassDemand(net0, prevRows, data.prev_slot_daily, cls, weight, prevRows[0].yr, m, censoring, prevDays)
        prevClasses.push(builtPrev.demand)
        if (prevDays > 0) {
          info.prevRev = prevRows.reduce((t, r) => t + r.net, 0) / prevDays
          info.prevPax = prevRows.reduce((t, r) => t + r.pax, 0) / prevDays
          prevRev += info.prevRev * weight
          prevPax += info.prevPax * weight
        }
        if (prevUn && prevUn.running_days > 0) {
          havePrevUntimed = true
          info.prevUntimedNet = prevUn.net / prevUn.running_days
          prevUntimedNet += info.prevUntimedNet * weight
        }
      }
      perCls[cls] = info
    }
    if (classes.length === 0) return null
    return {
      classes, prevClasses, perCls, reports,
      runningDays: totalWeight(classes),
      untimedNetPerPeriod: untimedNet,
      untimedPaxPerPeriod: untimedPax,
      prevRevPerPeriod: havePrev ? prevRev : null,
      prevPaxPerPeriod: havePrev ? prevPax : null,
      prevUntimedNetPerPeriod: havePrevUntimed ? prevUntimedNet : null,
      hasPrev: havePrev,
    }
    // net0 is a stable geometric reference — see below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, monthKey, dayClass, isWeek, grain, censoring, monthSources])

  // =========================================================================
  //  Network
  //
  //  The path budget defaults to what we run TODAY: Chris's instruction was
  //  "you have what we do now — we don't want to be running any more than we
  //  need to", so Mode B has to live inside the observed usage rather than a
  //  number someone invented. That also makes the A/B comparison fair.
  // =========================================================================
  // W0 — "current" IS the real working timetable: thebigplan's workings for the
  // slice's actual pattern, decoded with measured times, diagram identity and
  // set capacities, scoped to daily services (Pullman dining excluded — its
  // revenue is a separate P&L). Sales-derived stays only as a flagged fallback.
  const wttObserved = useMemo(
    () => {
      if (!baselineWtt?.available || (baselineWtt.workings?.length ?? 0) === 0) return null
      // The API now serves EVERY pattern's workings (the season view needs them
      // all); the slice's "current" is the SELECTED pattern's.
      const mine = baselineWtt.workings!.filter((w) => !baselineWtt.selectedPattern || w.pattern_code === baselineWtt.selectedPattern)
      return mine.length > 0 ? wttToDeps(mine, formation) : null
    },
    [baselineWtt, formation]
  )

  // The slice's pattern comes from the date→colour calendar, never a guess:
  // re-fetch the baseline when the modelled month/day-class runs a different
  // colour than the pattern currently loaded.
  useEffect(() => {
    if (!baselineWtt?.available || !baselineWtt.days?.length || !monthKey) return
    const want = patternForSlice(baselineWtt.days, monthKey, isWeek ? 'Week' : dayClass)
    if (!want || want === baselineWtt.selectedPattern) return
    if (!baselineWtt.patterns?.some((p) => p.code === want)) return
    void refreshBaseline(want)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineWtt, monthKey, dayClass, isWeek])

  const observedDeps = useMemo(() => {
    if (wttObserved) return wttObserved.deps
    if (!selection) return []
    const north = selection.classes.flatMap((c) => c.north)
    const south = selection.classes.flatMap((c) => c.south)
    return baselineDeps(net0, north, south)
  }, [selection, wttObserved])

  // W3 — DEFAULT-APPLY the standard timetable as the running set: the railway's
  // real calendar (thebigplan's run flags per date) is the default, so the
  // roll-up always has real running-days. Chris only SAVES a new set if he
  // changes the pattern — the default creates no DB row.
  useEffect(() => {
    if (runningSetId != null || appliedDays != null) return
    // Only this season's dates: just after a season switch the baseline can
    // still be the previous season's timetable — wait for this season's.
    const days = datesInSeason(baselineWtt?.days, data?.season_from, data?.season_to)
    if (!days.length) return
    // THE CALENDAR DEFINES THE RUNNING DAYS (Chris, 2026-08-26). Build the
    // default applied set straight from thebigplan's real calendar — its run
    // flag and colour per date — never from sales day-types, which a season
    // being designed does not have. Classify the day-type from sales where they
    // exist, else by day-of-week. Only calendar dates are included, so no
    // phantom running days (law 8).
    const dtByDate = new Map((data?.day_types ?? []).map((dt) => [dt.actual_date.slice(0, 10), dt.day_class]))
    const dowClass = (date: string) => { const w = (new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7; return w === 5 ? 'Sat' : w === 6 ? 'Sun' : 'Wkdy' }
    setAppliedDays(days.map((d) => ({
      date: d.date,
      day_class: dtByDate.get(d.date) ?? dowClass(d.date),
      running: d.run,
      ...(d.colour ? { colour: d.colour } : {}),
    })))
    setRunningSetName('Standard timetable (thebigplan)')
  }, [baselineWtt, data, runningSetId, appliedDays])

  // W5 — the operating window defaults to the REAL WTT's first departure and
  // last arrival (measured), editable after; a loaded scenario's window is the
  // author's choice and is never overridden.
  const wttWindow = useMemo(() => {
    if (!wttObserved || wttObserved.deps.length === 0) return null
    const first = Math.min(...wttObserved.deps.map((d) => d.minutes))
    const last = Math.max(...wttObserved.deps.map((d) => d.arrMin ?? d.minutes))
    return { first: fmtMin(first), last: fmtMin(last) }
  }, [wttObserved])
  useEffect(() => {
    if (!wttWindow || windowTouched.current) return
    setFirstTrain(wttWindow.first)
    setLastTrain(wttWindow.last)
  }, [wttWindow])

  const observedPaths = useMemo(
    () => observedNrTraversals(net0, observedDeps),
    [observedDeps]
  )

  const net: Network = useMemo(() => {
    const base = buildNetwork(mode, {
      mctMin, transferPlatforms: platforms, interchangePenaltyMin: penaltyMin,
      waitWeight, aversionShare: aversionPct / 100, steamOverNR,
      shuttleTurnaroundMin: shuttleTurn,
      nrPathsPerDay: nrPaths > 0 ? nrPaths : undefined,
    })
    return withPathBudget(base, nrPaths > 0 ? nrPaths : observedPaths)
  }, [mode, mctMin, platforms, penaltyMin, waitWeight, aversionPct, steamOverNR, shuttleTurn, nrPaths, observedPaths])

  const params: EngineParams = useMemo(() => ({
    toleranceMin: tolerance,
    recapture: recapturePct / 100,
    fareMult: farePct / 100,
    dwellMin,
    returnMode,
    opts: { forceEarly, maxStay, spillToNearest: spillNearest, lastMin: toMin(lastTrain) || 24 * 60 },
  }), [tolerance, recapturePct, farePct, dwellMin, returnMode, forceEarly, maxStay, spillNearest, lastTrain])

  // Switching mode resets the roster so the shuttle unit appears / disappears.
  useEffect(() => { setSetsRoster(modeSets(mode)) }, [mode])

  /**
   * THE ONE OWNER of "which date do we ask thebigplan about for this month".
   *
   * Never the 15th. thebigplan serves the timetable covering the date you ask
   * about, and the 15th is not necessarily a date the railway runs: March
   * 2026's nine running days are ALL 21 March or later (Spring/Summer 2026,
   * which publishes Yellow), but the 15th returned Autumn/Winter 2025, which
   * has no Yellow pattern at all. Seven of March's nine days therefore had no
   * baseline and the month was never modelled (Chris, 2026-08-25: "March
   * hasn't even changed — absolutely no modelling has been done on it").
   */
  const probeDateFor = (mk: string): string => baselineProbeDate({
    monthKey: mk,
    calendarDays: seasonWtt[mk]?.days ?? baselineWtt?.days ?? [],
    appliedDays: appliedDays ?? null,
  })

  // Default timetable on load — the REAL working timetable when we have it
  // (W0: the designer starts from what the railway actually runs, with measured
  // times, diagram identity and set pins — the sales-derived slots merged
  // same-minute workings like the 12:35s and lost 2P07, which is how the
  // Internal set got stranded at Grosmont). Sales-derived stays as the
  // fallback; in Full week that's the observed WEEKDAY pattern rather than the
  // union of every class's departures, which over-filled the roster.
  useEffect(() => {
    if (!selection) { setDeps([]); setFitLog(null); return }
    // ONE OWNER (Chris, 2026-08-25). This effect is the only thing that seeds
    // `deps`. A separate "load the banked month" effect raced it: the month's
    // demand goes null for a beat while it loads, which used to clear the seed
    // key, and the WTT then re-seeded straight over the built timetable — so
    // "Build the modelling" looked like it did nothing and the month view
    // compared today with itself. The banked signature is IN the key, so a
    // finished build reseeds automatically and nothing else has to.
    const bankedView = bankedViewFor(monthKey)
    const banked = bankedView && bankedView.deps.length > 0 ? bankedView.deps : null
    const key = `${monthKey}|${dayClass}|${mode}|${bankedSig(monthKey)}`
    if (depsSeedKey.current === key && depsTouched.current) return // author's design stands
    const seed = chooseSeedSource({
      hasBanked: !!banked,
      // The month WAS modelled and no working pays. Show that, not thebigplan's
      // timetable — see chooseSeedSource for why.
      modelledEmpty: !!bankedView && bankedView.deps.length === 0,
      hasWtt: !!wttObserved && wttObserved.deps.length > 0,
    })
    let base: Departure[]
    if (seed === 'banked') {
      // The month has been built: the built timetable IS what to show.
      base = banked!
    } else if (seed === 'modelled-empty') {
      base = []
    } else if (seed === 'wtt') {
      base = wttObserved!.deps.map((d) => ({ ...d }))
    } else {
      const wk = selection.classes.filter((c) => c.cls.startsWith('Wkdy'))
      const src = isWeek && wk.length > 0 ? wk : selection.classes
      const north = src.flatMap((c) => c.north)
      const south = src.flatMap((c) => c.south)
      base = baselineDeps(net, north, south).map((d) => ({ ...d, id: d.id.replace(/^b-/, '') }))
    }
    if (mode === 'grosmont') {
      // A through working cannot be the starting point in Mode B: truncate the
      // observed pattern at Grosmont and let Best fit discover the shuttle. A
      // truncated leg is hypothetical — its measured arrival, diagram identity
      // and set pin no longer apply.
      const x = net.transfers[0]?.station ?? 3
      base = base.map((d) => {
        if (Math.min(d.o, d.d) < x && Math.max(d.o, d.d) > x) {
          const cut = d.d > d.o ? { ...d, d: x } : { ...d, o: x }
          delete cut.arrMin
          delete cut.diagramId
          delete cut.setPin
          return cut
        }
        return d
      })
    }
    depsSeedKey.current = key
    depsTouched.current = false
    setDeps(base)
    setFitLog(null)
    setClassView(null)
    setDayOverrides({})
    setDayDraft(null)
    setDraftDirty(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, mode, wttObserved, seedNonce, slices, monthKey])

  useEffect(() => {
    if (classView != null) {
      setDayDraft((dayOverrides[classView] ?? deps).map((d) => ({ ...d })))
      setDraftDirty(false)
    } else {
      setDayDraft(null)
      setDraftDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classView])

  const classDemandFor = useCallback(
    (cls: string): ClassDemand | undefined => selection?.classes.find((c) => c.cls === cls),
    [selection]
  )
  const viewClass = isWeek && classView != null ? classView : null
  const viewDemand = viewClass ? classDemandFor(viewClass) : undefined
  const activeDeps = viewClass != null ? (dayDraft ?? []) : deps

  const updateActive = (fn: (prev: Departure[]) => Departure[]) => {
    depsTouched.current = true
    if (viewClass != null) { setDayDraft((prev) => fn(prev ?? [])); setDraftDirty(true) }
    else setDeps((prev) => fn(prev))
  }

  // =========================================================================
  //  Evaluation
  // =========================================================================
  // Who is one-way on TODAY's timetable for the month on screen — the same rule
  // the season build applies (engine.ts::oneWayShares via planDemand), so the
  // live scratchpad and the banked build value the same people (Chris,
  // 2026-09-04: today's unreturnable travellers keep their sale, are never
  // stranded, and no one is moved onto a train they cannot get home from).
  const classesToday = useMemo<ClassDemand[]>(() => {
    if (!selection) return []
    if (!wttObserved) return selection.classes
    return selection.classes.map((c) => stampOneWay(c, oneWayShares(net, wttObserved.deps, c, params)))
  }, [selection, wttObserved, net, params])
  const viewDemandToday = useMemo<ClassDemand | null>(() => {
    if (!viewDemand) return null
    if (!wttObserved) return viewDemand
    return stampOneWay(viewDemand, oneWayShares(net, wttObserved.deps, viewDemand, params))
  }, [viewDemand, wttObserved, net, params])

  const evalResult = useMemo(() => {
    if (!selection) return null
    const mkPlan = (classes: ClassDemand[], d: Departure[]): SetPlan =>
      planSets(net, classes, d, setsRoster, params, objective, rates, dieselLoco, fleet, maxUnreturned, formation)

    if (!isWeek) {
      const plan = mkPlan(classesToday, deps)
      const applied = applySetPlan(deps, plan)
      const sc = runClasses(net, classesToday, applied, params)
      const c = costFromDeps(net, applied, rates, dieselLoco, fleet, plan.carriages, plan.setsInTraffic)
      return { scenario: sc, costsInfo: c, periodCosts: c.total * selection.runningDays, plan, applied, locoBy: legLocoCodes(net, applied, dieselLoco, fleet.locos) }
    }
    if (viewClass != null && viewDemandToday) {
      const dDeps = dayDraft ?? dayOverrides[viewClass] ?? deps
      const own = draftDirty || !!dayOverrides[viewClass]
      const plan = mkPlan(own ? [{ ...viewDemandToday, weight: 1 }] : classesToday, dDeps)
      const applied = applySetPlan(dDeps, plan)
      const sc = runScenario(net, viewDemandToday.north, viewDemandToday.south, applied, params)
      const c = costFromDeps(net, applied, rates, dieselLoco, fleet, plan.carriages, plan.setsInTraffic)
      return { scenario: sc, costsInfo: c, periodCosts: c.total, plan, applied, locoBy: legLocoCodes(net, applied, dieselLoco, fleet.locos) }
    }

    // Whole week, class by class, honouring per-class overrides.
    const sharedPlan = mkPlan(classesToday, deps)
    const appliedShared = applySetPlan(deps, sharedPlan)
    const sharedClasses = classesToday.filter((c) => !dayOverrides[c.cls])
    const base = sharedClasses.length > 0
      ? runClasses(net, sharedClasses, appliedShared, params)
      : runClasses(net, [], appliedShared, params)
    let scenario: Scenario = base
    let costsTotal = sharedClasses.reduce(
      (t, c) => t + costFromDeps(net, appliedShared, rates, dieselLoco, fleet, sharedPlan.carriages, sharedPlan.setsInTraffic).total * c.weight,
      0
    )
    for (const c of classesToday) {
      const ovr = dayOverrides[c.cls]
      if (!ovr) continue
      const p = mkPlan([{ ...c, weight: 1 }], ovr)
      const applied = applySetPlan(ovr, p)
      const r = runClasses(net, [{ ...c, weight: c.weight }], applied, params)
      costsTotal += costFromDeps(net, applied, rates, dieselLoco, fleet, p.carriages, p.setsInTraffic).total * c.weight
      scenario = mergeScenarios(scenario, r)
    }
    return {
      scenario,
      costsInfo: costFromDeps(net, appliedShared, rates, dieselLoco, fleet, sharedPlan.carriages, sharedPlan.setsInTraffic),
      periodCosts: costsTotal,
      plan: sharedPlan,
      applied: appliedShared,
      locoBy: legLocoCodes(net, appliedShared, dieselLoco, fleet.locos),
    }
  }, [selection, net, params, deps, dayDraft, draftDirty, dayOverrides, viewClass, viewDemandToday, classesToday, isWeek, setsRoster, formation, objective, rates, dieselLoco, fleet, maxUnreturned])

  const scenario = evalResult?.scenario ?? null
  const costs = evalResult?.costsInfo ?? null
  const periodCosts = evalResult?.periodCosts ?? 0
  const plan = evalResult?.plan ?? null
  const locoBy = evalResult?.locoBy ?? {}

  const conflicts = useMemo(() => (evalResult ? crossingConflicts(net, evalResult.applied) : []), [evalResult, net])
  const imbalances = useMemo(() => (evalResult ? stockImbalances(net, evalResult.applied) : []), [evalResult, net])
  const paths = useMemo(() => (evalResult ? pathUsage(net, evalResult.applied) : []), [evalResult, net])
  const chainNext = useMemo(() => chainNextOf(net, activeDeps), [net, activeDeps])

  // Baseline = what actually ran, at default sliders, on ITS OWN network (the
  // through line) — otherwise Mode B would be compared against a fiction.
  const baseline = useMemo(() => {
    if (!selection) return null
    const byCls: Record<string, { revenue: number; carried: number; costs: number; miles: number }> = {}
    let revenue = 0, carried = 0, costsTotal = 0, milesTotal = 0, lost = 0
    let lastCb: CostBreakdown | null = null
    let lastServices = 0
    for (const c of selection.classes) {
      // W0: "today" is the REAL working timetable when thebigplan supplies it.
      const bd = wttObserved?.deps ?? baselineDeps(net0, c.north, c.south)
      const bp: EngineParams = { toleranceMin: 75, recapture: 0.7, fareMult: 1, dwellMin: 180, returnMode, opts: params.opts }
      const p = planSets(net0, [{ ...c, weight: 1 }], bd, setsRoster, bp, 'revenue', rates, dieselLoco, fleet, Number.MAX_SAFE_INTEGER, formation)
      const applied = applySetPlan(bd, p)
      const r = runScenario(net0, c.north, c.south, applied, bp)
      const cb = costFromDeps(net0, applied, rates, dieselLoco, fleet, p.carriages, p.setsInTraffic)
      const cost = cb.total
      const cbMiles = cb.steamMiles + cb.dieselMiles
      byCls[c.cls] = { revenue: r.revenue, carried: r.carried, costs: cost, miles: cbMiles }
      revenue += r.revenue * c.weight
      carried += r.carried * c.weight
      costsTotal += cost * c.weight
      milesTotal += cbMiles * c.weight
      lost += r.lostSales * c.weight
      lastCb = cb
      lastServices = applied.filter((x) => x.enabled && !x.positioning).length
    }
    return {
      revenue, carried, costs: costsTotal, miles: milesTotal, lost, byCls, steamN: lastCb?.steamN ?? 0, dieselN: (lastCb?.dieselN ?? 0) + (lastCb?.railcars ?? 0), services: lastServices,
      // Each named engine's timetabled miles today, and the engines that worked.
      engineMiles: lastCb?.engineMiles ?? {}, engineDays: Object.fromEntries((lastCb?.engineWorked ?? []).map((k) => [k, 1])),
    }
  }, [selection, returnMode, params.opts, setsRoster, formation, rates, dieselLoco, fleet, wttObserved])

  const period = isWeek && viewClass == null ? 'week' : 'day'
  const runningDays = selection?.runningDays ?? 1

  const cardCtx = useMemo(() => {
    if (!selection) return null
    if (viewClass != null) {
      const info = selection.perCls[viewClass]
      const base = baseline?.byCls[viewClass] ?? { revenue: 0, carried: 0, costs: 0, miles: 0 }
      return {
        untimedNet: info?.untimedNet ?? 0, untimedPax: info?.untimedPax ?? 0,
        prevRev: info?.prevRev ?? null, prevPax: info?.prevPax ?? null, prevUntimedNet: info?.prevUntimedNet ?? null,
        baseRevenue: base.revenue, baseCarried: base.carried, baseCosts: base.costs, baseMiles: base.miles,
      }
    }
    return {
      untimedNet: selection.untimedNetPerPeriod, untimedPax: selection.untimedPaxPerPeriod,
      prevRev: selection.prevRevPerPeriod, prevPax: selection.prevPaxPerPeriod, prevUntimedNet: selection.prevUntimedNetPerPeriod,
      baseRevenue: baseline?.revenue ?? 0, baseCarried: baseline?.carried ?? 0, baseCosts: baseline?.costs ?? 0, baseMiles: baseline?.miles ?? 0,
    }
  }, [selection, viewClass, baseline])

  // =========================================================================
  //  Best fit
  // =========================================================================
  const fitConfig = useCallback((over?: Partial<FitConfig>): FitConfig | null => {
    if (!selection) return null
    const classes = viewClass != null && viewDemandToday ? [{ ...viewDemandToday, weight: 1 }] : classesToday
    const uniqCount = (pick: (c: ClassDemand) => Slot[]) =>
      new Set(classes.flatMap(pick).map((s) => `${s.time}|${s.route}`)).size
    return {
      net,
      classes,
      params,
      minGainPerDep: minRevPerDep,
      maxPerDir: Math.min(10, Math.max(uniqCount((c) => c.north), uniqCount((c) => c.south)) + 3),
      objective,
      rates,
      dieselLoco,
      fleet,
      sets: setsRoster,
      formation,
      maxUnreturned,
      firstMin: toMin(firstTrain) || 0,
      lastMin: toMin(lastTrain) || 24 * 60,
      ecsDiesel,
      ...over,
    }
  }, [selection, viewClass, viewDemandToday, classesToday, net, params, minRevPerDep, objective, rates, dieselLoco, fleet, setsRoster, formation, maxUnreturned, firstTrain, lastTrain, ecsDiesel])

  const runBestFit = (allowanceOverride?: number) => {
    const cfg = fitConfig(allowanceOverride != null ? { maxUnreturned: allowanceOverride } : undefined)
    if (!cfg) return
    const r = engineMode === 'ilp' ? ilpBuild(cfg) : bestFit(cfg)
    const built = r.deps.sort((a, b) => a.minutes - b.minutes)
    updateActive(() => built)
    // FLEET IS PHYSICS. The season design has always designated the overflow
    // as diesel rather than inventing a phantom engine; the interactive Best
    // fit did not, so a timetable needing a 4th steam diagram simply FAILED
    // with "4 steam diagrams needed but only 3 available" while the railway's
    // diesels and railcar sat idle (Chris, 2026-08-24). Same rule both sides.
    const fleetLog: string[] = []
    const c0 = costFromDeps(net, built, rates, dieselLoco, fleet)
    if (c0.steamN > fleet.maxSteam) {
      const overflow = c0.steamN - fleet.maxSteam
      const dieselRoom = fleet.maxDiesel + fleet.maxRailcar - (c0.dieselN + c0.railcars)
      const newDl = dieselLoco + overflow
      setDieselLoco(newDl)
      fleetLog.push(
        overflow <= dieselRoom
          ? `Fleet is physics: this timetable needs ${c0.steamN} steam diagrams and the railway has ${fleet.maxSteam}. The ${overflow} overflow diagram${overflow === 1 ? ' is' : 's are'} designated DIESEL (diesel designation now ${newDl}) — never a phantom steam engine. Steam haulage is unchanged on every other working.`
          : `Fleet is physics: this timetable needs ${c0.steamN} steam diagrams against ${fleet.maxSteam} steam, and the ${overflow} overflow exceeds the ${Math.max(0, dieselRoom)} spare diesel/railcar diagram${dieselRoom === 1 ? '' : 's'} too. It is NOT workable as drawn — cut a working, or confirm more availability.`
      )
    }
    // The header line stamps every run: two runs under different settings can
    // legitimately build the SAME timetable, and without the stamp that read
    // as "the button does nothing" (Chris, 2026-08-24).
    const n = built.filter((d) => d.enabled && !d.positioning).length
    setFitLog([
      `★ Best fit ran at ${new Date().toLocaleTimeString('en-GB')} — ±${tolerance} min tolerance · minimum £${minRevPerDep.toLocaleString('en-GB')} profit per working per ${period} · built ${n} revenue working${n === 1 ? '' : 's'}`,
      ...fleetLog,
      ...r.log,
    ])
    setStage('build')
  }

  // =========================================================================
  //  Scenario store
  // =========================================================================
  // Saved scenarios are RETIRED from the UI (Chris, 2026-08-20): the modelling
  // SESSION — slices + version-on-write — is the record of work now, and the
  // year comparison covers what scenario A/B was for. The scenarios API and
  // stored rows are untouched, so the panel can come back if ever missed.

  const captureState = (over?: MonthFixChange) => ({
    mode, dayClass, grain, monthKey,
    net: { mctMin, platforms, penaltyMin, waitWeight, aversionPct, nrPaths, steamOverNR, shuttleTurn, shuttleCoaches },
    demand: { tolerance, recapturePct, farePct, dwellMin, maxStay, forceEarly, spillNearest, returnMode, maxUnreturned: over?.maxUnreturned ?? maxUnreturned, censoring, monthSources, tti },
    window: { firstTrain, lastTrain },
    money: { rates, dieselLoco, objective, minRevPerDep, tractionLocked, faresLocked },
    pullman: { day: pullmanDay, night: pullmanNight },
    fleet: stripLocos(fleetWith(fleet, over)), setsRoster, formation,
    ecsDiesel: over?.ecsDiesel ?? ecsDiesel,
    firecon,
    enginePins,
    deps, dayOverrides,
    narrative,
    whatsChanged,
    advisor: advisorLog,
  })

  const applyState = (s: Record<string, unknown>) => {
    const g = <T,>(path: string[], fallback: T): T => {
      let cur: unknown = s
      for (const k of path) {
        if (cur == null || typeof cur !== 'object') return fallback
        cur = (cur as Record<string, unknown>)[k]
      }
      return (cur === undefined ? fallback : cur) as T
    }
    setMode(g(['mode'], 'through'))
    setDayClass(g(['dayClass'], 'Week'))
    // grain is derived from the data now (W8) — a saved choice is ignored.
    // Snap to the month the scenario was designed for. Without this, loading a
    // scenario from a different month than the one currently selected would run
    // its timetable against the wrong month's demand. Only set a valid key that
    // the current data actually offers, so we never strand the selector.
    const mk = g<string>(['monthKey'], '')
    if (/^\d{4}-\d{2}$/.test(mk) && monthOptions.some((o) => o.value === mk)) setMonthKey(mk)
    setMctMin(g(['net', 'mctMin'], DEFAULT_MCT_MIN))
    setPlatforms(g(['net', 'platforms'], DEFAULT_TRANSFER_PLATFORMS))
    setPenaltyMin(g(['net', 'penaltyMin'], DEFAULT_INTERCHANGE_PENALTY_MIN))
    setWaitWeight(g(['net', 'waitWeight'], DEFAULT_WAIT_WEIGHT))
    setAversionPct(g(['net', 'aversionPct'], 12))
    setNrPaths(g(['net', 'nrPaths'], 0))
    setSteamOverNR(g(['net', 'steamOverNR'], false))
    setShuttleTurn(g(['net', 'shuttleTurn'], DEFAULT_SHUTTLE_TURNAROUND_MIN))
    setShuttleCoaches(g(['net', 'shuttleCoaches'], DEFAULT_SHUTTLE_COACHES))
    // The saved MODEL-SETTINGS defaults OUTRANK a slice's frozen state — the
    // hard barriers must never change just because a slice was opened
    // (Chris, 2026-08-21). The slice's values stay in its state_json record.
    const re = railwayEngine.current
    setTolerance(re?.tolerance ?? g(['demand', 'tolerance'], 75))
    setRecapturePct(re?.recapturePct ?? g(['demand', 'recapturePct'], 70))
    setFarePct(re?.farePct ?? g(['demand', 'farePct'], 100))
    setDwellMin(re?.dwellMin ?? g(['demand', 'dwellMin'], 180))
    setMaxStay(re?.maxStay ?? g(['demand', 'maxStay'], false))
    setForceEarly(re?.forceEarly ?? g(['demand', 'forceEarly'], true))
    setSpillNearest(re?.spillNearest ?? g(['demand', 'spillNearest'], false))
    setReturnMode(re?.returnMode ?? g(['demand', 'returnMode'], 'latest'))
    setMaxUnreturned(re?.maxUnreturned ?? g(['demand', 'maxUnreturned'], 0))
    setMonthSources(re
      ? adoptSources(re)
      : g<MonthSources>(['demand', 'monthSources'], { by: {}, reason: '' }))
    setCensoring(re?.censoring ?? g(['demand', 'censoring'], true))
    // The saved operating window outranks a slice's frozen one, like every
    // other model setting; with none saved, a loaded scenario's window is its author's.
    windowTouched.current = true
    setFirstTrain(re?.window?.firstTrain ?? g(['window', 'firstTrain'], '09:30'))
    setLastTrain(re?.window?.lastTrain ?? g(['window', 'lastTrain'], '18:00'))
    {
      // The railway default OUTRANKS a slice's frozen model: opening a slice
      // must never overwrite a saved calibration (that overwrite is exactly
      // why Chris's saves 'didn't save'). The slice's model stays in its
      // state_json for the record.
      const savedModel = g<CostModel | null>(['money', 'rates'], null)
      setRates(railwayDefault.current
        ?? (savedModel && Array.isArray(savedModel.locos) ? savedModel : cloneCostModel(DEFAULT_COST_MODEL)))
    }
    setDieselLoco(g(['money', 'dieselLoco'], 0))
    // objective is always 'profit' (W4) — a saved 'revenue' choice is ignored.
    setMinRevPerDep(re?.minRevPerDep ?? g(['money', 'minRevPerDep'], 500))
    setFleet({ ...DEFAULT_FLEET, ...(railwayFleet.current ?? g<Partial<FleetCaps>>(['fleet'], {})) })
    setEcsDiesel(re?.ecsDiesel ?? g<boolean>(['ecsDiesel'], true))
    setFirecon(normaliseFirecon(re?.firecon ?? g<unknown>(['firecon'], DEFAULT_FIRECON)))
    // The planner's pins are season-wide: the most recently saved slice carries the
    // whole list (captureState writes it whole), whichever month was on screen.
    setEnginePins(latestSlicePins(slices) ?? cleanPins(g<unknown>(['enginePins'], [])))
    const rosterIn = railwaySets.current ?? g<CarriageSet[]>(['setsRoster'], [])
    // A roster saved before D6 carries no measured seats — the shipped measured
    // capacities beat it (else the 64/coach heuristic sneaks back in).
    if (rosterIn.length > 0 && rosterIn.some((x) => x.seats != null)) setSetsRoster(rosterIn)
    setFormationChoice(g<SetFormation | null>(['formation'], null))
    const depsIn = g<Departure[]>(['deps'], [])
    if (depsIn.length > 0) { depsTouched.current = true; setDeps(depsIn) }
    setDayOverrides(g(['dayOverrides'], {}))
    setNarrative(g(['narrative'], ''))
    setWhatsChanged(g(['whatsChanged'], ''))
    setTti(normaliseTti(re?.tti ?? g<unknown>(['demand', 'tti'], DEFAULT_TTI)))
    setPullmanDay(re?.pullman?.day ?? g(['pullman', 'day'], true))
    setPullmanNight(re?.pullman?.night ?? g(['pullman', 'night'], true))
    setTractionLocked(g(['money', 'tractionLocked'], true))
    setFaresLocked(g(['money', 'faresLocked'], true))
    setAdvisorLog(g<AdvisorRunLog | null>(['advisor'], null))
    setClassView(null)
    setDayDraft(null)
    setDraftDirty(false)
  }

  const headline = () => {
    // D4 guard: walk-up uplift only when the timetable actually runs services.
    const svc = evalResult ? evalResult.applied.filter((x) => x.enabled && !x.positioning).length : 0
    const rev = (scenario?.revenue ?? 0) + (svc > 0 ? (cardCtx?.untimedNet ?? 0) * (farePct / 100) : 0)
    return { revenue: rev, costs: periodCosts, contribution: rev - periodCosts, carried: scenario?.carried ?? 0, unreturned: scenario?.unreturned ?? 0 }
  }

  // =========================================================================
  //  Candidate evaluation — shared by the advisor and the A/B comparison
  // =========================================================================
  const asResult = useCallback((
    id: string, m: StudioMode, n: Network, classes: ClassDemand[], d: Departure[], pr: EngineParams,
    rt: CostModel, dl: number, fl: FleetCaps, obj: 'revenue' | 'profit', ns: number,
    untimed: number, fareMult: number
  ): CandidateResult => {
    const p = planSets(n, classes, d, setsRoster, pr, obj, rt, dl, fl, ns, formation)
    const applied = applySetPlan(d, p)
    const sc = runClasses(n, classes, applied, pr)
    const w = totalWeight(classes) || 1
    const cost = costFromDeps(n, applied, rt, dl, fl, p.carriages, p.setsInTraffic)
    // D4 guard: the untimed walk-up uplift is income earned BY RUNNING TRAINS —
    // a timetable with no services earns none of it. Crediting it to an empty
    // timetable is how an empty timetable "won" Compare in the live test.
    const svcN = applied.filter((x) => x.enabled && !x.positioning).length
    const revenue = sc.revenue + (svcN > 0 ? untimed * fareMult : 0)
    const periodCost = cost.total * w
    const pu = pathUsage(n, applied)
    const t = sc.transfers[0]
    return {
      id, mode: m,
      revenue, costs: periodCost, contribution: revenue - periodCost,
      carried: sc.carried, lostSales: sc.lostSales, unreturned: sc.unreturned,
      lostInterchange: sc.lostInterchange, connected: sc.connected,
      services: svcN,
      steamDiagrams: cost.steamN, dieselDiagrams: cost.dieselN + cost.railcars,
      carriages: cost.carriages, setsInTraffic: p.setsInTraffic,
      nrPathsUsed: pu.reduce((s, x) => s + x.used, 0),
      nrPathsAvailable: pu.length > 0 ? pu[0].available : null,
      nrPathsOver: pu.reduce((s, x) => s + Math.max(0, x.used - x.available), 0),
      peakTransferWaiting: t?.peakWaiting ?? 0,
      meanTransferWaitMin: t?.meanWaitMin ?? 0,
      fatalConflicts: crossingConflicts(n, applied).filter((x) => !x.resolvable).length,
      stockIssues: stockImbalances(n, applied).length,
      fleetOverCap: cost.overCap,
      coalTonnes: cost.coalTonnes,
      co2eTonnes: cost.co2eTonnes,
    }
  }, [setsRoster, formation])

  const evaluateCandidate = useCallback((cand: AdvisorCandidate): CandidateResult => {
    if (!selection) throw new Error('no demand selected')
    const s = cand.settings
    const cm = cand.mode
    // THE PLANNER'S HARD BARRIERS ARE PINNED (Chris, 2026-08-24: a candidate
    // reset his ±75 min tolerance to 120 and its "win" was scored under it).
    // A candidate shapes the TIMETABLE — which turns run and how they chain.
    // The behavioural model (tolerance, recapture, dwell, return rules), the
    // £/working floor, the operating window, the NR path budget and the
    // steam-over-NR policy are the planner's inputs, exactly like fares and
    // traction: every candidate is scored under the planner's values, so no
    // candidate can "win" by moving an assumption. Mode B interchange
    // assumptions (connection time, aversion, wait weighting) stay explorable
    // — they are Claude's declared assumptions, not the planner's barriers.
    const n0 = buildNetwork(cm, {
      mctMin: s.mctMin ?? mctMin,
      transferPlatforms: platforms,
      interchangePenaltyMin: penaltyMin,
      waitWeight: s.waitWeight ?? waitWeight,
      aversionShare: (s.aversionPct ?? aversionPct) / 100,
      steamOverNR,
      shuttleTurnaroundMin: shuttleTurn,
      nrPathsPerDay: nrPaths > 0 ? nrPaths : undefined,
    })
    const n = withPathBudget(n0, nrPaths > 0 ? nrPaths : observedPaths)
    // Fares locked ⇒ every candidate is scored at the planner's fare level.
    const fm = (faresLocked ? farePct : (s.farePct ?? farePct)) / 100
    const pr: EngineParams = {
      toleranceMin: tolerance,
      recapture: recapturePct / 100,
      fareMult: fm,
      dwellMin,
      returnMode,
      opts: {
        forceEarly,
        maxStay,
        lastMin: toMin(lastTrain) || 24 * 60,
      },
    }
    const obj = objective // W4: profit only — a candidate cannot switch objective
    const ns = maxUnreturned
    const sets = cm === 'grosmont'
      ? modeSets(cm).map((x) => (x.code === 'SH' ? { ...x, coaches: s.shuttleCoaches ?? shuttleCoaches } : x))
      : modeSets(cm)
    const cfg: FitConfig = {
      net: n, classes: selection.classes, params: pr,
      minGainPerDep: minRevPerDep,
      // Traction lock: a candidate may not re-mix steam and diesel — steam is
      // the product (Chris, 2026-08-20).
      maxPerDir: 10, objective: obj, rates, dieselLoco: tractionLocked ? dieselLoco : (s.dieselLoco ?? dieselLoco),
      fleet, sets, maxUnreturned: ns,
      firstMin: toMin(firstTrain) || 0,
      lastMin: toMin(lastTrain) || 24 * 60,
    }
    const built = cand.bestFit ? bestFit(cfg).deps : deps
    // asResult uses the component's roster; align it with the candidate's.
    const p = planSets(n, selection.classes, built, sets, pr, obj, rates, cfg.dieselLoco, fleet, ns, formation)
    const applied = applySetPlan(built, p)
    const base = asResult(cand.id, cm, n, selection.classes, applied, pr, rates, cfg.dieselLoco, fleet, obj, ns, selection.untimedNetPerPeriod, fm)
    return base
  }, [selection, mctMin, platforms, penaltyMin, waitWeight, aversionPct, steamOverNR, shuttleTurn, nrPaths, observedPaths, farePct, tolerance, recapturePct, dwellMin, returnMode, forceEarly, maxStay, lastTrain, firstTrain, objective, maxUnreturned, shuttleCoaches, minRevPerDep, rates, dieselLoco, fleet, deps, asResult])

  const applyCandidate = (candidateId: string) => {
    if (!advisorLog) return
    const cand = findCandidate(advisorLog, candidateId)
    if (!cand) return
    const s = cand.settings
    setMode(cand.mode)
    // THE PLANNER'S HARD BARRIERS ARE PINNED (Chris, 2026-08-24): adopting a
    // candidate takes its TIMETABLE IDEA — the mode and, for Mode B, its
    // interchange assumptions — and rebuilds it under the planner's own
    // tolerance, recapture, dwell, return rules, £/working floor, operating
    // window, path budget and unreturned allowance. A candidate that only
    // scored well by moving one of those has not answered the question, and
    // adopting it must never quietly move the planner's sliders.
    if (s.farePct != null && !faresLocked) setFarePct(s.farePct)
    // objective is pinned to 'profit' (W4) — an advisor candidate cannot change it.
    // Traction lock: applying a candidate never re-mixes steam and diesel.
    if (s.dieselLoco != null && !tractionLocked) setDieselLoco(s.dieselLoco)
    if (s.mctMin != null) setMctMin(s.mctMin)
    if (s.aversionPct != null) setAversionPct(s.aversionPct)
    if (s.waitWeight != null) setWaitWeight(s.waitWeight)
    if (s.shuttleCoaches != null) setShuttleCoaches(s.shuttleCoaches)
    setStage('build')
    // Rebuild on the next tick so the new settings are in play.
    setTimeout(() => runBestFit(), 0)
  }

  const stopAdvisor = () => { advisorAbort.current?.abort() }

  const startAdvisor = async () => {
    if (!selection || !scenario || !costs || !cardCtx || !data) return
    setAdvisorRunning(true)
    setStage('explore')
    const ctrl = new AbortController()
    advisorAbort.current = ctrl
    const settings: AdvisorSettings = {
      tolerance, recapturePct, dwellMin, farePct, firstTrain, lastTrain, minRevPerDep,
      objective, dieselLoco, maxStay, forceEarly, maxUnreturned,
      mctMin, aversionPct, waitWeight, nrPathsPerDay: nrPaths, shuttleCoaches, steamOverNR,
    }
    const baselineRes = asResult(
      'baseline', mode, net, selection.classes, evalResult?.applied ?? deps, params,
      rates, dieselLoco, fleet, objective, maxUnreturned, selection.untimedNetPerPeriod, farePct / 100
    )
    try {
      const log = await runAdvisor(
        {
          season: data.season,
          monthLabel: monthOptions.find((m) => m.value === monthKey)?.label ?? monthKey,
          dayClassLabel: dayLabel,
          mode,
          demandProfile: demandChart.map((r) => ({ time: r.time, north: r.Northbound, south: r.Southbound })),
          baseline: baselineRes,
          settings,
          network: {
            stations: net.stations.map((s) => s.name),
            nrSegment: 'Grosmont – Whitby',
            mctMin,
            transferPlatforms: platforms,
            nrPathsObservedToday: observedPaths,
          },
          fleet,
          sets: setsRoster.filter((s) => s.active).map((s) => ({ code: s.code, seats: setSeats(s, formation), coaches: s.coaches, stables: net.stations[s.start].name })),
          backtest: backtest ? { mape: backtest.mape, bias: backtest.bias, slices: backtest.sliceCount } : null,
          userSteer: [
            // The locks are standing steers, not knobs Claude can move.
            // These are the planner's inputs, set on the Build tab — NOT knobs
            // Claude may turn. They are pinned UNCONDITIONALLY, whether or not
            // the lock toggles happen to be on (Chris, 2026-08-26: the kickoff
            // must make sure Claude never changes fares, tolerances or any
            // baseline setting). Laws 4 and 5.
            `TRACTION MIX IS FIXED: keep dieselLoco exactly ${dieselLoco} — steam is the railway's primary tourist draw. Do not re-mix steam and diesel or recommend more diesel.${tractionLocked ? ' The planner has locked this.' : ''}`,
            `FARES ARE FIXED at ${farePct}% of today and are NOT yours to change: score every candidate at this exact fare. Do not propose or recommend a fare change — the gains must come from operating the timetable for less (fewer engine diagrams, fewer turns, fewer carriage sets) while serving the same demand. You may run ONE clearly-labelled fare sensitivity to quantify what a change would be worth, but it can never be the recommendation.${faresLocked ? ' The planner has locked this.' : ''}`,
            `THE PLANNER'S BARRIERS ARE FIXED and enforced by the client — do NOT propose changing any of them: tolerance ±${tolerance} min, recapture ${recapturePct}%, dwell ${dwellMin} min, minimum £${minRevPerDep}/working, the ${firstTrain}–${lastTrain} operating window, the Network Rail path budget and the unreturned allowance (${maxUnreturned}). Every candidate is scored under these exact values — proposing different ones changes nothing, so spend the search on the timetable itself: which turns run, when, and how they chain into diagrams and sets.`,
            userSteer.trim(),
          ].filter(Boolean).join('\n') || undefined,
        },
        evaluateCandidate,
        (l) => { setAdvisorLog({ ...l }) },
        ctrl.signal
      )
      setAdvisorLog(log)
      // The advisor NO LONGER writes the board paper's narrative or "what
      // changes" (Chris, 2026-08-26). When it scored an empty run its prose
      // said "no trains built, cannot recommend" and contradicted a season the
      // model had actually built — nonsense on the page. The board paper's
      // case now comes from the season build itself (see the report assembly);
      // Explore stays an optional tool and its runs are in Appendix F only.
    } finally {
      setAdvisorRunning(false)
      advisorAbort.current = null
    }
  }

  // (Saved-scenario A/B comparison retired with the panel — sessions + the
  // year comparison are the record now; API and rows remain.)

  // =========================================================================
  //  Ticket mix for the service breakdown modal
  // =========================================================================
  const [mixes, setMixes] = useState<Record<string, { item: string; qty: number; net: number }[]> | null>(null)
  useEffect(() => {
    if (!enabled || !monthKey) return
    let live = true
    setMixes(null)
    const [y, m] = monthKey.split('-').map(Number)
    Promise.all([1, 2, 3, 4, 5, 6, 7].map((dn) =>
      fetch(`/api/research/weekday-revenue/detail?people=1&day_no=${dn}&y=${y}&m=${m}`)
        .then((r) => (r.ok ? (r.json() as Promise<{ rows: { item: string; qty: number; net: number }[] }>) : Promise.reject(new Error(String(r.status)))))
        .then((b) => ({ dn, rows: b.rows }))
    ))
      .then((all) => {
        if (!live) return
        const acc: Record<string, Map<string, { item: string; qty: number; net: number }>> = {
          Sat: new Map(), Sun: new Map(), Wkdy: new Map(), WkdyH: new Map(), WkdyT: new Map(), all: new Map(),
        }
        for (const { dn, rows } of all) {
          const cls = dn === 6 ? 'Sat' : dn === 7 ? 'Sun' : 'Wkdy'
          for (const r of rows) {
            for (const key of [cls, 'WkdyH', 'WkdyT', 'all'].filter((k) => k === cls || k === 'all' || cls === 'Wkdy')) {
              const cur = acc[key].get(r.item) ?? { item: r.item, qty: 0, net: 0 }
              cur.qty += r.qty; cur.net += r.net
              acc[key].set(r.item, cur)
            }
          }
        }
        setMixes(Object.fromEntries(Object.entries(acc).map(([k, v]) => [k, [...v.values()].sort((a, b) => b.net - a.net)])))
      })
      .catch(() => { if (live) setMixes({}) })
    return () => { live = false }
  }, [enabled, monthKey])

  // =========================================================================
  //  Charts
  // =========================================================================
  const demandChart = useMemo(() => {
    if (!selection) return [] as { time: string; Northbound: number; Southbound: number; 'N last season'?: number; 'S last season'?: number }[]
    const cur: ClassDemand[] = viewClass != null ? (viewDemand ? [{ ...viewDemand, weight: 1 }] : []) : selection.classes
    const prev: ClassDemand[] = viewClass != null
      ? selection.prevClasses.filter((c) => c.cls === viewClass).map((c) => ({ ...c, weight: 1 }))
      : selection.prevClasses
    const sum = (classes: ClassDemand[], pick: (c: ClassDemand) => Slot[], t: string) =>
      Math.round(classes.reduce((tot, c) => tot + c.weight * pick(c).filter((s) => s.time === t).reduce((x, s) => x + s.paxPerDay, 0), 0))
    const all = [
      ...cur.flatMap((c) => [...c.north, ...c.south]),
      ...(showPrev ? prev.flatMap((c) => [...c.north, ...c.south]) : []),
    ]
    const times = [...new Set(all.map((s) => s.time))].sort((a, b) => toMin(a) - toMin(b))
    return times.map((t) => ({
      time: t,
      Northbound: sum(cur, (c) => c.north, t),
      Southbound: sum(cur, (c) => c.south, t),
      ...(showPrev ? { 'N last season': sum(prev, (c) => c.north, t), 'S last season': sum(prev, (c) => c.south, t) } : {}),
    }))
  }, [selection, showPrev, viewClass, viewDemand])

  const censoringSummary = useMemo(() => {
    if (!selection || !censoring) return null
    const reps = Object.values(selection.reports)
    if (reps.length === 0) return null
    const applied = reps.filter((r) => r.applied)
    const added = applied.reduce((t, r) => t + r.addedPerDay, 0)
    const lifted = applied.reduce((t, r) => t + Object.keys(r.uplift).length, 0)
    const declined = reps.filter((r) => !r.applied)
    return { applied: applied.length, declined, added, lifted }
  }, [selection, censoring])

  const dayLabel = isWeek
    ? (viewClass != null ? (CLASS_LABELS[viewClass] ?? viewClass) : 'Full week')
    : (CLASS_LABELS[dayClass] ?? dayClass)
  const monthLabel = monthOptions.find((m) => m.value === monthKey)?.label ?? ''

  // =========================================================================
  //  Executive report
  // =========================================================================
  const makeSide = (label: string, n: Network, d: Departure[], classes: ClassDemand[], pr: EngineParams, untimed: number, fm: number, sideFormation?: SetFormation, dlOverride?: number): ExecSide => {
    const p = planSets(n, classes, d, setsRoster, pr, objective, rates, dlOverride ?? dieselLoco, fleet, Number.MAX_SAFE_INTEGER, sideFormation ?? formation)
    const applied = applySetPlan(d, p)
    const sc = runClasses(n, classes, applied, pr)
    const cost = costFromDeps(n, applied, rates, dlOverride ?? dieselLoco, fleet, p.carriages, p.setsInTraffic)
    const w = totalWeight(classes) || 1
    // D4 guard: no services ⇒ no walk-up uplift for this side.
    const sideSvc = applied.filter((x) => x.enabled && !x.positioning).length
    // Per-working set + loco for the "Working turns and capacity spread" panel
    // (Chris, 2026-08-27). Same source as the on-screen panel: the set plan's
    // byDep and the leg loco assignment.
    const locoCodes = legLocoCodes(n, applied, dlOverride ?? dieselLoco)
    const locoByDep: Record<string, { label: string; code: string }> = {}
    for (const [id, lc] of Object.entries(locoCodes)) {
      const v = lc as { label?: string; code?: string }
      locoByDep[id] = { label: v.label ?? '', code: v.code ?? '' }
    }
    // Belt-and-braces: stamp the set code and loco label onto the departures
    // themselves too, so the board paper's working-turns panel can show them
    // even if the id-keyed maps and the displayed deps ever diverge (Chris,
    // 2026-08-27: the optimised turns table showed "—" for set and loco).
    const stamped = applied.map((x) => (x.enabled ? { ...x, setCode: p.byDep[x.id], locoLabel: locoCodes[x.id]?.label } : x))
    return {
      label, scenario: sc, deps: stamped, costs: cost,
      periodCosts: cost.total * w,
      periodRevenue: sc.revenue + (sideSvc > 0 ? untimed * fm : 0),
      paths: pathUsage(n, applied),
      conflicts: crossingConflicts(n, applied),
      imbalances: stockImbalances(n, applied),
      transfers: sc.transfers,
      setsInTraffic: p.setsInTraffic,
      setByDep: p.byDep ?? {},
      locoByDep,
    }
  }

  /** The season workbook (Excel) — Summary, per-month WSS day by day, and
   *  per-month timetables, optimised AND unoptimised (Chris, 2026-09-02). It
   *  reads the SAME buildSeasonExec cells the board paper prints. */
  const exportWorkbook = async () => {
    if (!data) { setSessionError('No Studio data loaded yet — wait for the season to load, then export.'); return }
    setSessionError(null)
    setXlsxBusy(true)
    try {
      const exec = await buildSeasonExec()
      if (exec.xlsxMonths.length === 0) {
        setSessionError('Build the season (★ Build Model) before exporting the workbook — there are no designed months to write.')
        return
      }
      // The Fleet sheet: the register, the availability grid over the season's running
      // weeks, each engine's season on the roster, and the allocation date by date.
      const regLocos = fleet.locos ?? []
      const runningAll = monthOptions.flatMap((mo) => (monthDayGroups(mo.value)?.groups ?? []).filter((g) => !unscorableColour(g.colour)).flatMap((g) => g.dates)).sort()
      const grid = availabilityGrid(regLocos, runningAll)
      const monthNos = monthOptions.map((mo) => Number(mo.value.split('-')[1]))
      const fleetSheet: StudioXlsxFleet | null = locos.length > 0 ? {
        register: [...locos].sort((a, b) => (a.sortOrder - b.sortOrder) || a.number.localeCompare(b.number)).map((l) => ({
          number: l.number, name: l.name ?? '', owner: l.owner ?? '', ownership: l.ownership, traction: l.traction, fuel: l.fuel, whitby: l.whitbyEnabled, inSimulation: l.inSimulation,
          costPerMile: l.costPerMile, dayRate: l.dayRate, fixedAnnualCost: l.fixedAnnualCost, maintenancePerMile: l.maintenancePerMile,
          minContractMiles: l.minContractMiles, basisMiles: l.basisMiles,
          basisAssumed: l.basisMiles != null ? 'typed (COO)' : (l.minContractMiles ?? 0) > 0 ? 'the contracted minimum' : 'estimated from the timetable',
          activeFrom: l.activeFrom, activeTo: l.activeTo, washoutSeed: l.washoutSeed, washoutCycleDays: l.washoutCycleDays, washoutLockoutDays: l.washoutLockoutDays,
          boilerCertExpiry: l.boilerCertExpiry, unavailable: (l.unavailable ?? []).map((p) => `${p.from} to ${p.to}: ${p.reason || 'out of service'}`).join('; '), notes: l.notes ?? '',
        })),
        weeks: grid.weeks.map((w) => w.start),
        outDays: grid.rows.map((r) => ({ engine: engineLabel(r.engine), cells: r.cells.map((c) => c.out), reasons: r.cells.map((c) => c.reasons) })),
        available: (['steam', 'diesel', 'railcar'] as const).filter((t) => regLocos.some((l) => l.traction === t)).map((t) => ({ traction: t, cells: grid.available[t] })),
        engines: seasonRoster ? Object.values(seasonRoster.engines).sort((a, b) => b.miles - a.miles).map((e) => ({ engine: e.label, traction: e.traction, minimum: e.minimum, miles: e.miles, days: e.days, blocks: e.blocks, milesByMonth: e.milesByMonth })) : null,
        allocation: seasonRoster ? [...seasonRoster.byDate.values()].sort((a, b) => a.date.localeCompare(b.date)).flatMap((day) => day.turns.map((t) => ({
          date: day.date, colour: day.colour, turn: t.key.split('|').slice(1).join(' '), traction: t.traction,
          engine: t.engineId != null ? engineLabel(regLocos.find((l) => l.id === t.engineId) ?? { number: String(t.engineId), name: null }) : 'NO ENGINE',
          reason: t.reason, swapped: t.swappedToDiesel, pinned: t.pinned, failure: day.failure, miles: t.miles,
        }))) : [],
        monthNos,
      } : null
      // The Cost model sheet: the lines the Cost model stage shows, both tables.
      const costSheet = pnlSim ? {
        basis: costBasis, hasModel: pnlSim.hasModel,
        lines: pnlSim.sim.lines.map((r) => ({
          label: r.line.label, category: r.line.category, kind: r.line.kind, behaviour: r.line.behaviour === 'fixed' ? 'fixed' : `variable · ${r.line.driver}`,
          rate: r.rateLabel ?? null, included: r.line.included, basisMonths: r.basisMonths, modelMonths: r.modelMonths, basisTotal: r.basisTotal, modelTotal: r.modelTotal, delta: r.delta,
          rollingStock: r.line.category === ROLLING_STOCK_CATEGORY || r.line.category === LOCO_CATEGORY || !!r.line.parentId || !!r.line.residualOf || !!r.line.memoOf, memo: !!r.line.memoOf || !!r.line.rsMemo,
        })),
      } : null
      await buildStudioWorkbook({ season: data.season, yearRows, months: exec.xlsxMonths, fleet: fleetSheet, costModel: costSheet })
    } catch (e) {
      setSessionError(`Couldn't build the workbook: ${String((e as Error)?.message ?? e)}`)
    } finally {
      setXlsxBusy(false)
    }
  }

  const exportExecReport = async () => {
    if (!data) { setSessionError('No Studio data loaded yet — wait for the season to load, then export.'); return }
    if (!selection || !scenario || !costs || !cardCtx || !evalResult) {
      setSessionError('Open a month that has a design (or build the season) before exporting a board paper.')
      return
    }
    setSessionError(null)
    setPdfBusy(true)
    try {
      const fm = farePct / 100
      const untimed = cardCtx.untimedNet
      const basePr: EngineParams = { toleranceMin: 75, recapture: 0.7, fareMult: 1, dwellMin: 180, returnMode, opts: params.opts }
      const current = makeSide(
        'Today', net0,
        wttObserved?.deps ?? baselineDeps(net0, selection.classes.flatMap((c) => c.north), selection.classes.flatMap((c) => c.south)),
        selection.classes, basePr, untimed, 1
      )
      const proposed = makeSide('Proposed', net, evalResult.applied, selection.classes, params, untimed, fm)

      // Sensitivity: vary only what the conclusion might hang on.
      const sens: { label: string; contribution: number; note: string }[] = []
      const variant = (label: string, note: string, over: Partial<{ pr: EngineParams; n: Network }>) => {
        const s = makeSide(label, over.n ?? net, evalResult.applied, selection.classes, over.pr ?? params, untimed, fm)
        sens.push({ label, contribution: s.periodRevenue - s.periodCosts, note })
      }
      variant('Recapture 50%', 'less sticky spilled demand', { pr: { ...params, recapture: 0.5 } })
      variant('Recapture 90%', 'stickier spilled demand', { pr: { ...params, recapture: 0.9 } })
      variant('Fares +10%', 'elasticity -0.3', { pr: { ...params, fareMult: fm * 1.1 } })
      variant('Fares -10%', 'elasticity -0.3', { pr: { ...params, fareMult: fm * 0.9 } })
      if (mode === 'grosmont') {
        for (const av of [Math.max(0, aversionPct - 10), aversionPct + 10]) {
          variant(
            `Refuse to change ${av}%`,
            'the least measurable assumption in the model',
            { n: withPathBudget(buildNetwork('grosmont', { mctMin, transferPlatforms: platforms, interchangePenaltyMin: penaltyMin, waitWeight, aversionShare: av / 100, steamOverNR, shuttleTurnaroundMin: shuttleTurn }), nrPaths > 0 ? nrPaths : observedPaths) }
          )
        }
        variant(
          'Connection 20 min',
          'optimistic — 30 min is the operational figure',
          { n: withPathBudget(buildNetwork('grosmont', { mctMin: 20, transferPlatforms: platforms, interchangePenaltyMin: penaltyMin, waitWeight, aversionShare: aversionPct / 100, steamOverNR, shuttleTurnaroundMin: shuttleTurn }), nrPaths > 0 ? nrPaths : observedPaths) }
        )
      }

      // Month-by-month, on the same timetable, at each month's own demand.
      const monthly = monthOptions.map((mo) => {
        const [yy, mm] = mo.value.split('-').map(Number)
        const cls: ClassDemand[] = []
        const w = classWeights(data.untimed, yy, mm, classesInGrain)
        for (const c of classesInGrain) {
          const rows = data.demand.filter((r) => r.yr === yy && r.month_no === mm && r.day_class === c)
          if (rows.length === 0 || (w[c] ?? 0) <= 0) continue
          const cd = timedRunningDays(rows)
          cls.push(buildClassDemand(net0, rows, data.slot_daily, c, w[c], yy, mm, censoring, cd).demand)
        }
        if (cls.length === 0) return { label: mo.label.slice(0, 3), current: 0, proposed: 0 }
        const un = classesInGrain.reduce((t, c) => {
          const u = data.untimed.find((x) => x.yr === yy && x.month_no === mm && x.day_class === c)
          return t + (u && u.running_days > 0 ? (u.net / u.running_days) * (w[c] ?? 0) : 0)
        }, 0)
        const cSide = makeSide('c', net0, baselineDeps(net0, cls.flatMap((c) => c.north), cls.flatMap((c) => c.south)), cls, basePr, un, 1)
        const pSide = makeSide('p', net, evalResult.applied, cls, params, un, fm)
        return {
          label: mo.label.slice(0, 3),
          current: cSide.periodRevenue - cSide.periodCosts,
          proposed: pSide.periodRevenue - pSide.periodCosts,
        }
      })

      const assumptions: ExecReportData['assumptions'] = [
        { name: 'One timetable per colour', value: 'the whole week', provenance: 'measured', note: 'Each published timetable is designed once, against the demand of every day it runs, and then valued day by day on each day-type\u2019s own sales. The railway publishes one Red timetable and runs it Monday to Saturday; Sunday has its own colour.' },
        ...(monthSourceRows.length > 0
          ? [{
              name: 'Where each month\u2019s data comes from',
              value: monthSourceRows.map((m) => `${m.label.replace(/ \d{4}$/, '')} ${m.auto ?? '—'}${m.chosen ? '*' : ''}`).join(', '),
              provenance: 'measured' as const,
              note: `The season being designed has not traded, so each month takes its sales, capacity and turns from a season that has — the most recent one that finished trading and describes the railway we intend to run. Months marked * were set by hand.${monthSources.reason ? ` ${monthSources.reason}` : ''}`,
            }]
          : []),
        { name: 'Departure-time tolerance', value: `±${tolerance} min`, provenance: 'assumed', note: 'How far a passenger will shift from their desired departure before giving up.' },
        { name: 'Recapture rate', value: `${recapturePct}%`, provenance: 'assumed', note: 'Share of spilled demand that survives to the next departure. The honest uncertainty knob.' },
        { name: 'Minimum time at destination', value: maxStay ? 'max stay' : `${Math.floor(dwellMin / 60)}h ${dwellMin % 60}m`, provenance: 'assumed', note: 'Before a passenger wants their return train.' },
        { name: 'Minimum profit per working', value: `£${minRevPerDep.toLocaleString('en-GB')} per ${period}`, provenance: 'assumed', note: `The withdrawal test. A working is only kept if its fares beat the extra cost of running it by at least this much over the ${period}; day-fixed costs are not charged to it. This is a judgement, and it is what decides how deep the cuts go.` },
        { name: 'Fare level', value: `${farePct}% of today`, provenance: 'measured', note: 'Yield per ticket type is measured; the demand response uses an elasticity of -0.3.' },
        { name: 'Operating window', value: `${firstTrain}–${lastTrain}`, provenance: wttWindow && firstTrain === wttWindow.first && lastTrain === wttWindow.last ? 'measured' : 'assumed', note: wttWindow ? `Defaulted from the real WTT (first dep ${wttWindow.first}, last arr ${wttWindow.last}); editable.` : 'No working may depart outside this, including invented rescue trains.' },
        { name: 'Seats per coach', value: '64 (Autocar 48)', provenance: 'assumed', note: 'Indicative until ERP supplies real per-service consists.' },
        ...costModelRates(rates).map((r) => ({
          name: r.label,
          value: r.label.includes('on-costs') ? `${Math.round(r.rate.value * 100)}%` : `£${r.rate.value.toLocaleString('en-GB')}`,
          provenance: r.rate.prov,
          note: r.rate.basis,
        })),
        { name: 'Fleet available', value: `${fleet.maxSteam} steam, ${fleet.maxDiesel} diesel, ${fleet.maxRailcar} railcar`, provenance: 'assumed', note: 'Boiler tickets and washouts constrain this in practice.' },
        {
          name: 'Set capacities',
          value: setsRoster.filter((s) => s.active).map((s) => `${s.code} ${setSeats(s, formation)}`).join(' · '),
          provenance: 'measured',
          note: `Seats per set, ${formation === 'peak' ? 'peak' : 'off-peak'} formation (Chris, 2026-08-19: Whitby 407/320, Internal 279/~220, Autocar 90).`,
        },
        {
          name: 'Formation',
          value: formation === 'peak' ? 'Peak' : 'Off-peak',
          provenance: formationChoice ? 'measured' : 'assumed',
          note: formationChoice ? 'Chosen on the set roster.' : 'Derived from the modelled month (school-summer months run the strengthened rakes) — override on the set roster.',
        },
        { name: 'Turnaround', value: `${turnaroundAt(net, 0)} min`, provenance: 'assumed', note: 'Per station; the shuttle turns quicker than a full rake.' },
        ...(paths.length > 0 ? [{
          name: 'Network Rail paths',
          value: `${paths[0].available} per day`,
          provenance: (nrPaths > 0 ? 'assumed' : 'measured') as 'assumed' | 'measured',
          note: nrPaths > 0
            ? 'Manually overridden. The default is what we run today.'
            : 'Derived from the observed timetable — the railway\'s instruction is to run no more than we need to.',
        }] : []),
        ...(mode === 'grosmont' ? [
          { name: 'Grosmont connection time', value: `${mctMin} min each way`, provenance: 'measured' as const, note: 'Operational figure supplied by the railway: how long it takes to move a full trainload between trains.' },
          { name: 'Grosmont platforms', value: String(platforms), provenance: 'measured' as const, note: 'Two roads; both can be occupied for a transfer at once.' },
          { name: 'Refuse to change trains', value: `${aversionPct}%`, provenance: 'assumed' as const, note: 'CANNOT be measured — no observed passenger has ever changed at Grosmont. The most uncertain number in the model.' },
          { name: 'Interchange penalty', value: `${penaltyMin} min equivalent`, provenance: 'assumed' as const, note: 'Standard rail practice; waiting time additionally weighted ×' + waitWeight + '.' },
          { name: 'Steam over Network Rail', value: steamOverNR ? 'permitted' : 'not permitted', provenance: 'assumed' as const, note: 'Diesel-only is far simpler to regulate — no mainline steam certification.' },
        ] : []),
        ...(advisorLog?.recommendation?.assumptions ?? []).map((a) => ({ ...a })),
      ]

      const findings: string[] = []
      const dContrib = (proposed.periodRevenue - proposed.periodCosts) - (current.periodRevenue - current.periodCosts)
      findings.push(
        `${mode === 'grosmont' ? 'Connecting at Grosmont' : 'The optimised through timetable'} changes contribution by ${dContrib >= 0 ? '+' : ''}${gbp(dContrib)} per ${period} on ${dayLabel.toLowerCase()} demand.`
      )
      findings.push(
        `Services go from ${current.deps.filter((x) => x.enabled).length} to ${proposed.deps.filter((x) => x.enabled).length} per operating day, on ${proposed.costs.diagrams.length} engine diagram${proposed.costs.diagrams.length === 1 ? '' : 's'} (${proposed.costs.steamN} steam, ${proposed.costs.dieselN + proposed.costs.railcars} diesel).`
      )
      if (proposed.scenario.lostSales > current.scenario.lostSales) {
        findings.push(`It turns away ${num0.format(Math.round(proposed.scenario.lostSales - current.scenario.lostSales))} more people per ${period} than today — capacity is binding somewhere.`)
      } else if (current.scenario.lostSales > 0) {
        findings.push(`Turn-aways fall by ${num0.format(Math.round(current.scenario.lostSales - proposed.scenario.lostSales))} per ${period}.`)
      }
      if (mode === 'grosmont' && proposed.transfers.length > 0) {
        const t = proposed.transfers[0]
        findings.push(`${num0.format(Math.round(t.transferred))} passenger connections per ${period} at Grosmont, peaking at ${num0.format(Math.round(t.peakWaiting))} people waiting around ${fmtMin(t.peakAtMin)} with a mean wait of ${Math.round(t.meanWaitMin)} minutes.`)
        if (proposed.scenario.lostInterchange > 0) {
          findings.push(`${num0.format(Math.round(proposed.scenario.lostInterchange))} passengers per ${period} are assumed to refuse the change altogether — this is an assumption, not a measurement.`)
        }
      }
      if (paths.length > 0) {
        findings.push(`Network Rail paths: ${paths[0].used} of ${paths[0].available} used${paths[0].over ? ' — OVER BUDGET' : ''}.`)
      }

      const risks: string[] = [
        ...(advisorLog?.recommendation?.risks ?? []),
      ]
      if (mode === 'grosmont') {
        risks.push('Interchange behaviour is unmeasurable from ticket sales. Mitigation: trial the pattern on a small number of days and count the actual refusal rate before committing a season.')
        risks.push('A 30-minute connection each way adds an hour to a round trip. Mitigation: confirm with station staff whether the transfer can be shortened with more platform staff or better signage before assuming it can.')
      }
      if (proposed.costs.overCap.length > 0) risks.push(`The plan exceeds the available fleet (${proposed.costs.overCap.join('; ')}). Mitigation: reduce the diagram count or confirm additional availability.`)
      if (proposed.scenario.unreturned > 0.5) risks.push(`${num0.format(Math.round(proposed.scenario.unreturned))} passengers per ${period} cannot get home on this timetable. Mitigation: tick "no unreturned passengers" and re-run Best fit before this goes anywhere.`)
      if (!backtest) risks.push('The model has not been calibrated against actual revenue. Mitigation: run the calibration tab and re-issue this paper with the measured error band.')

      const nextSteps = [
        'Confirm the Grosmont transfer time and platform working with the station and operations managers.',
        'Confirm Network Rail path availability and whether the pattern proposed is acceptable to them.',
        'Confirm fleet and crew availability for the diagram count in Appendix A.',
        'Trial the pattern on a small number of days and measure the actual interchange behaviour.',
        'Re-run this report with the trial data before committing a season.',
      ]

      const seasonExec = await buildSeasonExec()
      // ★ NO free-form prose reaches the board paper (Chris, 2026-09-02: "I
      // can't trust the app to generate free-form conclusions"). The paper
      // prints computed figures only; the planner's on-screen notes stay on
      // screen. The mechanical "what's changed" bullets below are derived
      // numbers, printed in Report B only.
      const ss = seasonExec.seasonSummary
      const pctCarried = ss ? Math.round((ss.carriedProp / Math.max(1, ss.carriedCur)) * 100) : 0
      const autoChanges = ss
        ? [
            `${num0.format(ss.turnsCur - ss.turnsProp)} fewer hauled workings a season (${num0.format(ss.turnsCur)} down to ${num0.format(ss.turnsProp)}).`,
            `Running cost down ${gbp(ss.costCur - ss.costProp)} across ${ss.days} running days.`,
            `${pctCarried}% of today's passengers still carried.`,
            `Up to ${ss.locosProp} locomotives a day, against ${ss.locosCur} today.`,
            `Fares and steam haulage unchanged; no diesel substitution.`,
          ].join('\n')
        : ''
      // Accounts-anchored cost model for the board paper (Management P&L basis) —
      // the SAME buildSim the on-screen tables use, so paper and screen agree.
      const costModelPnl = studioCostsData ? (() => {
        const { sim, hasModel } = buildSim(studioCostsData, costConfig, costLegacySeed, costActivity.basis, costActivity.model, rollingInput)
        const groupDefs: { title: string; kind: 'expense' | 'labour' | 'override' }[] = [
          { title: 'Direct & indirect expenses (Management P&L)', kind: 'expense' },
          { title: 'Departmental labour', kind: 'labour' },
          { title: 'Overrides & Additions', kind: 'override' },
        ]
        return {
          basis: costBasis, hasModel, flexCap: sim.flexCap,
          basisTotal: sim.basisTotal, modelTotal: sim.modelTotal,
          groups: groupDefs.map((g) => ({
            title: g.title,
            rows: sim.lines.filter((r) => r.line.kind === g.kind).map((r) => ({
              label: r.line.label,
              behaviour: r.line.behaviour === 'fixed' ? 'fixed' : `var/${r.line.driver}`,
              rate: r.rateLabel,
              basis: r.basisTotal, model: r.modelTotal, delta: r.delta, included: r.line.included,
            })),
          })),
        }
      })() : null

      // The example month's cost LINE BY LINE on the P&L, per running day, today
      // against the proposal — the season table's row ÷ its running days. Replaces
      // the rate-card "running cost by category" table in the board paper (Chris,
      // 2026-09-04: two cost models in one report).
      const exampleMonthPnl = (() => {
        if (!pnlCost || !pnlSim) return null
        const usable = (r: YearRow) => !!r.cur && !!r.mod && (r.runningDays ?? 0) > 0 && r.mod.services > 0
        const row = yearRows.find((r) => r.monthNo === 8 && usable(r)) ?? yearRows.find(usable)
        if (!row) return null
        const days = row.runningDays ?? 0
        const act = (s: YearSideSnap) => ({ services: s.services, carriedPerDay: s.carriedPerDay, miles: s.milesPerDay ?? s.services * 24 })
        const mName = MONTHS[row.monthNo - 1] ?? ''
        const yearOf = Number(data.season.slice(0, 4)) + (row.monthNo === 1 ? 1 : 0)
        return {
          label: `${mName} ${yearOf}`, runningDays: days,
          today: pnlDayLines(pnlSim.sim, costActivity.basis, 'cur', row.monthNo, days, act(row.cur!), mName),
          proposed: pnlDayLines(pnlSim.sim, costActivity.basis, 'mod', row.monthNo, days, act(row.mod!), mName),
          todayActivity: act(row.cur!), proposedActivity: act(row.mod!),
        }
      })()

      const d: ExecReportData = {
        variant: reportVariant,
        costModelPnl,
        exampleMonthPnl,
        net, season: data.season, monthLabel, dayClassLabel: dayLabel,
        period: period as 'day' | 'week',
        runningDays,
        periodsPerSeason: period === 'week' ? 30 : Math.max(1, runningDays) * 8,
        preparedBy: upn ?? 'Reports Platform',
        dateIso: new Date().toISOString().slice(0, 10),
        dataAsAt: new Date().toISOString().slice(0, 10),
        scenarioId, scenarioName: scenarioName || '(unsaved)',
        current, proposed,
        demandProfile: demandChart.map((r) => ({ time: r.time, north: r.Northbound, south: r.Southbound })),
        monthly, sensitivity: sens, assumptions,
        // The season sections — poster, cuts, uplift summary, month P&L and
        // per-pattern diagrams (Chris, 2026-08-21: the executive business plan).
        ...seasonExec,
        // The season-by-month table (both sides) for the money pages, and the
        // month-by-day-of-week view for Appendix G (Chris, 2026-08-20). With no
        // programme yet, the ACTIVE month still makes a one-row table — the
        // section must never print empty.
        yearRows: yearRows.length > 0 ? yearRows : ((liveCurSnap || liveModSnap) ? [{
          key: 'live', monthNo, monthLabel: MONTHS[monthNo - 1] ?? monthLabel, dayClass: dayLabel,
          statusLabel: 'Live design, not yet saved', designed: servicesNow > 0,
          runningDays: runDaysForSlice(monthNo, currentDc),
          cur: liveCurSnap, mod: servicesNow > 0 ? liveModSnap : null, live: true,
        }] : []),
        ...((() => {
          try {
            const [yy, mm] = monthKey.split('-').map(Number)
            const neutral = { Sat: { rev: 1, pax: 1 }, Sun: { rev: 1, pax: 1 }, Wkdy: { rev: 1, pax: 1 } }
            const sales = scheduleSalesFor(monthKey)
            const common = {
              data: sales?.data ?? data, y: sales?.year ?? yy, m: mm, dayClass: dayClass === 'WkdyH' || dayClass === 'WkdyT' ? 'Wkdy' : dayClass,
              deps, dayOverrides: isWeek ? Object.fromEntries(Object.entries(dayOverrides).flatMap(([cls, dd]) => (CLASS_DOWS[cls] ?? []).map((dow) => [dow, dd]))) : {},
              baselineTimetable: wttObserved?.deps, sets: setsRoster, objective, maxUnreturned,
              ep: params, fareMult: fm, rates, dieselLoco, net, fleet, mults: neutral,
            }
            const mod = buildDayModels({ ...common, useScenario: true })
            const cur = buildDayModels({ ...common, useScenario: false })
            const DOWS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
            const side = (x: DayModel | null) => x ? { services: x.services, steamN: x.steamN, dieselN: x.dieselN, pax: Math.round(x.pax), revenue: x.revenue, costs: x.costs, profit: x.profit } : null
            return {
              monthView: DOWS.map((dw, i) => ({ dow: dw, cur: side(cur[i + 1]), mod: side(mod[i + 1]) })),
              // The season timetable in thebigplan TTBuilder format — the
              // proposed patterns (Weekday/Saturday/Sunday + any customised
              // day), printed as WTT pages in the exec report (Chris,
              // 2026-08-20).
              seasonWtt: buildTtPatterns(mod),
            }
          } catch { return { monthView: null, seasonWtt: [] } }
        })()),
        rates, fleet, backtest, advisor: null,
        whatsChanged: (whatsChanged || autoChanges)
          .split('\n').map((x) => x.replace(/^[-•]\s*/, '').trim()).filter(Boolean),
        findings, risks, nextSteps,
        censoringNote: censoringSummary && censoringSummary.applied > 0
          ? `Demand was corrected for censoring: ${censoringSummary.lifted} departure slots were lifted where they had been selling out, adding about ${Math.round(censoringSummary.added)} passengers a day. Without this correction the model part-learns "demand equals what we could seat".`
          : null,
      }
      await buildExecReport(d)
    } catch (e) {
      setSessionError(`Couldn't build the report: ${String((e as Error)?.message ?? e)}`)
    } finally {
      setPdfBusy(false)
    }
  }

  // =========================================================================
  //  Render
  // =========================================================================
  if (!enabled) return null

  const overriddenClasses = Object.keys(dayOverrides)
  const fatalCount = conflicts.filter((c) => !c.resolvable).length
  const stayFmt = (m: number) => `${Math.floor(m / 60)}h${Math.round(m % 60) ? ` ${Math.round(m % 60)}m` : ''}`

  // =========================================================================
  //  StudioShell derivations (§5f) — the shell chrome, programme roll-up and
  //  Live Results rail are all fed from the engine memos above; the chrome
  //  never calculates. The no-garbage guards (MASTER_PLAN §6) live here:
  //  designClean is the disqualification rule; sliceQualifies is the "designed"
  //  gate; the roll-up counts only designed slices × real running-days.
  // =========================================================================
  const gbpK = (n: number) => {
    const a = Math.abs(n)
    if (a >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}m`
    if (a >= 1_000) return `£${(n / 1_000).toFixed(1)}k`
    return gbp(n)
  }
  const monthNo = Number(monthKey.split('-')[1]) || 0
  const dataReady = !!(selection && scenario && costs && baseline && cardCtx && evalResult)
  const hl = scenario && cardCtx ? headline() : null
  // D4: an empty timetable is not a design, earns no walk-up uplift, and can
  // never qualify, win Compare or feed the roll-up.
  const servicesNow = evalResult ? evalResult.applied.filter((x) => x.enabled && !x.positioning).length : 0
  const upliftNow = cardCtx ? cardCtx.untimedNet * (farePct / 100) : 0
  const upliftModelled = servicesNow > 0 ? upliftNow : 0
  const periodDaysNow = period === 'week' ? Math.max(1, runningDays) : 1
  const contribPerDayNow = hl ? hl.contribution / periodDaysNow : 0
  const pathOver = paths.some((pp) => pp.over)
  const designClean = !!(evalResult && servicesNow > 0 && fatalCount === 0 && imbalances.length === 0 && (costs?.overCap.length ?? 0) === 0 && !pathOver && (scenario?.unreturned ?? 1) < 0.5)
  const sliceQualifies = costLocked && appliedDays != null && !!baseline && designClean

  // A date-range modelling period narrows the running-days the roll-up counts.
  const rangeActive = periodMode === 'range' && !!rangeFrom && !!rangeTo
  const runDaysForSlice = (m: number, dc: string): number | null => {
    if (!appliedDays) return null
    const inClass = (c: string) => dc === 'Week' ? true : dc === 'Wkdy' ? (c === 'Wkdy' || c === 'WkdyH' || c === 'WkdyT') : c === dc
    let n = 0
    for (const d of appliedDays) {
      if (!d.running || Number(d.date.slice(5, 7)) !== m || !inClass(d.day_class)) continue
      if (rangeActive && (d.date < rangeFrom || d.date > rangeTo)) continue
      n += 1
    }
    return n
  }
  type SliceResultStored = { contributionPerDay: number; carried: number; unreturned: number; revenue: number; services?: number; snap?: { mod: YearSideSnap | null; cur: YearSideSnap | null }; issues?: MonthIssue[] }
  const parseSliceResult = (s: SessionSlice): SliceResultStored | null => {
    try {
      const r = s.result_json ? JSON.parse(s.result_json) as SliceResultStored : null
      // D4 backstop: a stored result for a timetable with no services (possible
      // in pre-guard saves) contributes nothing anywhere.
      return r && r.services === 0 ? null : r
    } catch { return null }
  }
  // A built month is a built month: 'in_progress' is only the legacy name for
  // one where the Brief bound (before 2026-09-04) — the point itself is listed
  // from the slice's issues, never implied by a status word.
  const SLICE_LABEL: Record<SliceStatus, string> = { not_started: 'Not started', draft: 'Draft', in_progress: 'Built', designed: 'Built' }
  const currentDc = isWeek ? 'Week' : dayClass
  const programmeRows: ProgrammeSliceRow[] = slices.map((s) => {
    const res = parseSliceResult(s)
    const rd = runDaysForSlice(s.month_no, s.day_class)
    const monthly = res && rd != null ? res.contributionPerDay * rd : null
    return {
      id: String(s.id),
      title: `${MONTHS[s.month_no - 1] ?? '?'} · ${CLASS_LABELS[s.day_class] ?? s.day_class}`,
      statusLabel: SLICE_LABEL[s.status] ?? s.status,
      status: s.status,
      contrib: monthly != null ? `${monthly >= 0 ? '+' : ''}${gbpK(monthly)}` : null,
      active: s.month_no === monthNo && s.day_class === currentDc && s.mode === mode,
    }
  })
  // Year comparison rows (Compare stage + exec PDF): every programme slice,
  // both sides. The ACTIVE slice shows live state; others their saved snapshot.
  const liveModSnap: YearSideSnap | null = hl && costs ? {
    services: servicesNow, steamN: costs.steamN, dieselN: costs.dieselN + costs.railcars,
    carriedPerDay: hl.carried / periodDaysNow, revenuePerDay: hl.revenue / periodDaysNow, costsPerDay: hl.costs / periodDaysNow,
    milesPerDay: costs.steamMiles + costs.dieselMiles,
    turnedAwayPerDay: (scenario?.lostSales ?? 0) / periodDaysNow,
    ...engineSnap(costs),
  } : null
  const liveCurSnap: YearSideSnap | null = baseline ? {
    services: baseline.services, steamN: baseline.steamN, dieselN: baseline.dieselN,
    carriedPerDay: baseline.carried / periodDaysNow,
    revenuePerDay: (baseline.revenue + (baseline.services > 0 ? upliftNow : 0)) / periodDaysNow,
    costsPerDay: baseline.costs / periodDaysNow,
    milesPerDay: baseline.miles / periodDaysNow,
    turnedAwayPerDay: baseline.lost / periodDaysNow,
    ...(Object.keys(baseline.engineMiles).length > 0 ? { engineMilesPerDay: baseline.engineMiles, engineDaysPerDay: baseline.engineDays } : {}),
  } : null
  // EACH SEASON MONTH'S OWN covering timetable (Chris, 2026-08-20: "each month
  // needs to be modelled against its running timetable"). thebigplan serves
  // the timetable whose effective window covers the requested date — March and
  // November live in Autumn/Winter, the summer months in Spring/Summer — so
  // one baseline per month, keyed by monthKey. The ACTIVE month's baselineWtt
  // is only a fallback when a month's own fetch fails (scoring April against
  // the winter WTT is exactly the garbage this exists to prevent). Fetched
  // when the season view can be seen (Compare/Results) and on demand by the
  // season design run.
  const [seasonWtt, setSeasonWtt] = useState<Record<string, BaselineWtt>>({})
  const seasonWttFetching = useRef<Set<string>>(new Set())
  useEffect(() => {
    // Cost stage included: the monthly-costs panel's day counts come from the
    // real calendar too.
    if (!enabled || !data || (stage !== 'build' && stage !== 'results' && stage !== 'cost')) return
    // No cancellation flag and seasonWtt NOT in the deps: each landing fetch
    // must commit independently (a flag keyed to the effect's lifetime dropped
    // 8 of the 9 months' results the moment the first one landed). An
    // UNAVAILABLE answer retries with backoff and is never cached as final —
    // a thebigplan cold-pool blip once froze all nine months on the drifted
    // running set's day counts (2026-08-21).
    const fetchMonth = (mk: string, attempt: number) => {
      loadBaseline(undefined, probeDateFor(mk), 'Week')
        .then((b) => {
          if (!b.available && attempt < 3) { setTimeout(() => fetchMonth(mk, attempt + 1), 4000 * attempt); return }
          setSeasonWtt((prev) => ({ ...prev, [mk]: b }))
          seasonWttFetching.current.delete(mk)
        })
        .catch(() => {
          if (attempt < 3) { setTimeout(() => fetchMonth(mk, attempt + 1), 4000 * attempt); return }
          seasonWttFetching.current.delete(mk)
        })
    }
    setSeasonWtt((have) => {
      for (const mo of monthOptions) {
        const mk = mo.value
        if (have[mk]?.available || seasonWttFetching.current.has(mk)) continue
        seasonWttFetching.current.add(mk)
        fetchMonth(mk, 1)
      }
      return have
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rawData, stage, monthOptions])
  /** The month's own baseline, fetching (with retries) if the effect hasn't
   *  yet — the season design run can start before Compare was ever opened,
   *  and must never design against a cold-pool blip. */
  const ensureMonthWtt = async (mk: string): Promise<BaselineWtt | null> => {
    const have = seasonWtt[mk]
    if (have?.available) return have
    let last: BaselineWtt | null = null
    for (let attempt = 1; attempt <= 3; attempt++) {
      last = await loadBaseline(undefined, probeDateFor(mk), 'Week')
      if (last.available) break
      await new Promise((r) => setTimeout(r, 3000 * attempt))
    }
    if (last) setSeasonWtt((prev) => ({ ...prev, [mk]: last! }))
    return last?.available ? last : null
  }

  /**
   * ONE day-type's demand for a month, LIKE-FOR-LIKE (Chris): a month that has
   * already run uses this season's actual sales; a FUTURE month whose bookings
   * are still open (thinner than the same month last year ran) uses LAST
   * SEASON's sales for that month — labelled, never silent. Weight 1: the
   * caller is scoring a single real day of this type, not a blended week.
   * (Declared BEFORE seasonCurrent: the memo executes during render, and a
   * later `const` would be in its temporal dead zone — that exact bug made
   * every month's current side silently null, 2026-08-20.)
   */
  const dayClassDemand = (yy: number, mm: number, dayClass: string): { cls: ClassDemand; un: number; basis: string } | null => {
    if (!data) return null
    // LAW 6 in ONE place: sourceTierFor picks the newest usable season for this
    // month (honouring the planner's per-month choice) — the SAME helper the
    // live screen reads through, so the build and the screen can never disagree.
    const tier = sourceTierFor(yy, mm)
    if (!tier) return null
    const { rows: rowsSrc, untimed: untimedSrc, slots: slotSrc, srcYr, basis } = tier
    // Fall back from a fine class (WkdyH/WkdyT) to plain Wkdy when the fine
    // rows don't exist for this month.
    const tryClasses = dayClass.startsWith('Wkdy') && dayClass !== 'Wkdy' ? [dayClass, 'Wkdy'] : [dayClass]
    for (const c of tryClasses) {
      const rows = rowsSrc.filter((r) => r.day_class === c)
      if (rows.length === 0) continue
      const u = untimedSrc.find((x) => x.yr === srcYr && x.month_no === mm && x.day_class === c)
      const cd = timedRunningDays(rows)
      return {
        cls: buildClassDemand(net0, rows, slotSrc, c, 1, srcYr, mm, censoring, cd).demand,
        un: u && u.running_days > 0 ? u.net / u.running_days : 0,
        // The basis is spelled out by chooseSalesBasis — which year, and which
        // years were passed over and why. Never a bare "last season".
        basis,
      }
    }
    return null
  }

  /** The formation a given season month runs — Chris's rule: explicit choice
   *  wins, else peak sets in the school holidays (Jul/Aug), off-peak otherwise.
   *  Every day must be scored with ITS OWN capacity (law 6), never the active
   *  month's. */
  /**
   * One row per month of the season being designed: what the automatic rule
   * would pick, what the planner has chosen, and the reasoning in words.
   *
   * Declared here, above every reader (the assumptions register and the Brief's
   * JSX). In this component a helper a memo calls MUST be declared first — a
   * later `const` throws inside the memo and silently nulls everything.
   */
  const monthSourceRows = useMemo(() => {
    if (!data) return []
    const nowKey = new Date().toISOString().slice(0, 7)
    return monthOptions.map((mo) => {
      const [yy, mm] = mo.value.split('-').map(Number)
      const candidates: SeasonCandidate[] = [
        { season: data.season, yr: yy, pax: data.demand.filter((r) => r.yr === yy && r.month_no === mm).reduce((a, r) => a + r.pax, 0) },
        { season: data.prev_season ?? 'last season', yr: yy - 1, pax: data.prev_demand.filter((r) => r.month_no === mm).reduce((a, r) => a + r.pax, 0) },
        { season: data.prev2_season ?? 'the season before', yr: yy - 2, pax: (data.prev2_demand ?? []).filter((r) => r.month_no === mm).reduce((a, r) => a + r.pax, 0) },
      ]
      const auto = automaticSource({ monthKey: mo.value, nowKey, candidates, monthSources: monthSources.by })
      const chosen = monthSources.by[mo.value] ?? ''
      const live = chooseSalesBasis({
        monthKey: mo.value, nowKey, candidates,
        monthSources: monthSources.by, disruptedMonths: [], monthNames: MONTHS,
      })
      return {
        key: mo.value,
        label: mo.label,
        auto: auto.season,
        chosen,
        note: live.basis,
        // NO run-day count here. `realRunDays` and `monthDayGroups` are both
        // declared BELOW this memo, and a memo executes during render — calling
        // them threw "Cannot access before initialization", the component threw,
        // and the whole page went blank (Chris: "now it just goes blank",
        // 2026-08-25). That is the TDZ law in CLAUDE.md, broken hours after I
        // wrote it down. The count is decoration; the panel does not need it.
        runDays: null,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, monthOptions, monthSources])

    const monthFormationFor = (mm: number): SetFormation => formationChoice ?? (mm === 7 || mm === 8 ? 'peak' : 'offpeak')

  /**
   * DAY-BY-DAY month decomposition (Chris, 2026-08-20: "check each day for the
   * month, establish what timetable it is... then run the best-fit for that
   * day, and bank it").
   *
   * ONE TIMETABLE PER COLOUR (Chris, 2026-08-25). The unit of design is the
   * timetable COLOUR, because that is the unit the railway publishes: Yellow
   * and Red run Monday to Saturday, Sunday has its own colour (Pink, or Blue
   * in high summer). Each colour's running dates are still carried broken down
   * by sales day-type in `spans`, and the VALUATION walks those — so every day
   * is still earning on its own data (law 6). The design blends the week; the
   * money never does.
   *
   * This replaced grouping by (colour × day-type), which designed timetables
   * the railway could not print, fragmented March into four plans and October
   * into five, and — the symptom Chris saw — banked May's headline timetable
   * from its largest group, the six term weekdays, which have almost no Whitby
   * bookings. The month then read as a Goathland-only railway.
   *
   * TWO SOURCES, ONE JOB EACH (Chris, 2026-08-24):
   *   - WHICH DATES RUN comes from the APPLIED RUNNING SET whenever one covers
   *     this month. That set is the planner's curated truth — the railway ran
   *     Saturday to Wednesday from April to mid-July 2026 while thebigplan's
   *     calendar still carried the old Thursday/Friday pattern, and editing it
   *     there is blocked anyway (the calendar view only surfaces the
   *     highest-status timetable per date). A month the set does not cover
   *     falls back to the calendar rather than silently reporting no running
   *     days — a wrong number is worse than an honest fallback.
   *   - WHICH TIMETABLE COLOUR a date runs still comes from thebigplan.
   * A date the set runs but the calendar has no colour for is counted in
   * runDays and handled by the existing unscorable path: skipped loudly, its
   * money extrapolated from the scored days and footnoted.
   *
   * Note a fresh session auto-applies thebigplan's own calendar as the default
   * set (W3 below), so this changes nothing until the planner applies a set of
   * their own.
   */
  const monthDayGroups = (mk: string): { runDays: number; groups: SeasonDayGroup[]; source: 'set' | 'calendar' } | null => {
    const mw = seasonWtt[mk]
    if (!mw?.available || !mw.days?.length) return null
    return groupRunningDates({
      monthKey: mk,
      calendarDays: mw.days,
      appliedDays: appliedDays ?? null,
      classesInGrain,
    })
  }
  /**
   * THE SEASON BUILD'S ENVIRONMENT — the railway, the planner's barriers and
   * the money, gathered in one place and handed to studio/seasonBuild.ts.
   *
   * The build itself is PURE and lives in that module so it can be run
   * headlessly against real data (.studiotest/seasonbuild.test.ts) instead of
   * only by a human clicking a button. This component's job is fetch, await,
   * save, render — nothing else.
   */
  const buildEnv = (over?: MonthFixChange): BuildEnv => ({
    mode, net0, params,
    basePr: { toleranceMin: 75, recapture: 0.7, fareMult: 1, dwellMin: 180, returnMode, opts: params.opts },
    rates, fleet: fleetWith(fleet, over), sets: setsRoster, dieselLoco, objective,
    farePct, minRevPerDep, maxUnreturned: over?.maxUnreturned ?? maxUnreturned, observedPaths, firstTrain, lastTrain,
    classLabels: CLASS_LABELS, monthNames: MONTHS, engineMode, ecsDiesel: over?.ecsDiesel ?? ecsDiesel,
  })

  /** A month's fetched working timetable, in the shape the build module wants. */
  const monthWtt = (mw: BaselineWtt): MonthWtt => ({
    workings: mw.workings ?? [],
    patternCodes: (mw.patterns ?? []).map((p) => p.code),
    timetableName: mw.timetable?.name,
    effectiveFrom: mw.timetable?.effective_from?.slice(0, 10),
    effectiveTo: mw.timetable?.effective_to?.slice(0, 10),
    days: mw.days,
  })

  /** One month's demand lookup — WHICH SEASON's sales stays here (law 6, and
   *  the declared Bridge 42 exception), where `chooseSalesSeason` gates it. */
  const demandLookupFor = (yy: number, mm: number) =>
    (dayClass: string) => dayClassDemand(yy, mm, dayClass)

  /**
   * Score one month's CURRENT side. Delegates to the build module so the two
   * sides of the season table are produced by the SAME arithmetic and the delta
   * can never be an artefact of two code paths (96 section 4).
   */
  const scoreMonthCurrentAgg = (yy: number, mm: number, mk: string): YearSideSnap | null => {
    const info = monthDayGroups(mk)
    const mw = seasonWtt[mk]
    if (!info || !mw?.workings) return null
    return scoreMonthCurrent({
      groups: info.groups,
      wtt: monthWtt(mw),
      formation: monthFormationFor(mm),
      demandFor: demandLookupFor(yy, mm),
      env: buildEnv(),
    })
  }

  // EVERY MONTH of the season appears in the year view, scored DAY BY DAY on
  // its own data — each day's own timetable colour (from thebigplan's
  // calendar), its own day-type demand, its own capacity formation. Never
  // another month's or another day's data (Chris, 2026-08-20: "the stakes are
  // too high"). A month with no designed slice still shows its CURRENT side,
  // computed here, so every month can be validated before it is designed.
  const seasonCurrent = useMemo(() => {
    const out = new Map<number, YearSideSnap | null>()
    // 'cost' included: the Modelling-costs Rate column (£ per driver-unit) needs
    // the basis activity on the Cost stage too (Chris, 2026-09-02) — the WTT
    // fetch effect above already covers that stage.
    if ((stage !== 'build' && stage !== 'results' && stage !== 'cost') || !data) return out
    for (const mo of monthOptions) {
      const [yy, mm] = mo.value.split('-').map(Number)
      try {
        out.set(mm, scoreMonthCurrentAgg(yy, mm, mo.value))
      } catch (e) {
        // Law 8 (no garbage): a month that cannot be scored shows "—", but the
        // failure must be visible somewhere, never swallowed whole.
        console.warn(`[studio] season current side failed for month ${mm}:`, e)
        out.set(mm, null)
      }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, data, seasonWtt, appliedDays, formationChoice, setsRoster, rates, dieselLoco, fleet, returnMode, censoring, grain])

  // Both sides of the season table are priced with TODAY'S cost model. A
  // designed slice's snapshot keeps its demand-side truth (services, carried,
  // revenue — rates don't move those; fares are locked), but its COSTS are
  // recomputed live from the saved departures — otherwise a recalibration
  // moves the current side and leaves the modelled side on design-time rates
  // (Chris, 2026-08-20: "you've reduced the services but not the costs").
  const sliceLiveCost = useMemo(() => {
    const out = new Map<number, number>()
    for (const s of slices) {
      if (!builtSlice(s)) continue
      try {
        const st = JSON.parse(s.state_json) as { deps?: Departure[]; dayPlans?: SeasonDayPlan[] }
        // net0 is fine here: costFromDeps reads segments/crossing points only,
        // which the path budget and Grosmont-connect transfers don't change.
        if (st.dayPlans && st.dayPlans.length > 0) {
          // Day-by-day slice: price each (colour × day-type) plan and weight
          // by its real day count.
          let days = 0, cost = 0
          for (const p of st.dayPlans) {
            if (!p.deps || p.deps.length === 0 || p.dates.length === 0) continue
            days += p.dates.length
            cost += costFromDeps(net0, p.deps, rates, p.dl ?? dieselLoco, fleet).total * p.dates.length
          }
          if (days > 0) out.set(s.id, cost / days)
        } else if (st.deps && st.deps.length > 0) {
          out.set(s.id, costFromDeps(net0, st.deps, rates, dieselLoco, fleet).total)
        }
      } catch { /* snapshot cost stands */ }
    }
    return out
  }, [slices, rates, dieselLoco, fleet])

  // ==========================================================================
  //  THE PROPOSED SEASON, day by day (Chris, 2026-08-21): fold the banked
  //  designs into named patterns, paint every designed day, and serve the
  //  per-day drill-downs (workings, ticket split, cost line items, both sides).
  // ==========================================================================
  /** The colour of the pattern on the Modelling stage (the WTT pattern loaded). */
  const liveColour = (baselineWtt?.selectedPattern ?? 'TT-RED').replace(/^TT-/i, '').toUpperCase()

  /**
   * THE SEASON ENGINE ROSTER (Chris, 2026-09-11; engineRoster.ts) — which engine
   * works which turn on every banked date, decided over the whole season: paid-for
   * contract miles first, spread and in blocks, interleaved; then owned steam; then
   * the cheapest contract steam; then diesel; the planner's pins on top; a date no
   * engine can cover is a FAILURE DAY. Null without a register in use. ONE owner:
   * the season table, the board paper, the workbook, the WSS and the Modelling
   * stage's engine chips all read it.
   */
  const seasonRoster = useMemo(() => {
    if (!registerInUse) return null
    const dates: RosterDate[] = []
    for (const s of slices) {
      let dayPlans: SeasonDayPlan[] = []
      try { dayPlans = (JSON.parse(s.state_json) as { dayPlans?: SeasonDayPlan[] }).dayPlans ?? [] } catch { continue }
      if (dayPlans.length === 0) continue
      const mm = s.month_no
      const allDates = [...new Set(dayPlans.flatMap((p) => p.dates))].sort()
      const fc = fireconDays(firecon, mm, allDates)
      for (const p of dayPlans) {
        const turns = patternTurns(net0, p.colour, p.deps ?? [], p.dl ?? dieselLoco)
        for (const dt of p.dates) dates.push({ date: dt, month: mm, colour: p.colour, turns, firecon: fc.get(dt) ?? null })
      }
    }
    return buildRoster(dates, fleet.locos ?? [], enginePins)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerInUse, slices, fleet, firecon, enginePins, net0, dieselLoco])

  /** The month on screen's running dates, for the roster's engine chips and the picker. */
  const liveMonthDates = useMemo(() => (monthKey ? (monthDayGroups(monthKey)?.groups ?? []).filter((g) => g.colour.toUpperCase() === liveColour).flatMap((g) => g.dates).sort() : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthKey, seasonWtt, appliedDays, liveColour])
  /** The engine chip per working from the SEASON ROSTER for the month on screen —
   *  the engine that works the turn most days, "+n" when others share it. Falls
   *  back to the pattern-level codes when the roster has no dates for this month. */
  const rosterLocoBy = useMemo(() => {
    if (!seasonRoster || liveMonthDates.length === 0) return null
    const enabled = activeDeps.filter((d) => d.enabled && Number.isFinite(d.minutes))
    const byId = new Map(enabled.map((d) => [d.id, d]))
    const diagrams = estimateDiagrams(net, enabled)
    const turns = patternTurns(net, liveColour, enabled, dieselLoco)
    const labels = rosterLabelsFor(seasonRoster, liveMonthDates, turns, fleet.locos ?? [])
    if (Object.keys(labels).length === 0) return null
    const out: Record<string, { code: string; traction: Traction; label: string; group: string; detail?: string; pinned?: boolean }> = {}
    for (const g of diagrams) {
      const first = byId.get(g.legIds[0])
      if (!first) continue
      const lab = labels[turnKeyOf(net, liveColour, first)]
      if (!lab) continue
      for (const id of g.legIds) out[id] = { code: lab.code, traction: lab.traction, label: lab.label, group: g.group, detail: lab.detail, pinned: lab.pinned }
    }
    return out
  }, [seasonRoster, liveMonthDates, activeDeps, net, liveColour, dieselLoco, fleet])

  const seasonPlan: SeasonPlanData = useMemo(() => {
    const empty: SeasonPlanData = { months: [], patterns: [] }
    if (stage !== 'build' && stage !== 'results') return empty
    const patterns = new Map<string, SeasonPlanPattern>()
    const months: SeasonPlanMonth[] = []
    for (const mo of monthOptions) {
      const mm = Number(mo.value.split('-')[1])
      const s = slices.find((x) => x.month_no === mm)
      if (!s) continue
      let dayPlans: SeasonDayPlan[] = []
      try { dayPlans = (JSON.parse(s.state_json) as { dayPlans?: SeasonDayPlan[] }).dayPlans ?? [] } catch { /* no plans */ }
      if (dayPlans.length === 0) continue
      const info = monthDayGroups(mo.value)
      const byDate = new Map<string, SeasonPlanDay>()
      const fcPlan = fireconDays(firecon, mm, dayPlans.flatMap((pl) => pl.dates))
      dayPlans.forEach((p, planIdx) => {
        const gk = `${mo.value}|${p.colour.toUpperCase()}|${p.dayClass}|${planIdx}`
        const enabled = p.deps.filter((d) => d.enabled && Number.isFinite(d.minutes))
        // An EMPTY design is not an answer (law 8) — those days stay unpainted
        // with a "no working pays" note rather than becoming a phantom plan.
        if (enabled.filter((d) => !d.positioning).length === 0) {
          for (const date of p.dates) {
            byDate.set(date, {
              date, dayNum: Number(date.slice(8, 10)),
              dow: ((new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7) + 1,
              patternId: null, services: null, note: 'no working pays — not designed',
              groupKey: gk,
              // A Firecon date the design runs nothing on is still a Firecon date.
              ...(fcPlan.get(date) ? { firecon: fcPlan.get(date) } : {}),
            })
          }
          return
        }
        const sig = enabled.slice().sort((a, b) => a.minutes - b.minutes || a.o - b.o)
          .map((d) => `${d.minutes}|${d.o}|${d.d}|${d.positioning ? 1 : 0}`).join(';')
        let pat = patterns.get(sig)
        if (!pat) {
          const idx = patterns.size
          pat = {
            id: `plan-${idx}`, name: `Plan ${String.fromCharCode(65 + (idx % 26))}${idx >= 26 ? Math.floor(idx / 26) : ''}`,
            colour: PLAN_COLOURS[idx % PLAN_COLOURS.length],
            workings: enabled.filter((d) => !d.positioning).length,
            daysUsed: 0, monthsUsed: [],
            sampleKey: gk, sampleDate: p.dates[0],
          }
          patterns.set(sig, pat)
        }
        pat.daysUsed += p.dates.length
        const mLabel = MONTHS[mm - 1] ?? '?'
        if (!pat.monthsUsed.includes(mLabel)) pat.monthsUsed.push(mLabel)
        for (const date of p.dates) {
          byDate.set(date, {
            date, dayNum: Number(date.slice(8, 10)),
            dow: ((new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7) + 1,
            patternId: pat.id, services: pat.workings,
            groupKey: gk,
            ...(fcPlan.get(date) ? { firecon: fcPlan.get(date) } : {}),
            ...(seasonRoster?.byDate.get(date)?.failure ? { failure: seasonRoster.byDate.get(date)!.uncovered } : {}),
          })
        }
      })
      // Running days the design left alone (galas/diners) still appear, marked.
      for (const g of info?.groups ?? []) {
        if (!unscorableColour(g.colour)) continue
        for (const date of g.dates) {
          if (byDate.has(date)) continue
          byDate.set(date, {
            date, dayNum: Number(date.slice(8, 10)),
            dow: ((new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7) + 1,
            patternId: null, services: null, note: g.colour || 'Special',
          })
        }
      }
      // Month grid, Monday-first, every calendar day (blank = not running).
      const [yy2, mm2] = mo.value.split('-').map(Number)
      const daysInMonth = new Date(Date.UTC(yy2, mm2, 0)).getUTCDate()
      const firstDow = ((new Date(Date.UTC(yy2, mm2 - 1, 1)).getUTCDay() + 6) % 7)
      const cells: (SeasonPlanDay | null)[] = Array.from({ length: firstDow }, () => null)
      for (let d = 1; d <= daysInMonth; d++) {
        const date = `${mo.value}-${String(d).padStart(2, '0')}`
        cells.push(byDate.get(date) ?? { date, dayNum: d, dow: ((new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7) + 1, patternId: null, services: null })
      }
      while (cells.length % 7 !== 0) cells.push(null)
      const weeks: (SeasonPlanDay | null)[][] = []
      for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
      months.push({ mk: mo.value, label: `${MONTHS[mm - 1] ?? '?'} ${yy2}`, weeks })
    }
    return { months, patterns: [...patterns.values()] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, slices, seasonWtt, appliedDays, grain, firecon, seasonRoster])

  /** One day-group's full drill-down, computed on demand: the banked design
   *  and the current timetable for that colour, both run through the engine
   *  against that day-type's demand and priced with TODAY's cost model. */
  const seasonPlanDetail = (groupKey: string, date?: string): SeasonDayDetail | null => {
    try {
      const [mk, colourKey, dayClass, idxS] = groupKey.split('|')
      const [yy, mm] = mk.split('-').map(Number)
      const s = slices.find((x) => x.month_no === mm)
      if (!s) return null
      const dayPlans = (JSON.parse(s.state_json) as { dayPlans?: SeasonDayPlan[] }).dayPlans ?? []
      const byIdx = idxS != null ? dayPlans[Number(idxS)] : undefined
      const p = byIdx && byIdx.colour.toUpperCase() === colourKey && byIdx.dayClass === dayClass
        ? byIdx
        : dayPlans.find((x) => x.colour.toUpperCase() === colourKey && x.dayClass === dayClass)
      if (!p) return null
      const gd0 = planDemand(p, demandLookupFor(yy, mm), CLASS_LABELS)
      const gd = gd0 ? applyOneWayToday(gd0, p.oneWayToday) : null
      if (!gd) return null
      // The drill-down is opened on a DATE, so it reports THAT date's own
      // day-type (law 6) — not the blended week the design was built for.
      const clicked = date ? p.spans?.find((sp) => sp.dates.includes(date))?.dayClass : undefined
      const part = (clicked && gd.parts.find((x) => x.dayClass === clicked)) || gd.parts[0]
      const md = { cls: part.cls, un: part.un, basis: gd.design.basis }
      const shownClass = part.dayClass === 'Week' ? gd.mix : (CLASS_LABELS[part.dayClass] ?? part.dayClass)
      const monthFormation = monthFormationFor(mm)
      const stations = net0.stations.map((st) => st.name)
      // A side's cost is the P&L day cost — the same pricing as the season table,
      // the grid and the board paper — and its cost lines are the P&L lines for
      // that day (Chris, 2026-09-04). The rate card only when no P&L is loaded.
      const mkSide = (label: string, deps: Departure[], pr: EngineParams, fm: number, dl?: number, sideKind: 'mod' | 'cur' = 'mod'): PlanSideDetail => {
        const side = makeSide(label, net0, deps, [md.cls], pr, md.un, fm, monthFormation, dl)
        const svcN = side.deps.filter((x) => x.enabled && !x.positioning).length
        const miles = side.costs.steamMiles + side.costs.dieselMiles
        const dayCost = pnlCost ? pnlDayCost(mm, svcN, side.scenario.carried, sideKind, miles) : side.periodCosts
        return {
          services: svcN,
          steamN: side.costs.steamN, dieselN: side.costs.dieselN + side.costs.railcars,
          sets: side.setsInTraffic,
          carried: side.scenario.carried, revenue: side.periodRevenue, costs: dayCost,
          profit: side.periodRevenue - dayCost,
          workings: depsToPlanRows(side.deps, stations),
          costLines: pnlCost && pnlSim
            ? pnlDayLines(pnlSim.sim, costActivity.basis, sideKind, mm, monthRunDaysOf(mm), { services: svcN, carriedPerDay: side.scenario.carried, miles }, MONTHS[mm - 1] ?? '')
            : costLines(side.costs, rates),
        }
      }
      const basePr: EngineParams = { toleranceMin: 75, recapture: 0.7, fareMult: 1, dwellMin: 180, returnMode, opts: params.opts }
      const opt = mkSide(`plan-${groupKey}`, p.deps, params, farePct / 100, p.dl)
      const mw = seasonWtt[mk]
      const curRows = mw?.workings?.filter((x) => x.pattern_code === `TT-${colourKey}`) ?? []
      const cur = curRows.length > 0 ? mkSide(`cur-${groupKey}`, wttToDeps(curRows, monthFormation).deps, basePr, 1, undefined, 'cur') : null
      return {
        basis: md.basis, dayClass: shownClass, colour: p.colour, dates: p.dates,
        mix: gd.mix, opt, cur, editDeps: p.deps.map((x) => ({ ...x })), stations,
      }
    } catch (e) {
      console.warn('[studio] season plan drill-down failed:', e)
      return null
    }
  }

  /** The month's observed ticket mix (same source as the Weekly Service
   *  Schedule), scaled to a day's modelled passengers and revenue. */
  const seasonMixCache = useRef<Map<string, Record<string, { item: string; qty: number; net: number }[]>>>(new Map())
  const seasonSplitFor = async (groupKey: string, pax: number, revenue: number, date?: string): Promise<TicketSplitRow[] | null> => {
    try {
      const [mk, , dayClass] = groupKey.split('|')
      const [yy, mm] = mk.split('-').map(Number)
      let byClass = seasonMixCache.current.get(mk)
      if (!byClass) {
        const all = await Promise.all([1, 2, 3, 4, 5, 6, 7].map((d) =>
          fetch(`/api/research/weekday-revenue/detail?people=1&day_no=${d}&y=${yy}&m=${mm}`)
            .then((r) => (r.ok ? (r.json() as Promise<{ cur?: { items?: { item: string; qty: number; net: number }[] } }>) : Promise.resolve({})))
            .then((b) => ({ dayNo: d, rows: (b as { cur?: { items?: { item: string; qty: number; net: number }[] } }).cur?.items ?? [] }))
        ))
        const agg: Record<string, Map<string, { item: string; qty: number; net: number }>> = { Sat: new Map(), Sun: new Map(), Wkdy: new Map() }
        for (const { dayNo, rows } of all) {
          const cls = dayNo === 6 ? 'Sat' : dayNo === 7 ? 'Sun' : 'Wkdy'
          for (const r of rows) {
            const cur = agg[cls].get(r.item) ?? { item: r.item, qty: 0, net: 0 }
            cur.qty += r.qty; cur.net += r.net
            agg[cls].set(r.item, cur)
          }
        }
        byClass = Object.fromEntries(Object.entries(agg).map(([k, v]) => [k, [...v.values()]]))
        seasonMixCache.current.set(mk, byClass)
      }
      // A colour group spans the week, so the mix follows the DATE clicked;
      // `dayClass` on the key is only meaningful for pre-2026-08-25 plans.
      const dow = date ? new Date(`${date}T12:00:00Z`).getUTCDay() : -1
      const coarse = date
        ? (dow === 6 ? 'Sat' : dow === 0 ? 'Sun' : 'Wkdy')
        : (dayClass === 'Sat' ? 'Sat' : dayClass === 'Sun' ? 'Sun' : 'Wkdy')
      const rows = byClass[coarse] ?? []
      const tot = rows.reduce((t, r) => ({ qty: t.qty + r.qty, net: t.net + r.net }), { qty: 0, net: 0 })
      if (tot.net <= 0) return null
      return rows
        .filter((r) => r.net > 0)
        .sort((a, b) => b.net - a.net)
        .slice(0, 25)
        .map((r) => ({ item: r.item, tickets: tot.qty > 0 ? (r.qty / tot.qty) * pax : 0, net: (r.net / tot.net) * revenue, share: r.net / tot.net }))
    } catch { return null }
  }

  /**
   * The SEASON sections of the executive report (Chris, 2026-08-21: the
   * executive business plan): the public poster, the cuts analysis, the
   * season uplift summary, the month-by-month P&L and the per-pattern
   * running diagrams — all built fresh from the banked day plans, both sides
   * priced with today's cost model. ~1–2s of engine work at export time.
   */
  const buildSeasonExec = async (): Promise<{ poster: SeasonPoster | null; cuts: CutGroupRow[]; seasonSummary: SeasonSummary | null; planWtt: PlanWttPattern[]; monthPnl: MonthPnl[]; monthCosts: ExecMonthCatCosts[]; exampleDay: ExecExampleDay | null; wssMonths: ExecWssMonth[]; appendixMonths: ExecAppendixMonth[]; xlsxMonths: StudioXlsxMonth[]; fleetMonths: ExecFleetMonth[]; fleetEngines: ExecFleetEngine[] }> => {
    const emptyOut = { poster: null, cuts: [] as CutGroupRow[], seasonSummary: null, planWtt: [] as PlanWttPattern[], monthPnl: [] as MonthPnl[], monthCosts: [] as ExecMonthCatCosts[], exampleDay: null as ExecExampleDay | null, wssMonths: [] as ExecWssMonth[], appendixMonths: [] as ExecAppendixMonth[], xlsxMonths: [] as StudioXlsxMonth[], fleetMonths: [] as ExecFleetMonth[], fleetEngines: [] as ExecFleetEngine[] }
    try {
      if (!data || seasonPlan.patterns.length === 0) return emptyOut
      const basePr: EngineParams = { toleranceMin: 75, recapture: 0.7, fareMult: 1, dwellMin: 180, returnMode, opts: params.opts }
      const N_DEST: Record<number, string> = { 4: 'T', 3: 'G', 2: 'A', 1: 'L' }
      const S_DEST: Record<number, string> = { 0: 'P', 1: 'L', 2: 'A', 3: 'G' }
      const cuts: CutGroupRow[] = []
      const monthPnl: MonthPnl[] = []
      const monthCosts: ExecMonthCatCosts[] = []
      // Per-date P&L for the Weekly Service Schedule grids (optimised + today),
      // keyed month -> date-number. EACH DATE ITS OWN FIGURES (Chris,
      // 2026-09-02): the modelled side is the date's own day-type valuation
      // (like-for-like projection); the unoptimised side is the date's REAL
      // sales from the basis season. Never a class average stamped on every day.
      type WssDay = { colour: string; dayClass: string; opt: ExecDaySide | null; cur: ExecDaySide | null; pullman: { services: number; covers: number; net: number } | null }
      const wssByMonth = new Map<string, Map<number, WssDay>>()
      // The fleet month by month for the board paper (fleetAvailabilitySection),
      // and each engine's season on the roster.
      const fleetMonths: ExecFleetMonth[] = []
      const fleetEngines: ExecFleetEngine[] = seasonRoster
        ? Object.values(seasonRoster.engines).sort((a, b) => b.miles - a.miles).map((e) => ({ label: e.label, traction: e.traction, minimum: e.minimum, miles: e.miles, days: e.days, blocks: e.blocks }))
        : []
      // Per-month provenance for the unoptimised side + the Pullman overlay.
      type PullmanLeg = { colour: string; time: string; from: string; to: string; arr: string }
      const wssMeta = new Map<string, { basis: string; basisSeason: string; unmatched: number; noService: number; pullmanServices: number; pullmanDescription: string; pullmanConflicts: number; pullmanLegs: PullmanLeg[]; pullmanDates: number; maxLocos: { opt: number; cur: number }; firecon: number }>()
      // Per-month timetable listings for the workbook (opt + unoptimised + Pullman).
      type XlsxTtRow = { time: string; from: string; to: string; arr: string; capacity: number; set: string; loco: string; kind: 'Service' | 'ECS' | 'Pullman' }
      type XlsxPlan = { label: string; colour: string; dates: string[]; opt: XlsxTtRow[]; cur: XlsxTtRow[] }
      const xlsxPlansByMonth = new Map<string, XlsxPlan[]>()
      // The representative running day per month (peak — most Saturdays, then
      // most dates), scored both ways, for the per-month appendix.
      type AppCap = { label: string; dayClassLabel: string; current: ExecSide; proposed: ExecSide; demandProfile: { time: string; north: number; south: number }[]; score: number }
      const appCap = new Map<string, AppCap>()
      // The worked example day for Report A's Costs & Modelling section: the
      // best August running day (most Saturdays, then most dates). Captured in
      // the loop; the ticket split is fetched after (async).
      type AugCap = { yy: number; mm: number; colour: string; date: string; score: number
        services: ExecExampleDayService[]; costLines: { label: string; amount: number; detail?: string }[]
        costBasis: ExecExampleDay['costBasis']; fixedShare: number; runningDayCharge: number; ecsMoves?: ExecExampleDay['ecsMoves']
        optimised: ExecExampleDay['optimised']; unoptimised: ExecExampleDay['unoptimised']; pax: number; revenue: number }
      let bestAug: AugCap | null = null
      // Pattern registry keyed by the SAME signature as the on-screen panel,
      // built in the same iteration order, so names and colours agree.
      const patterns = new Map<string, { meta: PosterPattern; wtt: PlanWttPattern }>()
      const sm = { days: 0, turnsCur: 0, turnsProp: 0, costCur: 0, costProp: 0, revenueCur: 0, revenueProp: 0, carriedCur: 0, carriedProp: 0, locosCur: 0, locosProp: 0, monthsCovered: 0 }
      for (const mo of monthOptions) {
        const mm = Number(mo.value.split('-')[1])
        const yy = Number(mo.value.split('-')[0])
        const slice = slices.find((x) => x.month_no === mm)
        if (!slice) continue
        let dayPlans: SeasonDayPlan[] = []
        try { dayPlans = (JSON.parse(slice.state_json) as { dayPlans?: SeasonDayPlan[] }).dayPlans ?? [] } catch { /* none */ }
        if (dayPlans.length === 0) continue
        // AWAIT the month's own baseline — an export clicked before the season
        // fetches land must never print a zero current side (law 8).
        const mw = (seasonWtt[mo.value]?.available ? seasonWtt[mo.value] : await ensureMonthWtt(mo.value)) ?? undefined
        const monthFormation = monthFormationFor(mm)
        const mLabel = `${MONTHS[mm - 1] ?? '?'} ${yy}`
        const pnlRows: MonthPnlRow[] = []
        let monthDays = 0
        const catAcc = { loco: 0, carriages: 0, crew: 0, infra: 0, overhead: 0 }
        const curCatAcc = { loco: 0, carriages: 0, crew: 0, infra: 0, overhead: 0 }
        const wssDays = wssByMonth.get(mo.value) ?? new Map<number, WssDay>()
        wssByMonth.set(mo.value, wssDays)
        // The month's REAL per-day sales, like-for-like from the basis season
        // (one mapping for the whole month, so the nth Saturday pairs month-wide).
        const allDates = [...new Set(dayPlans.flatMap((pl) => pl.dates))].sort()
        const monthActuals = dateActualsFor(mo.value, allDates)
        const monthPullman = pullmanForMonth(mm, allDates)
        // Each designed pattern's OWN Pullman path (its colour's set-P legs),
        // filtered by the running toggles, counted as SERVICES — one service =
        // out and back (Chris, 2026-09-03: "Pullman runs from Grosmont to
        // Pickering and back — 1 service"), checked against that pattern for
        // crossings (law 8: named, not assumed away) and listed in its
        // timetable tab on both sides. Never another colour's path.
        const pullmanLegsFor = (colour: string): Departure[] =>
          pullmanWttDeps((mw?.workings ?? []).filter((w) => w.pattern_code === `TT-${colour.toUpperCase()}`)).filter((pd) => pullmanModelled(mm, pd.time))
        let monthPullmanConflicts = 0
        let monthPullmanServices = 0
        let monthPullmanDescription = ''
        const monthPullmanLegs: PullmanLeg[] = []
        let monthNoService = 0
        let monthFirecon = 0
        // The fleet this month (fleetAvailability.ts): engines out with days and
        // reasons, the worst-day availability, and the design's swaps and uncovered turns.
        const monthOut = new Map<string, { days: number; reasons: string[] }>()
        let monthSwapped = 0, monthNoEngine = 0, monthSteamMin = Infinity, monthDieselMin = Infinity
        let monthFailure = 0
        const xlsxPlans: XlsxPlan[] = xlsxPlansByMonth.get(mo.value) ?? []
        xlsxPlansByMonth.set(mo.value, xlsxPlans)
        // Max locos in PASSENGER service on a single day (Chris, 2026-09-02):
        // distinct engine diagrams that haul at least one passenger working —
        // diesel ECS positioning turns are NOT locos in passenger service.
        let monthMaxLocosOpt = 0
        let monthMaxLocosCur = 0
        for (const p of dayPlans) {
          const gd0 = planDemand(p, demandLookupFor(yy, mm), CLASS_LABELS)
          const gd = gd0 ? applyOneWayToday(gd0, p.oneWayToday) : null
          if (!gd) continue
          const n = p.dates.length
          // ---- proposed side, fully applied (sets + locos assigned) ----
          const env = buildEnv()
          const optR = scoreColourPlan({ net: net0, deps: p.deps, gd, pr: params, fareMult: farePct / 100, dl0: p.dl ?? dieselLoco, formation: monthFormation, env })
          const dl = optR.dl
          const optApplied = optR.applied
          const optCost = optR.cost
          const optLegs = optApplied.filter((x) => x.enabled && Number.isFinite(x.minutes))
          const optSvc = optLegs.filter((x) => !x.positioning)
          const optRevD = optR.revenueD
          const optPlan = { byDep: optR.byDep ?? {}, setsInTraffic: optR.setsInTraffic }
          const optLoco = legLocoCodes(net0, optApplied, dl, fleet.locos)
          // ---- current side: the real workings of this colour ----
          const curRows = mw?.workings?.filter((x) => x.pattern_code === `TT-${p.colour.toUpperCase()}`) ?? []
          let curSvcN = 0, curCostD = 0, curRevD = 0, curCarriedD = 0, curLocoTxt = '—', curSets = 0, curMilesD = 0
          let curSteamN = 0, curDieselN = 0
          let curCatDay = { loco: 0, carriages: 0, crew: 0, infra: 0, overhead: 0 }
          let curSvcDeps: Departure[] = []
          let curApplied: Departure[] = []
          let curByDep: Record<string, string> = {}
          if (curRows.length > 0) {
            const curR = scoreColourPlan({ net: net0, deps: wttToDeps(curRows, monthFormation).deps, gd, pr: basePr, fareMult: 1, dl0: dieselLoco, formation: monthFormation, env })
            const curCost = curR.cost
            curCatDay = catPerDay(curCost)
            curApplied = curR.applied
            curByDep = curR.byDep ?? {}
            curSvcDeps = curR.applied.filter((x) => x.enabled && !x.positioning)
            curSvcN = curSvcDeps.length
            curSteamN = curCost.steamN
            curDieselN = curCost.dieselN + curCost.railcars
            curCostD = curCost.total
            curMilesD = curCost.steamMiles + curCost.dieselMiles
            curRevD = curR.revenueD
            curCarriedD = curR.carriedD
            curLocoTxt = `${curCost.steamN}S${curCost.dieselN + curCost.railcars > 0 ? `+${curCost.dieselN + curCost.railcars}D` : ''}`
            curSets = curR.setsInTraffic
            sm.locosCur = Math.max(sm.locosCur, curCost.steamN + curCost.dieselN + curCost.railcars)
          }
          // ---- season roll-up ---- revenue/carried/cost accumulate PER DATE
          // in the loop below (day-for-day valuation), same arithmetic as the
          // WSS cells, so the board paper prints exactly the screen's numbers.
          sm.days += n
          monthDays += n
          // turns / revenue / carried / cost season sums accumulate PER DATE in
          // the loop below (a no-service date adds no turns), so the tiles agree
          // with the day-for-day WSS cells exactly.
          sm.locosProp = Math.max(sm.locosProp, optCost.steamN + optCost.dieselN + optCost.railcars)
          // ---- Report A: per-month cost by category (modelled), and the
          //      worked August day (Chris, 2026-08-27) ----
          const cp = catPerDay(optCost)
          catAcc.loco += cp.loco * n; catAcc.carriages += cp.carriages * n; catAcc.crew += cp.crew * n
          catAcc.infra += cp.infra * n; catAcc.overhead += cp.overhead * n
          curCatAcc.loco += curCatDay.loco * n; curCatAcc.carriages += curCatDay.carriages * n; curCatAcc.crew += curCatDay.crew * n
          curCatAcc.infra += curCatDay.infra * n; curCatAcc.overhead += curCatDay.overhead * n
          // ---- per-date cells for the Weekly Service Schedule grid ----
          // Modelled side: the DATE's own day-type figures (optR.byClass) — the
          // like-for-like projection, so a Saturday never shows a blended
          // weekday average. Unoptimised side: the date's REAL sales from its
          // like-for-like basis day (monthActuals); the services/sets stay the
          // pattern's real timetable, which genuinely repeats. A design date
          // with no like-for-like basis day falls back to its day-type average
          // and is counted in wssMeta.unmatched (law 8).
          const clsOfDate = (dt: string): string => p.spans?.find((sp) => sp.dates.includes(dt))?.dayClass ?? p.dayClass
          // ---- per-date cells for the Weekly Service Schedule grid ----
          // ONE owner for every date's figures (perDateValue.ts::daySide) — the
          // same call the season table's memo (perDateMonth) makes, so the
          // board paper, the workbook's month sheets and its Summary agree to
          // the pound. Modelled side: the designed timetable valued on this
          // date's like-for-like ACTUAL demand; unoptimised side: that day's
          // ACTUAL sales. A like day that ran no standard services: 0 services,
          // fixed cost only, both sides. No like day: the side's labelled average.
          const sliceRes = parseSliceResult(slice)
          const optShape = planShapeFor(optApplied, dl, 'mod', mm, { sets: optR.setsInTraffic })
          // Firecon days this month — the same dates the season table marks.
          const fcMonth = fireconDays(firecon, mm, dayPlans.flatMap((pl) => pl.dates))
          const optShapeFc = fcMonth.size > 0 ? planShapeFor(optApplied, dl, 'mod', mm, { sets: optR.setsInTraffic, firecon: true }) : optShape
          const curShape = curRows.length > 0 ? planShapeFor(curApplied, dieselLoco, 'cur', mm, { sets: curSets }) : null
          const modAvg = { pax: sliceRes?.snap?.mod?.carriedPerDay ?? optR.carriedD, revenue: sliceRes?.snap?.mod?.revenuePerDay ?? optRevD }
          const curSnapAvg = seasonCurrent.get(mm) ?? sliceRes?.snap?.cur ?? null
          const curAvg = { pax: curSnapAvg?.carriedPerDay ?? curCarriedD, revenue: curSnapAvg?.revenuePerDay ?? curRevD }
          const toExecSide = (f: DaySideFigures, a: DateActual | null): ExecDaySide => ({
            services: f.services, sets: f.sets, steamN: f.steamN, dieselN: f.dieselN,
            pax: Math.round(f.pax), revenue: Math.round(f.revenue), costs: Math.round(f.costs), profit: Math.round(f.revenue) - Math.round(f.costs),
            actual: f.kind === 'actual', noService: f.kind === 'no-service', basisDate: a?.basisDate,
            ...(f.firecon ? { firecon: f.firecon } : {}),
            ...(f.fleet ? { fleet: f.fleet } : {}),
            ...(f.failure ? { failure: true } : {}),
          })
          for (const dt of p.dates) {
            const dnum = Number(dt.slice(8, 10))
            if (!Number.isFinite(dnum)) continue
            const cls = clsOfDate(dt)
            const act = monthActuals?.byDate[dt] ?? null
            if (isNoStandardServiceDay(act)) monthNoService += 1
            const fd = fcMonth.get(dt)
            if (fd) monthFirecon += 1
            const rd = seasonRoster?.byDate.get(dt) ?? null
            if (rd?.failure) monthFailure += 1
            const shape = seasonRoster ? planShapeFor(optApplied, dl, 'mod', mm, { sets: optR.setsInTraffic, firecon: !!fd, date: dt, colour: p.colour, roster: rd }) : (fd ? optShapeFc : optShape)
            if (fleet.locos?.length) {
              const av = availableOn(fleet.locos, dt)
              monthSteamMin = Math.min(monthSteamMin, av.filter((l) => l.traction === 'steam').length)
              monthDieselMin = Math.min(monthDieselMin, av.filter((l) => l.traction === 'diesel').length)
            }
            const optFig = daySide({
              act, plan: shape.plan, valuedKind: 'valued',
              valued: shape.plan.services === 0
                ? { pax: 0, revenue: 0, ...(rd?.failure && act && !isNoStandardServiceDay(act) ? { turnedAway: valueDepsOnDay(net0, [], act, params, farePct / 100, p.oneWayToday?.[cls], fireconScale(fd)).turnedAway } : {}) }
                : (act && !isNoStandardServiceDay(act) ? valueDepsOnDay(net0, optApplied, act, params, farePct / 100, p.oneWayToday?.[cls], fireconScale(fd)) : null),
              average: modAvg, cost: shape.cost, firecon: fd,
            })
            if (optFig.fleet) {
              monthSwapped += optFig.fleet.swapped; monthNoEngine += optFig.fleet.noEngine
              for (const o of optFig.fleet.out) { const cur = monthOut.get(o.label) ?? { days: 0, reasons: [] as string[] }; cur.days += 1; if (!cur.reasons.includes(o.reason)) cur.reasons.push(o.reason); monthOut.set(o.label, cur) }
            }
            const curFig = curShape
              ? daySide({ act, plan: curShape.plan, valuedKind: 'actual', valued: act ? { pax: act.pax, revenue: act.net } : null, average: curAvg, cost: curShape.cost })
              : null
            sm.revenueProp += optFig.revenue
            sm.carriedProp += optFig.pax
            sm.costProp += optFig.costs
            sm.turnsProp += optFig.services
            if (curFig) {
              sm.revenueCur += curFig.revenue
              sm.carriedCur += curFig.pax
              sm.costCur += curFig.costs
              sm.turnsCur += curFig.services
            }
            // Pullman dining on this date = its like-for-like day in the most
            // recent season with Pullman data, toggle-filtered (pullmanForMonth).
            wssDays.set(dnum, { colour: p.colour, dayClass: cls, opt: toExecSide(optFig, act), cur: curFig ? toExecSide(curFig, act) : null, pullman: monthPullman.byDate[dt] ?? null })
          }
          // ---- the Pullman must traffic THROUGH the designed pattern ----
          const pullmanWtt = pullmanLegsFor(p.colour)
          const pullmanSvc = pullmanServices(pullmanWtt)
          if (pullmanWtt.length > 0) {
            const baseConf = crossingConflicts(net0, optApplied).filter((x) => !x.resolvable).length
            const withP = crossingConflicts(net0, [...optApplied, ...pullmanWtt]).filter((x) => !x.resolvable).length
            monthPullmanConflicts += Math.max(0, withP - baseConf)
            monthPullmanServices += pullmanSvc.services
            if (!monthPullmanDescription) monthPullmanDescription = pullmanSvc.description
            // Where the Pullman runs through THIS pattern — for the board paper's
            // working-turns page (Chris, 2026-09-04).
            for (const leg of pullmanWtt) {
              monthPullmanLegs.push({
                colour: p.colour, time: fmtMin(leg.minutes),
                from: net0.stations[leg.o]?.name ?? String(leg.o), to: net0.stations[leg.d]?.name ?? String(leg.d),
                arr: fmtMin(leg.arrMin ?? leg.minutes + journeyMin(net0, leg.o, leg.d)),
              })
            }
          }
          // ---- workbook timetable rows: optimised, unoptimised, Pullman ----
          // With the season roster, the designed side's engine is the roster's for this
          // pattern over its dates ("92134 +1" when shared) — the same as the Modelling chips.
          const rosterLoco: Record<string, string> = {}
          if (seasonRoster) {
            const enabledOpt = optApplied.filter((x) => x.enabled && Number.isFinite(x.minutes))
            const byIdOpt = new Map(enabledOpt.map((x) => [x.id, x]))
            const labels = rosterLabelsFor(seasonRoster, [...p.dates].sort(), patternTurns(net0, p.colour, enabledOpt, dl), fleet.locos ?? [])
            for (const g of estimateDiagrams(net0, enabledOpt)) {
              const first = byIdOpt.get(g.legIds[0])
              const lab = first ? labels[turnKeyOf(net0, p.colour, first)] : undefined
              if (lab) for (const id of g.legIds) rosterLoco[id] = lab.label
            }
          }
          const ttRow = (d: Departure, byDep: Record<string, string>, locoOf: ReturnType<typeof legLocoCodes>, fromRoster = false): XlsxTtRow => ({
            time: fmtMin(d.minutes),
            from: net0.stations[d.o]?.name ?? String(d.o),
            to: net0.stations[d.d]?.name ?? String(d.d),
            arr: fmtMin(d.arrMin ?? d.minutes + journeyMin(net0, d.o, d.d)),
            capacity: depSeats(d),
            set: d.pullman ? 'Pullman' : (byDep[d.id] ?? d.setCode ?? d.setPin ?? ''),
            loco: (fromRoster ? rosterLoco[d.id] : undefined) ?? locoOf[d.id]?.code ?? d.locoLabel ?? (d.tractionPin ?? ''),
            kind: d.pullman ? 'Pullman' : d.positioning ? 'ECS' : 'Service',
          })
          const curLocoCodes = curApplied.length > 0 ? legLocoCodes(net0, curApplied, dieselLoco, fleet.locos) : {}
          monthMaxLocosOpt = Math.max(monthMaxLocosOpt, new Set(optSvc.map((l) => optLoco[l.id]?.code).filter(Boolean)).size)
          monthMaxLocosCur = Math.max(monthMaxLocosCur, new Set(curSvcDeps.map((l) => curLocoCodes[l.id]?.code).filter(Boolean)).size)
          xlsxPlans.push({
            label: `${p.colour} timetable${gd.mix ? ` — ${gd.mix}` : ''}`,
            colour: p.colour,
            dates: [...p.dates].sort(),
            opt: [...optLegs, ...pullmanWtt].sort((a, b) => a.minutes - b.minutes).map((d) => ttRow(d, optPlan.byDep, optLoco, true)),
            cur: [...curApplied.filter((x) => x.enabled && Number.isFinite(x.minutes)), ...pullmanWtt].sort((a, b) => a.minutes - b.minutes).map((d) => ttRow(d, curByDep, curLocoCodes)),
          })
          // Appendix: keep this month's peak running day, scored both ways.
          if (optSvc.length > 0) {
            const sats = p.dates.filter((dt) => new Date(`${dt}T12:00:00Z`).getUTCDay() === 6).length
            const score = sats * 100 + n
            const prev = appCap.get(mo.value)
            if (!prev || score > prev.score) {
              const classes = gd.parts.map((pt) => pt.cls)
              const un = gd.parts.reduce((t, pt) => t + (pt.un ?? 0), 0)
              const proposedSide = makeSide('Proposed', net0, p.deps, classes, params, un, farePct / 100, monthFormation, p.dl ?? dieselLoco)
              const currentSide = makeSide('Today', net0, curRows.length > 0 ? wttToDeps(curRows, monthFormation).deps : [], classes, basePr, un, 1, monthFormation)
              const prof = new Map<string, { time: string; north: number; south: number }>()
              for (const cls of classes) {
                for (const s of cls.north) { const e = prof.get(s.time) ?? { time: s.time, north: 0, south: 0 }; e.north += s.paxPerDay; prof.set(s.time, e) }
                for (const s of cls.south) { const e = prof.get(s.time) ?? { time: s.time, north: 0, south: 0 }; e.south += s.paxPerDay; prof.set(s.time, e) }
              }
              appCap.set(mo.value, {
                label: mLabel, dayClassLabel: CLASS_LABELS[p.dayClass] ?? p.dayClass,
                current: currentSide, proposed: proposedSide,
                demandProfile: [...prof.values()].sort((a, b) => a.time.localeCompare(b.time)), score,
              })
            }
          }
          if (mm === 8 && optSvc.length > 0) {
            const sats = p.dates.filter((dt) => new Date(`${dt}T12:00:00Z`).getUTCDay() === 6)
            const score = sats.length * 100 + n
            if (!bestAug || score > bestAug.score) {
              // ONE cost model in the board paper (Chris, 2026-09-04: the worked day
              // printed £1,136/working — the rate card — beside the P&L table). Each
              // working is priced at its P&L VARIABLE cost: per working turn, plus per
              // train-mile for its own miles, plus per passenger for the people who
              // board it. The running-day charge and the month's fixed share are the
              // DAY's (law 1). The rate card survives only when no P&L basis is loaded.
              const runDaysM = monthRunDaysOf(mm)
              const optMilesD = optCost.steamMiles + optCost.dieselMiles
              // Who boards each working, scaled so the services sum to the day's
              // carried passengers (the blended design day vs the day-weighted
              // valuation differ by a few people) — the per-service list must add
              // up to the day it explains.
              const svcPax = new Map<string, number>()
              if (pnlCost) {
                const run = runClasses(net0, [gd.design.cls], optApplied, params)
                const svcIds = new Set(optSvc.map((l) => l.id))
                const tot = run.perDep.filter((pd) => svcIds.has(pd.id)).reduce((t, pd) => t + pd.outAlloc, 0)
                const scale = tot > 0 ? optR.carriedD / tot : 1
                for (const pd of run.perDep) svcPax.set(pd.id, pd.outAlloc * scale)
              }
              // Empty-stock moves carry nobody but burn train-miles: the day's
              // variable cost includes them, so the per-service list shows them.
              const ecsLegs = optApplied.filter((x) => x.enabled && x.positioning && Number.isFinite(x.minutes))
              const ecsMilesD = ecsLegs.reduce((t, x) => t + journeyMiles(net0, x.o, x.d), 0)
              const lp = rates.locoPerTurn
              const locoWorkingCost = (id: string): number => {
                const tr = optLoco[id]?.traction
                if (lp) return (tr === 'diesel' || tr === 'railcar') ? lp.diesel.value : lp.steam.value
                const locoDay = optCost.steamRun + optCost.dieselRun + optCost.footplate + optCost.prep + optCost.overhaulLoco
                return optSvc.length > 0 ? locoDay / optSvc.length : 0
              }
              const serviceCost = (l: Departure): number => pnlCost
                ? pnlServiceVariable(pnlCost, { miles: journeyMiles(net0, l.o, l.d), pax: svcPax.get(l.id) ?? 0 })
                : locoWorkingCost(l.id)
              const dayCostOpt = pnlCost ? pnlDayCost(mm, optSvc.length, optR.carriedD, 'mod', optMilesD) : optCost.total
              const dayCostCur = pnlCost ? pnlDayCost(mm, curSvcN, curCarriedD, 'cur', curMilesD) : curCostD
              const services: ExecExampleDayService[] = optSvc.slice().sort((a, b) => a.minutes - b.minutes).map((l) => ({
                time: l.time,
                route: `${net0.stations[l.o]?.tt ?? '?'}–${net0.stations[l.d]?.tt ?? '?'}`,
                // Designed legs carry no measured arrMin — fall back to the
                // modelled journey time, never the departure time (accuracy
                // fix, Chris 2026-09-02: the PDF's example-day "arr" column
                // was printing the departure again).
                arr: fmtMin(l.arrMin ?? l.minutes + journeyMin(net0, l.o, l.d)),
                set: optPlan.byDep[l.id] ?? '',
                capacity: l.capacity ?? (l.coaches === 0 ? 48 : Math.max(0, l.coaches) * 64),
                loco: optLoco[l.id]?.code ?? '',
                cost: Math.round(serviceCost(l)),
              }))
              bestAug = {
                yy, mm, colour: p.colour, date: sats[0] ?? p.dates[0], score, services,
                costLines: pnlCost && pnlSim
                  ? pnlDayLines(pnlSim.sim, costActivity.basis, 'mod', mm, runDaysM, { services: optSvc.length, carriedPerDay: optR.carriedD, miles: optMilesD }, MONTHS[mm - 1] ?? 'August')
                  : costLines(optCost, rates).map((cl) => ({ label: cl.label, amount: cl.amount, detail: cl.detail })),
                costBasis: pnlCost ? 'pnl' : 'rate-card',
                fixedShare: pnlCost ? pnlDayFixed('mod', mm) : optCost.dayFixed,
                runningDayCharge: pnlCost ? pnlCost.rate.runningDays : 0,
                ecsMoves: pnlCost && ecsLegs.length > 0 ? { count: ecsLegs.length, miles: ecsMilesD, cost: pnlCost.rate.trainMiles * ecsMilesD } : undefined,
                optimised: { services: optSvc.length, steamN: optCost.steamN, dieselN: optCost.dieselN + optCost.railcars, pax: Math.round(optR.carriedD), revenue: Math.round(optRevD), costs: Math.round(dayCostOpt), profit: Math.round(optRevD - dayCostOpt) },
                unoptimised: { services: curSvcN, steamN: curSteamN, dieselN: curDieselN, pax: Math.round(curCarriedD), revenue: Math.round(curRevD), costs: Math.round(dayCostCur), profit: Math.round(curRevD - dayCostCur) },
                pax: Math.round(optR.carriedD), revenue: Math.round(optRevD),
              }
            }
          }
          // ---- pattern registry (same signature + order as the panel) ----
          let planName = '—'
          if (optSvc.length > 0) {
            const sig = optLegs.slice().sort((a, b) => a.minutes - b.minutes || a.o - b.o)
              .map((x) => `${x.minutes}|${x.o}|${x.d}|${x.positioning ? 1 : 0}`).join(';')
            let entry = patterns.get(sig)
            if (!entry) {
              const idx = patterns.size
              const colour = PLAN_COLOURS[idx % PLAN_COLOURS.length]
              const name = `Plan ${String.fromCharCode(65 + (idx % 26))}${idx >= 26 ? Math.floor(idx / 26) : ''}`
              const flagOf = (id: string): string => {
                const tr = optLoco[id]?.traction
                return tr === 'railcar' ? 'Auto' : tr === 'diesel' ? 'D' : 'X'
              }
              const nLegs = optLegs.filter((l) => l.d > l.o).sort((a, b) => a.minutes - b.minutes)
              const sLegs = optLegs.filter((l) => l.d < l.o).sort((a, b) => a.minutes - b.minutes)
              const headcodes = new Map<string, string>()
              nLegs.forEach((l, i) => headcodes.set(l.id, `1${N_DEST[l.d] ?? 'T'}${String(i * 2 + 1).padStart(2, '0')}`))
              sLegs.forEach((l, i) => headcodes.set(l.id, `1${S_DEST[l.d] ?? 'P'}${String(i * 2 + 2).padStart(2, '0')}`))
              const forms = new Map<string, string>()
              const bySet = new Map<string, Departure[]>()
              for (const l of optLegs) {
                const code = optPlan.byDep[l.id]
                if (!code) continue
                bySet.set(code, [...(bySet.get(code) ?? []), l])
              }
              for (const chainLegs of bySet.values()) {
                chainLegs.sort((a, b) => a.minutes - b.minutes)
                for (let i = 0; i + 1 < chainLegs.length; i++) forms.set(chainLegs[i].id, headcodes.get(chainLegs[i + 1].id) ?? '')
              }
              const toWork = (l: Departure) => ({
                headcode: headcodes.get(l.id) ?? '',
                loco: optLoco[l.id]?.code ?? '',
                guard: '',
                set: optPlan.byDep[l.id] ?? '',
                forms: forms.get(l.id) ?? '',
                t: slotTimes(l),
              })
              const toPoster = (l: Departure): PosterWork => ({ t: slotTimes(l), flag: flagOf(l.id) })
              entry = {
                meta: {
                  id: `plan-${idx}`, name, colour,
                  workingsPerDay: optSvc.length, daysUsed: 0, monthsUsed: [],
                  N: nLegs.filter((l) => !l.positioning).map(toPoster),
                  S: sLegs.filter((l) => !l.positioning).map(toPoster),
                },
                wtt: { name, colour, monthsUsed: [], daysUsed: 0, N: nLegs.map(toWork), S: sLegs.map(toWork) },
              }
              patterns.set(sig, entry)
            }
            entry.meta.daysUsed += n
            entry.wtt.daysUsed += n
            const mShort = MONTHS[mm - 1] ?? '?'
            if (!entry.meta.monthsUsed.includes(mShort)) entry.meta.monthsUsed.push(mShort)
            if (!entry.wtt.monthsUsed.includes(mShort)) entry.wtt.monthsUsed.push(mShort)
            planName = entry.meta.name
          }
          // ---- cuts analysis: match current workings against the proposal ----
          const withdrawn: { time: string; route: string }[] = []
          let kept = 0, retimed = 0
          for (const cw of curSvcDeps) {
            const match = optSvc.find((ow) => ow.o === cw.o && ow.d === cw.d && Math.abs(ow.minutes - cw.minutes) <= 20)
            if (!match) withdrawn.push({ time: cw.time, route: `${net0.stations[cw.o]?.name ?? cw.o} to ${net0.stations[cw.d]?.name ?? cw.d}` })
            else if (Math.abs(match.minutes - cw.minutes) > 5) retimed += 1
            else kept += 1
          }
          cuts.push({
            month: mLabel, colour: p.colour, dayClass: p.spans?.length ? gd.mix : p.dayClass, days: n, planName,
            curSvc: curSvcN, propSvc: optSvc.length,
            curLocos: curLocoTxt, propLocos: `${optCost.steamN}S${optCost.dieselN + optCost.railcars > 0 ? `+${optCost.dieselN + optCost.railcars}D` : ''}`,
            curSets, propSets: optPlan.setsInTraffic,
            curCostD, propCostD: optCost.total,
            curCarriedD, propCarriedD: optR.carriedD,
            withdrawn, kept, retimed,
          })
          pnlRows.push({
            colour: p.colour, dayClass: p.spans?.length ? gd.mix : p.dayClass, days: n, planName,
            svcCur: curSvcN, svcProp: optSvc.length,
            revD: optRevD, costD: optCost.total, profitD: optRevD - optCost.total,
            curProfitD: curRevD - curCostD,
          })
        }
        // Running dates this month that NO plan covers — a colour with no
        // timetable pattern (Diner/gala days, a colour thebigplan does not
        // publish). The line still costs money on them: a fixed-share cell on
        // both sides, named, so the month sheet, the grid and the season tiles
        // carry EVERY running day and the Summary's month fixed is met exactly
        // (Chris, 2026-09-04).
        {
          const planned = new Set(dayPlans.flatMap((pl) => pl.dates))
          const unplanned = (monthDayGroups(mo.value)?.groups ?? []).flatMap((g) => g.dates.filter((dt) => !planned.has(dt)).map((dt) => ({ dt, colour: g.colour })))
          for (const { dt, colour } of unplanned) {
            const dnum = Number(dt.slice(8, 10))
            if (!Number.isFinite(dnum) || wssDays.has(dnum)) continue
            const fixedSide = (side: 'mod' | 'cur'): ExecDaySide => {
              const f = pnlCost ? Math.round(pnlDayFixed(side, mm)) : 0
              return { services: 0, sets: 0, steamN: 0, dieselN: 0, pax: 0, revenue: 0, costs: f, profit: -f, noService: true, unscorable: true }
            }
            const o = fixedSide('mod'), cu = fixedSide('cur')
            sm.costProp += o.costs; sm.costCur += cu.costs
            sm.days += 1
            wssDays.set(dnum, { colour, dayClass: '', opt: o, cur: cu, pullman: monthPullman.byDate[dt] ?? null })
          }
        }
        // Provenance for this month's unoptimised WSS side + the Pullman check.
        wssMeta.set(mo.value, {
          basis: monthActuals?.basis ?? 'no sales basis',
          basisSeason: monthActuals?.basisSeason ?? '',
          unmatched: monthActuals ? monthActuals.unmatched.length : allDates.length,
          noService: monthNoService,
          pullmanServices: monthPullmanServices,
          pullmanDescription: monthPullmanDescription,
          pullmanConflicts: monthPullmanConflicts,
          pullmanLegs: monthPullmanLegs,
          pullmanDates: Object.keys(monthPullman.byDate).length,
          maxLocos: { opt: monthMaxLocosOpt, cur: monthMaxLocosCur },
          firecon: monthFirecon,
        })
        if (fleet.locos?.length) {
          fleetMonths.push({
            label: `${MONTHS[mm - 1] ?? '?'} ${yy}`, days: monthDays,
            outDays: [...monthOut.entries()].map(([label, v]) => ({ label, days: v.days, reasons: v.reasons })).sort((a, b) => b.days - a.days),
            swapped: monthSwapped, noEngine: monthNoEngine, failureDays: monthFailure,
            steamMin: Number.isFinite(monthSteamMin) ? monthSteamMin : null, dieselMin: Number.isFinite(monthDieselMin) ? monthDieselMin : null,
          })
        }
        if (pnlRows.length > 0) {
          monthPnl.push({ month: mLabel, days: monthDays, rows: pnlRows })
          sm.monthsCovered += 1
        }
        if (monthDays > 0) {
          monthCosts.push({
            key: mo.value, label: `${(MONTHS[mm - 1] ?? '?').slice(0, 3)} ${String(yy).slice(2)}`, days: monthDays,
            perDay: { loco: catAcc.loco / monthDays, carriages: catAcc.carriages / monthDays, crew: catAcc.crew / monthDays, infra: catAcc.infra / monthDays, overhead: catAcc.overhead / monthDays },
            curPerDay: { loco: curCatAcc.loco / monthDays, carriages: curCatAcc.carriages / monthDays, crew: curCatAcc.crew / monthDays, infra: curCatAcc.infra / monthDays, overhead: curCatAcc.overhead / monthDays },
          })
        }
      }
      // The line costs money in every month (Chris, 2026-09-04): the season
      // tiles carry the fixed cost of the months that run no standard services,
      // on both sides — the same figures the season table shows for them.
      if (pnlCost) {
        for (const mo of monthOptions) {
          const mmN = Number(mo.value.split('-')[1])
          if ((realRunDays(mo.value) ?? 0) !== 0) continue
          sm.costCur += pnlMonthFixedFor('cur', mmN)
          sm.costProp += pnlMonthFixedFor('mod', mmN)
        }
      }
      if (patterns.size === 0) return emptyOut
      // Poster months straight from the on-screen panel (same construction).
      const metaById = new Map([...patterns.values()].map((e) => [e.meta.id, e.meta]))
      const specialsAgg = new Map<string, number>()
      const posterMonths: PosterMonth[] = seasonPlan.months.map((m) => ({
        label: m.label,
        cells: m.weeks.flat().map((cell) => {
          if (!cell) return null
          if (cell.patternId && metaById.has(cell.patternId)) return { day: cell.dayNum, colour: metaById.get(cell.patternId)!.colour }
          if (cell.note && !cell.note.startsWith('no working')) {
            specialsAgg.set(cell.note, (specialsAgg.get(cell.note) ?? 0) + 1)
            return { day: cell.dayNum, colour: null, special: cell.note }
          }
          return { day: cell.dayNum, colour: null }
        }),
      }))
      // No current side at all (thebigplan unreachable) = no summary tiles —
      // "£0 today" is garbage, not a comparison.
      const seasonSummary: SeasonSummary | null = sm.costCur > 0 || sm.carriedCur > 0 ? {
        ...sm,
        profitCur: sm.revenueCur - sm.costCur,
        profitProp: sm.revenueProp - sm.costProp,
        monthsTotal: monthOptions.length,
      } : null
      // The worked August day — its ticket split is fetched now (async).
      let exampleDay: ExecExampleDay | null = null
      if (bestAug) {
        const b = bestAug
        let ticketSplit: ExecExampleDay['ticketSplit'] = []
        try {
          const rows = await seasonSplitFor(`${b.yy}-${String(b.mm).padStart(2, '0')}|${b.colour}|Week`, b.pax, b.revenue, b.date)
          if (rows) ticketSplit = rows.map((r) => ({ item: r.item, tickets: r.tickets, net: r.net, share: r.share }))
        } catch { /* ticket split is a nice-to-have; the costed day stands without it */ }
        const dow = new Date(`${b.date}T12:00:00Z`).getUTCDay()
        const dName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dow]
        const [dy, dm, dd] = b.date.split('-').map(Number)
        exampleDay = {
          label: `${dName} ${dd} ${MONTHS[dm - 1] ?? '?'} ${dy}`,
          monthLabel: MONTHS[b.mm - 1] ?? 'August',
          services: b.services, ticketSplit, costLines: b.costLines,
          costBasis: b.costBasis, fixedShare: b.fixedShare, runningDayCharge: b.runningDayCharge, ecsMoves: b.ecsMoves,
          optimised: b.optimised, unoptimised: b.unoptimised,
        }
      }
      // The Weekly Service Schedule calendar grids, per month, both sides.
      const wssMonths: ExecWssMonth[] = []
      for (const mo of monthOptions) {
        const days = wssByMonth.get(mo.value)
        if (!days || days.size === 0) continue
        const [yy2, mm2] = mo.value.split('-').map(Number)
        const daysInMonth = new Date(yy2, mm2, 0).getDate()
        const lead = (new Date(yy2, mm2 - 1, 1).getDay() + 6) % 7
        const buildWeeks = (which: 'opt' | 'cur'): ExecWssWeek[] => {
          const flat: (ExecWssCell | null)[] = []
          for (let i = 0; i < lead; i++) flat.push(null)
          for (let dnum = 1; dnum <= daysInMonth; dnum++) {
            const wd = days.get(dnum)
            flat.push(wd
              ? { date: dnum, colour: wd.colour, dayClass: wd.dayClass, side: which === 'opt' ? wd.opt : wd.cur, pullman: wd.pullman }
              : { date: dnum, colour: null, dayClass: '', side: null })
          }
          while (flat.length % 7 !== 0) flat.push(null)
          const weeks: ExecWssWeek[] = []
          for (let w = 0; w * 7 < flat.length; w++) {
            const cells = flat.slice(w * 7, w * 7 + 7)
            const sides = cells.filter((c): c is ExecWssCell => !!c && !!c.side).map((c) => c.side!)
            const total = sides.length ? sides.reduce((t, s) => ({
              services: t.services + s.services, steamN: Math.max(t.steamN, s.steamN), dieselN: Math.max(t.dieselN, s.dieselN),
              pax: t.pax + s.pax, revenue: t.revenue + s.revenue, costs: t.costs + s.costs, profit: t.profit + s.profit,
            }), { services: 0, steamN: 0, dieselN: 0, pax: 0, revenue: 0, costs: 0, profit: 0 }) : null
            weeks.push({ label: `Week ${w + 1}`, cells, total })
          }
          return weeks
        }
        const meta = wssMeta.get(mo.value)
        wssMonths.push({
          key: mo.value, label: `${MONTHS[mm2 - 1] ?? '?'} ${yy2}`,
          optimised: buildWeeks('opt'), unoptimised: buildWeeks('cur'),
          basis: meta?.basis, unmatchedDates: meta?.unmatched ?? 0, noServiceDates: meta?.noService ?? 0,
          firecon: meta && meta.firecon > 0 ? { dates: meta.firecon, reductions: firecon.reductions.slice() } : null,
          pullman: meta && meta.pullmanServices > 0 ? { workings: meta.pullmanServices, conflicts: meta.pullmanConflicts, description: meta.pullmanDescription, legs: meta.pullmanLegs, dates: meta.pullmanDates } : null,
        })
      }
      // Flat per-month structures for the workbook: one row per day + the
      // timetable listings (optimised, unoptimised, Pullman), from the SAME
      // cells the PDF prints — the workbook can never disagree with the paper.
      const xlsxMonths = monthOptions
        .filter((mo) => (wssByMonth.get(mo.value)?.size ?? 0) > 0)
        .map((mo) => {
          const [yy3, mm3] = mo.value.split('-').map(Number)
          const days = [...(wssByMonth.get(mo.value) ?? new Map<number, WssDay>()).entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([dnum, wd]) => ({ date: `${mo.value}-${String(dnum).padStart(2, '0')}`, dow: new Date(Date.UTC(yy3, mm3 - 1, dnum)).getUTCDay(), ...wd }))
          const meta = wssMeta.get(mo.value)
          return { key: mo.value, label: `${MONTHS[mm3 - 1] ?? '?'} ${yy3}`, days, plans: xlsxPlansByMonth.get(mo.value) ?? [], basis: meta?.basis ?? '', unmatched: meta?.unmatched ?? 0, noService: meta?.noService ?? 0, pullman: meta && meta.pullmanServices > 0 ? { workings: meta.pullmanServices, conflicts: meta.pullmanConflicts, description: meta.pullmanDescription } : null, maxLocos: meta?.maxLocos ?? { opt: 0, cur: 0 } }
        })
      // Per-month appendix blocks: the representative day + that month's WTT,
      // running diagrams and public poster.
      const appendixMonths: ExecAppendixMonth[] = []
      for (const mo of monthOptions) {
        const cap = appCap.get(mo.value)
        if (!cap) continue
        const mShort = MONTHS[Number(mo.value.split('-')[1]) - 1] ?? '?'
        const monthPatterns = [...patterns.values()].filter((e) => e.meta.monthsUsed.includes(mShort))
        appendixMonths.push({
          key: mo.value, label: cap.label, dayClassLabel: cap.dayClassLabel,
          current: cap.current, proposed: cap.proposed, demandProfile: cap.demandProfile,
          wtt: monthPatterns.map((e) => ({ name: e.wtt.name, N: e.wtt.N, S: e.wtt.S })),
          diagrams: monthPatterns.map((e) => e.wtt),
          posterMonth: posterMonths.find((pm) => pm.label.includes(mShort)) ?? null,
          posterPatterns: monthPatterns.map((e) => e.meta),
        })
      }
      return {
        poster: {
          seasonLabel: data.season,
          months: posterMonths,
          patterns: [...patterns.values()].map((e) => e.meta),
          specials: [...specialsAgg.entries()].map(([label, days]) => ({ label, days })),
        },
        cuts,
        seasonSummary,
        planWtt: [...patterns.values()].map((e) => e.wtt),
        monthPnl,
        monthCosts,
        exampleDay,
        wssMonths,
        appendixMonths,
        xlsxMonths,
        fleetMonths,
        fleetEngines,
      }
    } catch (e) {
      console.warn('[studio] season exec sections failed:', e)
      return emptyOut
    }
  }

  /**
   * Re-score a month's banked day plans AS THEY STAND (no Best fit — the
   * planner's edit IS the timetable) and save the slice.
   *
   * The arithmetic is `rescoreMonth` in studio/seasonBuild.ts — the SAME module
   * that builds the season. Editing a pattern therefore cannot move the money by
   * a different route than building it did, which is the two-code-paths defect
   * this app kept producing (96 section 4).
   */
  const bankMonthPlans = async (mk: string, dayPlans: SeasonDayPlan[]): Promise<boolean> => {
    if (sessionId == null || !data) return false
    const [yy, mm] = mk.split('-').map(Number)
    const mw = await ensureMonthWtt(mk)
    if (!mw) return false
    const info = monthDayGroups(mk)
    const r = rescoreMonth({
      monthKey: mk,
      dayPlans,
      groups: info?.groups ?? [],
      runDays: info?.runDays ?? 0,
      wtt: monthWtt(mw),
      formation: monthFormationFor(mm),
      demandFor: demandLookupFor(yy, mm),
      env: buildEnv(),
    })
    if (!r.mod) return false
    await saveSlice(sessionId, {
      month_no: mm, day_class: 'Week', mode,
      state: { ...captureState(), monthKey: mk, dayClass: 'Week', deps: r.headlineDeps, dayOverrides: {}, dayPlans: r.dayPlans },
      result: {
        contributionPerDay: r.contributionPerDay,
        carried: r.carried,
        unreturned: r.unreturned,
        revenue: r.mod.revenuePerDay,
        services: r.services,
        snap: { mod: r.mod, cur: r.cur },
        issues: r.issues,
      },
      status: 'designed',
    })
    const loaded = await loadSession(sessionId)
    setSlices(loaded.slices)
    setSavedAt('Saved just now')
    return true
  }

  /**
   * Apply an edit from the pattern editor (Chris's spec, 2026-08-21): scope
   * 'day' splits the edited date out of its group with the new timetable;
   * scope 'set' applies it to every day the timetable covers in the month.
   * Then the month is re-scored and re-banked.
   */
  const applySeasonEdit = async (groupKey: string, date: string, scope: 'day' | 'set', deps: Departure[]): Promise<boolean> => {
    try {
      const [mk, colourKey, dayClass, idxS] = groupKey.split('|')
      const mm = Number(mk.split('-')[1])
      const slice = slices.find((x) => x.month_no === mm)
      if (!slice || sessionId == null) return false
      const st = JSON.parse(slice.state_json) as Record<string, unknown> & { dayPlans?: SeasonDayPlan[] }
      const dayPlans = st.dayPlans ?? []
      let idx = idxS != null ? Number(idxS) : -1
      if (!(dayPlans[idx] && dayPlans[idx].colour.toUpperCase() === colourKey && dayPlans[idx].dayClass === dayClass)) {
        idx = dayPlans.findIndex((x) => x.colour.toUpperCase() === colourKey && x.dayClass === dayClass)
      }
      if (idx < 0) return false
      const target = dayPlans[idx]
      if (scope === 'set' || target.dates.length === 1) {
        dayPlans[idx] = { ...target, deps }
      } else {
        // Split: this date gets its own plan; the rest keep the original.
        dayPlans[idx] = { ...target, dates: target.dates.filter((d) => d !== date) }
        dayPlans.push({ colour: target.colour, dayClass: target.dayClass, dates: [date], deps, dl: target.dl })
      }
      return await bankMonthPlans(mk, dayPlans)
    } catch (e) {
      console.warn('[studio] pattern edit failed:', e)
      return false
    }
  }

  // Run days come from thebigplan's calendar (the month's own baseline), NOT
  // the applied running set — the set drifted to 29 March days when the
  // railway really runs 9 (2026-08-20).
  // No applied-set fallback: while a month's calendar is loading the row shows
  // "—" — a wrong day count (29 March days) is worse than a brief blank.
  /** This month's running calendar for the schedule grid: which dates run and
   *  which timetable each works. Same precedence as the season model — the
   *  applied running set decides whether a date runs, thebigplan (or the
   *  planner's override) decides its colour. */
  const monthCalendarForSchedule = useMemo(() => {
    if (!monthKey) return undefined
    const out: Record<string, { running: boolean; colour?: string }> = {}
    const mw = seasonWtt[monthKey] ?? (baselineWtt?.available ? baselineWtt : null)
    for (const d of mw?.days ?? []) {
      if (d.date.startsWith(monthKey)) out[d.date] = { running: d.run, colour: (d.colour ?? '').trim() || undefined }
    }
    for (const d of appliedDays ?? []) {
      if (!d.date.startsWith(monthKey)) continue
      out[d.date] = { running: d.running, colour: d.colour?.trim() || out[d.date]?.colour }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthKey, seasonWtt, baselineWtt, appliedDays])

  // The on-screen WSS month grid's per-date overlay: the basis label, each
  // running date's REAL sales (like-for-like from the basis season) and the
  // Pullman dining on its like day (toggle-filtered). Same owners as the
  // board-paper cells: dateActualsFor + pullmanBySeason.
  const wssOverlay = (() => {
    if (!monthKey || !monthCalendarForSchedule) return null
    const dates = Object.entries(monthCalendarForSchedule)
      .filter(([iso, v]) => v.running && iso.startsWith(monthKey))
      .map(([iso]) => iso)
      .sort()
    if (dates.length === 0) return null
    const act = dateActualsFor(monthKey, dates)
    if (!act) return null
    const mmO = Number(monthKey.split('-')[1])
    // Who is one-way today, per date, from the month's BANKED plans — the grid
    // values each date against the same people the build did.
    const oneWayToday: Record<string, Record<string, number>> = {}
    const banked = slices.find((x) => x.month_no === mmO && x.mode === mode && builtSlice(x))
    if (banked) {
      try {
        const dayPlans = (JSON.parse(banked.state_json) as { dayPlans?: SeasonDayPlan[] }).dayPlans ?? []
        for (const p of dayPlans) {
          for (const sp of (p.spans && p.spans.length > 0 ? p.spans : [{ dayClass: p.dayClass, dates: p.dates }])) {
            const m = p.oneWayToday?.[sp.dayClass]
            if (!m) continue
            for (const dt of sp.dates) oneWayToday[dt] = m
          }
        }
      } catch { /* a slice that will not parse has no plans to read */ }
    }
    return { basis: act.basis, dateActuals: act.byDate, oneWayToday, pullmanByDate: pullmanForMonth(mmO, dates).byDate }
  })()

  const realRunDays = (mk: string): number | null => monthDayGroups(mk)?.runDays ?? null
  const yearRowsBase: YearRow[] = monthOptions.flatMap((mo) => {
    const mm = Number(mo.value.split('-')[1])
    const monthSlices = slices.filter((s) => s.month_no === mm)
      .sort((a, b) => a.day_class.localeCompare(b.day_class))
    if (monthSlices.length === 0) {
      return [{
        key: `m-${mo.value}`,
        monthNo: mm,
        monthLabel: MONTHS[mm - 1] ?? '?',
        dayClass: 'Full week',
        statusLabel: 'Not started',
        designed: false,
        issues: [],
        runningDays: realRunDays(mo.value),
        cur: seasonCurrent.get(mm) ?? null,
        mod: null,
        live: false,
      }]
    }
    return monthSlices.map((s) => {
      const live = s.month_no === monthNo && s.day_class === currentDc && s.mode === mode
      const res = parseSliceResult(s)
      // Modelled side: snapshot for the demand-side truth, costs re-priced
      // with today's rates (sliceLiveCost); profit follows. An in-progress
      // month still SHOWS its banked design (the row is labelled), so the
      // season totals compare the same months on both sides.
      const modSnap = res?.snap?.mod ?? null
      const liveCost = modSnap ? sliceLiveCost.get(s.id) : undefined
      const mod = modSnap ? { ...modSnap, costsPerDay: liveCost ?? modSnap.costsPerDay } : null
      return {
        key: String(s.id),
        monthNo: s.month_no,
        monthLabel: MONTHS[s.month_no - 1] ?? '?',
        dayClass: CLASS_LABELS[s.day_class] ?? s.day_class,
        statusLabel: SLICE_LABEL[s.status] ?? s.status,
        designed: builtSlice(s),
        issues: res?.issues ?? [],
        runningDays: realRunDays(mo.value),
        // The DAY-BY-DAY sides are the month's truth: the live scratchpad
        // (one pattern, one day-type) never overwrites them — it only fills
        // in when nothing banked exists yet. Live current first computes with
        // today's rates; the snapshot is the fallback.
        cur: seasonCurrent.get(mm) ?? res?.snap?.cur ?? (live ? liveCurSnap : null),
        mod: mod ?? (live && servicesNow > 0 ? liveModSnap : null),
        live,
      }
    })
  })
  // Roll-up counts REAL running days per month (thebigplan calendar first).
  const rollup = seasonRollup(slices.map((s) => {
    const res = parseSliceResult(s)
    const mk = monthOptions.find((o) => Number(o.value.split('-')[1]) === s.month_no)?.value
    return {
      status: s.status,
      contributionPerDay: res?.contributionPerDay ?? null,
      runningDays: mk ? realRunDays(mk) : null,
    }
  }))
  const rollupValue = rollup.designedCount === 0 ? '—' : `${rollup.total >= 0 ? '+' : ''}${gbpK(rollup.total)}`
  // Which calendar the running-days came from is never left to guess (law 8):
  // the planner's applied set OUTRANKS thebigplan's calendar (Chris,
  // 2026-08-24), so the note names the source that is actually in play.
  const runDaySource = runningSetName
    ? `running days from your set "${runningSetName}"`
    : "running days from thebigplan's calendar"
  const rollupNote = appliedDays == null
    ? 'Apply a running set so the roll-up uses real running-days.'
    : rollup.partial
      ? `across ${rollup.designedCount} built month${rollup.designedCount === 1 ? '' : 's'}${rollup.excluded ? ` · ${rollup.excluded} need a running set/score` : ''} · ${runDaySource}`
      : `across ${rollup.designedCount} built month${rollup.designedCount === 1 ? '' : 's'} · ${runDaySource}`

  /**
   * EACH MONTH'S OWN modelled £/day by cost category, from its banked plan.
   *
   * Chris, 2026-08-25, on a monthly cost table full of zeros: "should I be
   * suspicious that the loco costs are not being calculated - look at the
   * zero's?" The table was taking the £/day of whichever month was ON SCREEN
   * and multiplying it across all nine columns. With March open — correctly
   * modelling no trains — every month read £0. Same shape as every other defect
   * here: one month's answer standing in for nine (law 6).
   *
   * Priced with TODAY's rates from each month's banked departures, day-weighted
   * across its plans, so a recalibration moves every month at once.
   */
  const monthCatPerDay = useMemo(() => {
    const out = new Map<string, Record<string, number>>()
    for (const s of slices) {
      let plans: SeasonDayPlan[] = []
      let flat: Departure[] = []
      try {
        const st = JSON.parse(s.state_json) as { deps?: Departure[]; dayPlans?: SeasonDayPlan[]; monthKey?: string }
        plans = st.dayPlans ?? []
        flat = st.deps ?? []
      } catch { continue }
      const mk = monthOptions.find((mo) => Number(mo.value.split('-')[1]) === s.month_no)?.value
      if (!mk) continue
      const acc: Record<string, number> = {}
      let days = 0
      const add = (deps: Departure[], dl: number, n: number) => {
        if (deps.length === 0 || n <= 0) return
        const per = catPerDay(costFromDeps(net0, deps, rates, dl, fleet))
        for (const [k, v] of Object.entries(per)) acc[k] = (acc[k] ?? 0) + v * n
        days += n
      }
      if (plans.length > 0) for (const pl of plans) add(pl.deps ?? [], pl.dl ?? dieselLoco, pl.dates?.length ?? 0)
      else add(flat, dieselLoco, 1)
      if (days > 0) out.set(mk, Object.fromEntries(Object.entries(acc).map(([k, v]) => [k, v / days])))
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slices, rates, dieselLoco, fleet, monthOptions])

  // Running days per month of the modelling period — the cost stage's monthly
  // panel and the per-card period totals ride on the applied running set.
  const costMonthDays = (() => {
    // The season's months with their REAL running days (thebigplan calendar);
    // the applied set only stands in while a month's baseline is loading. A
    // date-range modelling period narrows the counted dates.
    const inRange = (date: string) => !rangeActive || (date >= rangeFrom && date <= rangeTo)
    const out: { key: string; label: string; days: number; perDay?: Record<string, number> }[] = []
    for (const mo of monthOptions) {
      const info = monthDayGroups(mo.value)
      let days: number
      if (info) {
        days = rangeActive
          ? info.groups.reduce((t, g) => t + g.dates.filter(inRange).length, 0)
          : info.runDays
      } else if (appliedDays) {
        days = appliedDays.filter((d) => d.running && d.date.slice(0, 7) === mo.value && inRange(d.date)).length
      } else {
        continue
      }
      if (days > 0) out.push({ key: mo.value, label: `${(MONTHS[Number(mo.value.slice(5, 7)) - 1] ?? '?').slice(0, 3)} ${mo.value.slice(2, 4)}`, days, perDay: monthCatPerDay.get(mo.value) })
    }
    return out
  })()

  // ── Accounts-anchored cost model — activity vectors + legacy seed ──
  // The per-month activity (current side = today, model side = designed) re-prices
  // variable P&L lines and sets the ceiling on the fixed flex sliders.
  const costLegacySeed = buildLegacySeed(costMonthDays)
  const costActivity = (() => {
    // REAL per-segment train-miles from the engine (Chris, 2026-09-02: a
    // Grosmont–Whitby turn is 6 miles, not a full-line 24). Only a snapshot
    // saved before miles were recorded falls back to the 24-mi/turn proxy —
    // and the fallback is COUNTED so the screen can say so (law 8).
    const MILES_PER_WORKING = 24 // legacy proxy for pre-miles snapshots only
    const basisA: Activity[] = Array.from({ length: 12 }, () => ({ ...ZERO_ACTIVITY }))
    const modelA: Activity[] = Array.from({ length: 12 }, () => ({ ...ZERO_ACTIVITY }))
    type Agg = { days: number; mS: number; mC: number; mM: number; mW: number; cS: number; cC: number; cM: number; cW: number; mE: Record<string, number>; mEd: Record<string, number>; cE: Record<string, number>; cEd: Record<string, number> }
    const byMonth = new Map<number, Agg>()
    // Named engines (Chris, 2026-09-11): each engine's timetabled miles and
    // worked days per running day, day-weighted like every other figure here.
    const addEng = (into: Record<string, number>, from: Record<string, number> | undefined, w: number) => {
      for (const [k, v] of Object.entries(from ?? {})) into[k] = (into[k] ?? 0) + v * w
    }
    const modelEng: EngineActivity = { miles: {}, days: {} }
    const basisEng: EngineActivity = { miles: {}, days: {} }
    const putEng = (eng: EngineActivity, idx: number, sums: Record<string, number>, daysSum: Record<string, number>, w: number, md: number) => {
      for (const [k, v] of Object.entries(sums)) { eng.miles[k] = eng.miles[k] ?? (Array(12).fill(0) as number[]); eng.miles[k][idx] = (w > 0 ? v / w : 0) * md }
      for (const [k, v] of Object.entries(daysSum)) { eng.days[k] = eng.days[k] ?? (Array(12).fill(0) as number[]); eng.days[k][idx] = (w > 0 ? v / w : 0) * md }
    }
    const hasEng = (eng: EngineActivity) => Object.keys(eng.miles).length > 0 || Object.keys(eng.days).length > 0
    let proxyRows = 0
    const milesOf = (s: YearSideSnap): number => {
      if (s.milesPerDay != null) return s.milesPerDay
      proxyRows += 1
      return s.services * MILES_PER_WORKING
    }
    for (const r of yearRowsBase) {
      const days = r.runningDays ?? 0
      if (days <= 0) continue
      const a = byMonth.get(r.monthNo) ?? { days, mS: 0, mC: 0, mM: 0, mW: 0, cS: 0, cC: 0, cM: 0, cW: 0, mE: {}, mEd: {}, cE: {}, cEd: {} }
      a.days = days // month total (identical on every day-class slice row)
      if (r.mod) { const w = r.mod.scoredDays ?? 1; a.mS += r.mod.services * w; a.mC += r.mod.carriedPerDay * w; a.mM += milesOf(r.mod) * w; a.mW += w; addEng(a.mE, r.mod.engineMilesPerDay, w); addEng(a.mEd, r.mod.engineDaysPerDay, w) }
      if (r.cur) { const w = r.cur.scoredDays ?? 1; a.cS += r.cur.services * w; a.cC += r.cur.carriedPerDay * w; a.cM += milesOf(r.cur) * w; a.cW += w; addEng(a.cE, r.cur.engineMilesPerDay, w); addEng(a.cEd, r.cur.engineDaysPerDay, w) }
      byMonth.set(r.monthNo, a)
    }
    for (const [cal, a] of byMonth) {
      const idx = (cal - 3 + 12) % 12
      const md = a.days
      const modS = a.mW > 0 ? a.mS / a.mW : 0, modC = a.mW > 0 ? a.mC / a.mW : 0, modM = a.mW > 0 ? a.mM / a.mW : 0
      const curS = a.cW > 0 ? a.cS / a.cW : 0, curC = a.cW > 0 ? a.cC / a.cW : 0, curM = a.cW > 0 ? a.cM / a.cW : 0
      modelA[idx] = { runningDays: md, workings: modS * md, passengers: modC * md, trainMiles: modM * md }
      basisA[idx] = { runningDays: md, workings: curS * md, passengers: curC * md, trainMiles: curM * md }
      putEng(modelEng, idx, a.mE, a.mEd, a.mW, md)
      putEng(basisEng, idx, a.cE, a.cEd, a.cW, md)
    }
    const basisEngines = hasEng(basisEng) ? basisEng : undefined
    return { basis: withEngineActivity(basisA, basisEngines), model: withEngineActivity(modelA, hasEng(modelEng) ? modelEng : undefined), proxyRows, basisEngines }
  })()
  /** What the register adds to the cost model (costLines.ts::RollingStockInput):
   *  the engines ticked "Use in simulation", the months' running days for the
   *  Fixed Rolling Stock spread, and each engine's basis-season miles and days. */
  const rollingInput = { locos: fleet.locos, runningDays: costActivity.basis.map((a) => a.runningDays), basisEngines: costActivity.basisEngines }

  // ── The P&L cost model as a PER-DAY cost function ──
  // This is what makes the accounts costs actually DRIVE the modelling: the
  // scored cost of a day = a fixed per-running-day block + the accounts' per-
  // driver rates x that day's activity (workings, train-miles, passengers). It
  // replaces costFromDeps().total everywhere a day's COST feeds the roll-up, the
  // headline and the board paper, so the figures move to the accounts basis and
  // change as the design changes. Aggregated over the season it reproduces the
  // Modelling-costs Table 2 total. The rate-card CostBreakdown is still computed
  // for the legacy category panels; only the headline £ is swapped.

  const pnlSim = studioCostsData
    ? buildSim(studioCostsData, costConfig, costLegacySeed, costActivity.basis, costActivity.model, rollingInput)
    : null
  const pnlCost = pnlSim ? buildDayCostModel(pnlSim.lines, pnlSim.sim, costActivity.basis, 24) : null
  /** Running days of a calendar month of the season (thebigplan calendar / the applied set). */
  const monthRunDaysOf = (mm: number): number => {
    const mk = monthOptions.find((o) => Number(o.value.split('-')[1]) === mm)?.value
    return mk ? (realRunDays(mk) ?? 0) : 0
  }
  /** THIS month's FIXED cost on the accounts basis — charged whether or not a
   *  standard service runs (Chris, 2026-09-04: the line runs all year). */
  const pnlMonthFixedFor = (side: 'mod' | 'cur', mm: number): number => (pnlCost ? pnlMonthFixed(pnlCost, side, mm) : NaN)
  /** One running day's share of its month's fixed cost. */
  const pnlDayFixed = (side: 'mod' | 'cur', mm: number): number => (pnlCost ? pnlDayFixedShare(pnlCost, side, mm, monthRunDaysOf(mm)) : NaN)
  /** The VARIABLE cost of one running day from its activity. */
  const pnlDayVar = (services: number, carriedPerDay: number, miles?: number | null): number =>
    (pnlCost ? pnlDayVariable(pnlCost, { services, carriedPerDay, miles }) : NaN)
  /** One running day's total cost in month `mm`: fixed share + variable. */
  const pnlDayCost = (mm: number, services: number, carriedPerDay: number, side: 'mod' | 'cur', miles?: number | null): number =>
    pnlCost ? pnlDayCostFn(pnlCost, { services, carriedPerDay, miles }, side, { calMonth: mm, runningDays: monthRunDaysOf(mm) }) : NaN
  /**
   * The timetable one plan (one side) runs on a date, in the shape
   * perDateValue.ts::daySide wants, priced ONE way everywhere: the accounts
   * (P&L) model when loaded — fixed lines only on a no-service date — else the
   * rate card's total / day-fixed split. Miles are the engine's per-segment
   * mileage (costFromDeps), the same figure the cost tab's activity uses.
   * Shared by the season-table memo below AND buildSeasonExec (board paper +
   * workbook), so a month can only ever have one set of numbers.
   */
  const planShapeFor = (deps: Departure[], dl: number, side: 'mod' | 'cur', mm: number, extra?: { sets?: number; firecon?: boolean; date?: string; colour?: string; roster?: RosterDay | null }): { plan: DayPlanShape; cost: (pax: number, noService: boolean) => { fixed: number; variable: number } } => {
    const enabled = deps.filter((x) => x.enabled && Number.isFinite(x.minutes))
    // A Firecon day designates coal steam DIESEL; a DATED shape takes that day's
    // fleet — engines out that day are not in it (diagrams.ts::DesignateOptions);
    // with the season roster, the roster's engines for the date stand.
    const dated = !!(extra?.firecon || extra?.date)
    const cf = costFromDeps(net0, enabled, rates, dl, fleet, undefined, undefined, dated
      ? { firecon: !!extra?.firecon, date: extra?.date, ...(extra?.roster && extra.colour ? { roster: { colour: extra.colour, day: extra.roster } } : {}) }
      : undefined)
    const failure = !!extra?.roster?.failure
    const services = failure ? 0 : enabled.filter((x) => !x.positioning).length
    const miles = failure ? 0 : cf.steamMiles + cf.dieselMiles
    const plan: DayPlanShape = {
      services, sets: failure ? 0 : (extra?.sets ?? 0), steamN: failure ? 0 : cf.steamN, dieselN: failure ? 0 : cf.dieselN + cf.railcars, miles,
      ...(failure ? {} : { engineMiles: cf.engineMiles }),
      ...(extra?.date ? { fleet: { swapped: cf.steamSwapped, noEngine: extra?.roster ? extra.roster.uncovered.length : cf.locoOut.filter((o) => o.kind === 'no-engine').length, out: cf.enginesOut, ...(failure ? { failure: true } : {}) } } : {}),
    }
    // FIXED = this date's share of ITS MONTH's fixed cost (charged whether or
    // not trains run); VARIABLE = the trains' cost, nothing when none run.
    const cost = (pax: number, noService: boolean): { fixed: number; variable: number } => {
      const runs = !noService && services > 0
      return pnlCost
        ? { fixed: pnlDayFixed(side, mm), variable: runs ? pnlDayVar(services, pax, miles) : 0 }
        : { fixed: cf.dayFixed, variable: runs ? Math.max(0, cf.total - cf.dayFixed) : 0 }
    }
    return { plan, cost }
  }
  // DAY-FOR-DAY season valuation (Chris, 2026-09-02/03): each banked month's
  // modelled and current sides are the SUM of their dates — every date valued
  // on its like-for-like basis day's ACTUAL sales (perDateValue.ts::daySide,
  // the one owner the WSS cells, the board paper and the workbook also call),
  // a date whose like day ran no standard services carried at 0 services and
  // fixed cost only, a date with no like day at the side's labelled average.
  // Keyed by slice id; yearRows re-bases each slice row from it.
  const perDateMonth = useMemo(() => {
    const out = new Map<string, { mod: SideTotals; cur: SideTotals | null }>()
    if (!data) return out
    for (const s of slices) {
      let dayPlans: SeasonDayPlan[] = []
      try { dayPlans = (JSON.parse(s.state_json) as { dayPlans?: SeasonDayPlan[] }).dayPlans ?? [] } catch { continue }
      if (dayPlans.length === 0) continue
      const mk = monthOptions.find((o) => Number(o.value.split('-')[1]) === s.month_no)?.value
      if (!mk) continue
      const mm = s.month_no
      const allDates = [...new Set(dayPlans.flatMap((p) => p.dates))].sort()
      const act = dateActualsFor(mk, allDates)
      if (!act) continue
      const res = parseSliceResult(s)
      const modSnap = res?.snap?.mod ?? null
      const curSnap = seasonCurrent.get(mm) ?? res?.snap?.cur ?? null
      const mw = seasonWtt[mk]?.available ? seasonWtt[mk] : null
      const formation = monthFormationFor(mm)
      const mod = emptyTotals()
      const cur = emptyTotals()
      let anyCur = false
      const yy = Number(mk.split('-')[0])
      const env = buildEnv()
      // Firecon days this month (firecon.ts): the same dates every surface marks.
      const fc = fireconDays(firecon, mm, allDates)
      for (const p of dayPlans) {
        const deps = p.deps ?? []
        const modShape = planShapeFor(deps, p.dl ?? dieselLoco, 'mod', mm)
        const modShapeFc = fc.size > 0 ? planShapeFor(deps, p.dl ?? dieselLoco, 'mod', mm, { firecon: true }) : modShape
        // The CURRENT side is the real WTT scored the way every other current
        // figure is (scoreColourPlan → applied: rakes assigned, uncoverable legs
        // dropped) — the SAME inputs buildSeasonExec prices, so the season table
        // and the month sheets agree to the pound. A raw wttToDeps shape here
        // priced legs the set plan drops and disagreed by £7k–£18k a month.
        const curRows = mw?.workings?.filter((x) => x.pattern_code === `TT-${p.colour.toUpperCase()}`) ?? []
        const gd0 = curRows.length > 0 ? planDemand(p, demandLookupFor(yy, mm), CLASS_LABELS) : null
        const gd = gd0 ? applyOneWayToday(gd0, p.oneWayToday) : null
        const curR = gd && curRows.length > 0
          ? scoreColourPlan({ net: net0, deps: wttToDeps(curRows, formation).deps, gd, pr: env.basePr, fareMult: 1, dl0: dieselLoco, formation, env })
          : null
        const curShape = curR ? planShapeFor(curR.applied, dieselLoco, 'cur', mm, { sets: curR.setsInTraffic }) : null
        if (curShape) anyCur = true
        const clsOf = (dt: string): string | undefined => (p.spans ?? []).find((sp) => sp.dates.includes(dt))?.dayClass ?? p.dayClass
        for (const dt of p.dates) {
          const a = act.byDate[dt] ?? null
          // Turned away by timing rides with the valuation (perDateValue.ts::
          // valueDepsOnDay, Chris 2026-09-07): a design that runs nothing on a
          // running day turns away that day's whole timed demand — the engine
          // says so itself, so the passengers and the turned-away agree.
          const likeDay = a && !isNoStandardServiceDay(a) ? a : null
          // A Firecon date: the same timetable run DIESEL, valued on the like
          // day's demand less the day-of-run reduction (Chris, 2026-09-11).
          const fd = fc.get(dt)
          // With the register in use every date is engined by the season roster
          // (engineRoster.ts): availability, contract order, pins; a FAILURE DAY
          // runs nothing — its whole timed demand is turned away.
          const rd = seasonRoster?.byDate.get(dt) ?? null
          const shape = seasonRoster ? planShapeFor(deps, p.dl ?? dieselLoco, 'mod', mm, { firecon: !!fd, date: dt, colour: p.colour, roster: rd }) : (fd ? modShapeFc : modShape)
          const modVal = likeDay ? valueDepsOnDay(net0, rd?.failure ? [] : deps, likeDay, params, farePct / 100, p.oneWayToday?.[clsOf(dt) ?? ''], fireconScale(fd)) : null
          addDay(mod, daySide({
            act: a, plan: shape.plan, valuedKind: 'valued',
            valued: shape.plan.services === 0
              ? { pax: 0, revenue: 0, ...(modVal ? { turnedAway: modVal.turnedAway } : {}) }
              : modVal,
            average: { pax: modSnap?.carriedPerDay ?? 0, revenue: modSnap?.revenuePerDay ?? 0, ...(modSnap?.turnedAwayPerDay != null ? { turnedAway: modSnap.turnedAwayPerDay } : {}) },
            cost: shape.cost,
            firecon: fd,
          }))
          if (curShape) {
            // Today's side: the like day's ACTUAL sales, with its turned-away from
            // the real timetable run on that day's demand — the same engine rule
            // both sides, so "against M today" is like for like.
            const curTa = likeDay && curR ? valueDepsOnDay(net0, curR.applied, likeDay, env.basePr, 1, p.oneWayToday?.[clsOf(dt) ?? '']).turnedAway : undefined
            addDay(cur, daySide({
              act: a, plan: curShape.plan, valuedKind: 'actual',
              valued: a ? { pax: a.pax, revenue: a.net, ...(curTa != null ? { turnedAway: curTa } : {}) } : null,
              average: { pax: curSnap?.carriedPerDay ?? 0, revenue: curSnap?.revenuePerDay ?? 0, ...(curSnap?.turnedAwayPerDay != null ? { turnedAway: curSnap.turnedAwayPerDay } : {}) },
              cost: curShape.cost,
            }))
          }
        }
      }
      out.set(String(s.id), { mod, cur: anyCur ? cur : null })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slices, data, farePct, params, net0, monthOptions, monthSources, seasonWtt, seasonCurrent, pnlCost, rates, fleet, dieselLoco, formationChoice, setsRoster, objective, firecon, seasonRoster])
  /** What the availability calendar did to the design, per calendar month —
   *  the season table's own per-date totals, for the Brief's fleet planner. */
  const fleetCoverByMonth = useMemo(() => {
    const out = new Map<number, { swapped: number; noEngine: number; outDays: number; failureDays: number }>()
    for (const s of slices) {
      const agg = perDateMonth.get(String(s.id))
      if (!agg) continue
      const cur = out.get(s.month_no) ?? { swapped: 0, noEngine: 0, outDays: 0, failureDays: 0 }
      cur.swapped += agg.mod.swappedTurns; cur.noEngine += agg.mod.noEngineTurns; cur.outDays += agg.mod.outEngineDays; cur.failureDays = (cur.failureDays ?? 0) + agg.mod.failureDays
      out.set(s.month_no, cur)
    }
    return out
  }, [perDateMonth, slices])
  /** yearRows = the snapshots re-based DAY FOR DAY: a slice row with banked
   *  plans and a sales basis takes its per-day figures from the per-date sums
   *  (perDateMonth) — passengers, revenue, costs and miles — so the season
   *  table, the roll-up chip, the workbook Summary and the board paper's
   *  season pages all equal the sum of the WSS cells. A row with no per-date
   *  accumulation keeps its snapshot, re-priced on the accounts basis. */
  const yearRows: YearRow[] = yearRowsBase.map((r) => {
    const agg = perDateMonth.get(r.key)
    const days = r.runningDays ?? 0
    const mm = r.monthNo
    // A month with NO running days still costs money — the line runs all year
    // (Chris, 2026-09-04). Both sides carry its fixed cost, nothing else.
    if (r.runningDays === 0 && pnlCost) {
      const fixedOnly = (side: 'mod' | 'cur'): YearSideSnap => ({
        services: 0, steamN: 0, dieselN: 0, carriedPerDay: 0, revenuePerDay: 0, costsPerDay: 0,
        // No running day, no demand — nobody is turned away.
        turnedAwayPerDay: 0,
        variablePerDay: 0, fixedMonth: pnlMonthFixedFor(side, mm), scoredDays: 0,
      })
      return { ...r, dayClass: 'No standard services', statusLabel: 'Not running', designed: true, cur: fixedOnly('cur'), mod: fixedOnly('mod') }
    }
    // A banked month whose design runs NOTHING (no working clears the floor) is
    // still a month the line pays for: its modelled side is its fixed cost with
    // no trains against it — never "—" (Chris, 2026-09-04). The D4 backstop in
    // parseSliceResult nulls a zero-service result, so read the stored result here.
    const sliceRunsNothing = (): boolean => {
      const sl = slices.find((x) => String(x.id) === r.key)
      if (!sl?.result_json) return false
      try { return (JSON.parse(sl.result_json) as { services?: number }).services === 0 } catch { return false }
    }
    const rebase = (side: YearSideSnap | null, t: SideTotals | null | undefined, which: 'mod' | 'cur'): YearSideSnap | null => {
      if (!side) {
        if (which === 'mod' && pnlCost && days > 0 && sliceRunsNothing()) {
          const fixedMonth = pnlMonthFixedFor('mod', mm)
          // A design that runs nothing turns away the running days' whole timed
          // demand — the per-date sums carry it when the plans are banked.
          const ta = t && t.days > 0 && t.turnedAwayDays === t.days ? { turnedAwayPerDay: t.turnedAway / days } : {}
          return { services: 0, steamN: 0, dieselN: 0, carriedPerDay: 0, revenuePerDay: 0, costsPerDay: fixedMonth / days, fixedMonth, variablePerDay: 0, scoredDays: days, ...ta }
        }
        return null
      }
      if (t && t.days > 0 && days > 0) {
        // Day-for-day sums. The month's fixed is taken from the accounts model
        // directly (exact), the variable from the dates.
        const fixedMonth = pnlCost ? pnlMonthFixedFor(which, mm) : t.fixed
        return {
          ...side,
          // A month whose every date ran no standard services ran no turns —
          // never print the pattern's 13/day against £0 of trains.
          services: t.turns === 0 ? 0 : side.services,
          servicesRange: t.turns === 0 ? undefined : side.servicesRange,
          steamN: t.turns === 0 ? 0 : side.steamN, dieselN: t.turns === 0 ? 0 : side.dieselN,
          carriedPerDay: t.pax / days, revenuePerDay: t.revenue / days, milesPerDay: t.miles / days,
          // Turned away by timing, day for day like the passengers; a side whose
          // dates could not all carry the figure keeps the snapshot's (or none).
          turnedAwayPerDay: t.turnedAwayDays === t.days ? t.turnedAway / days : side.turnedAwayPerDay,
          // Each named engine's timetabled miles and worked days, per running day.
          ...(Object.keys(t.engineMiles).length > 0 ? {
            engineMilesPerDay: Object.fromEntries(Object.entries(t.engineMiles).map(([k, v]) => [k, v / days])),
            engineDaysPerDay: Object.fromEntries(Object.entries(t.engineDays).map(([k, v]) => [k, v / days])),
          } : {}),
          fixedMonth, variablePerDay: t.variable / days, costsPerDay: (fixedMonth + t.variable) / days,
          noServiceDays: t.noServiceDays, averageDays: t.averageDays,
        }
      }
      if (!pnlCost) return side
      // No per-date accumulation (no banked plans or no sales basis): the
      // snapshot re-priced on the accounts basis, month-own fixed.
      const fixedMonth = pnlMonthFixedFor(which, mm)
      const variablePerDay = side.services > 0 ? pnlDayVar(side.services, side.carriedPerDay, side.milesPerDay) : 0
      return { ...side, fixedMonth, variablePerDay, costsPerDay: days > 0 ? (fixedMonth + variablePerDay * days) / days : 0 }
    }
    return { ...r, cur: rebase(r.cur, agg?.cur, 'cur'), mod: rebase(r.mod, agg?.mod, 'mod') }
  })
  // The modelled season contribution on the accounts (P&L) basis — for the shell
  // roll-up chip, so it agrees with the season table rather than the rate card.
  const seasonProfitPnl = pnlCost ? (yearTotals(yearRows).mod?.profit ?? null) : null

  // ★ MODELLED NOT RUN — the days the planner asked to run but the model
  // dropped as unprofitable (their colour x day-type built no train that pays).
  // Derived from the banked build: a day-plan with dates but no departures.
  // Shown as its own category on the calendar (Chris, 2026-08-26).
  const modelledNotRun = useMemo(() => {
    const out: string[] = []
    for (const s of slices) {
      try {
        const st = JSON.parse(s.state_json) as { dayPlans?: SeasonDayPlan[] }
        for (const pl of st.dayPlans ?? []) {
          if ((pl.deps?.length ?? 0) === 0) for (const d of pl.dates ?? []) out.push(d)
        }
      } catch { /* a slice that will not parse contributes nothing */ }
    }
    return [...new Set(out)]
  }, [slices])

  const stageComplete: Record<StageKey, boolean> = {
    brief: !!data, cost: costLocked, calendar: appliedDays != null && !!baseline, baseline: !!baseline,
    build: designClean, explore: !!advisorLog?.recommendation, results: sliceQualifies,
  }
  // Explore is HIDDEN for now (Chris, 2026-09-02: "you can just hide this tab
  // for now — I don't have enough confidence in the basic data never mind
  // letting a claude run free over it"). Code kept; nav entry removed, and any
  // session saved on that stage lands on Results instead.
  const stageDefs: StageDef[] = STAGE_DEFS
    .filter((s) => s.key !== 'explore')
    .map((s, i) => ({ key: s.key, n: i + 1, label: s.label, complete: stageComplete[s.key] }))

  const STATUS_LABEL: Record<SessionStatus, string> = { draft: 'Draft', in_review: 'In review', approved: 'Approved', archived: 'Archived' }
  const modeLabel = mode === 'through' ? 'Through' : 'Grosmont connect'
  const dayTypeCount = new Set(slices.map((s) => s.day_class)).size
  const scope = slices.length === 0
    ? `${monthLabel || data?.season || ''} · ${dayLabel} · ${modeLabel}`
    : `${new Set(slices.map((s) => s.month_no)).size} month${new Set(slices.map((s) => s.month_no)).size === 1 ? '' : 's'} · ${dayTypeCount} day-type${dayTypeCount === 1 ? '' : 's'} · ${[...new Set([...slices.map((s) => s.mode), mode])].map((m) => m === 'through' ? 'Through' : 'Grosmont').join(' vs ')}`

  // Accounts-anchored (P&L) cost for the headline — falls back to the rate-card
  // model when no P&L basis is loaded, so the headline moves to the accounts basis.
  const periodCostsPnl = pnlCost && scenario
    ? pnlDayCost(monthNo, servicesNow, scenario.carried / periodDaysNow, 'mod', costs ? costs.steamMiles + costs.dieselMiles : undefined) * periodDaysNow
    : periodCosts
  const baseCostsPnl = pnlCost && cardCtx
    ? pnlDayCost(monthNo, baseline?.services ?? servicesNow, cardCtx.baseCarried / periodDaysNow, 'cur', cardCtx.baseMiles > 0 ? cardCtx.baseMiles / periodDaysNow : undefined) * periodDaysNow
    : (cardCtx?.baseCosts ?? 0)
  const proposedContrib = scenario && cardCtx ? (scenario.revenue + upliftModelled) - periodCostsPnl : 0
  const todayContrib = cardCtx ? (cardCtx.baseRevenue + upliftNow) - baseCostsPnl : 0
  const extraContrib = proposedContrib - todayContrib

  // ---- session + running-set handlers ----
  /** Remember which running set is applied for a season, so a plain reload
   *  restores it (Chris, 2026-08-27). Per-browser convenience; safe if blocked. */
  const rememberAppliedSet = (id: number | null) => {
    try {
      const key = `nymr-studio-applied-set-${data?.season ?? chosenSeason ?? ''}`
      if (id == null) localStorage.removeItem(key); else localStorage.setItem(key, String(id))
    } catch { /* localStorage unavailable — nothing to persist */ }
  }
  const applyRunningSet = async (id: number) => {
    setRunningBusy(true)
    try { const set = await loadRunningSet(id); setRunningSetId(set.id); setRunningSetName(set.name); setAppliedDays(set.days); rememberAppliedSet(set.id) }
    catch (e) { setSessionError(`Couldn't apply the running set: ${String((e as Error)?.message ?? e)}`) }
    finally { setRunningBusy(false) }
  }
  /** Save the calendar's running days. An id UPDATES that set in place — the
   *  planner corrects the set they are working on instead of being forced to
   *  invent a new one every time (Chris, 2026-08-24). */
  const saveRunningSet = async (name: string, days: RunningDay[], id?: number) => {
    if (!data) return
    setRunningBusy(true)
    try {
      const count = days.filter((d) => d.running).length
      let effId = id ?? null
      if (id != null) {
        await updateRunningSet(id, { name, season: data.season, days })
        setRunningSetMetas((prev) => prev.map((m) => (m.id === id ? { ...m, name, count } : m)))
        setRunningSetId(id); setRunningSetName(name)
      } else {
        const newId = await createRunningSet({ name, season: data.season, days })
        setRunningSetMetas((prev) => [{ id: newId, name, count, isDefault: false }, ...prev.filter((p) => p.id !== newId)])
        setRunningSetId(newId); setRunningSetName(name); effId = newId
      }
      setAppliedDays(days); rememberAppliedSet(effId); setSessionError(null)
    } catch (e) { setSessionError(`Couldn't save the running set: ${String((e as Error)?.message ?? e)}`) } finally { setRunningBusy(false) }
  }
  const removeRunningSet = async (id: number) => {
    setRunningBusy(true)
    try {
      await deleteRunningSet(id)
      setRunningSetMetas((prev) => prev.filter((m) => m.id !== id))
      // Deleting the set in play drops back to the standard timetable rather
      // than leaving the modelling pointed at something that no longer exists.
      if (runningSetId === id) { setRunningSetId(null); setRunningSetName(null); setAppliedDays(null); rememberAppliedSet(null) }
      setSessionError(null)
    } catch (e) { setSessionError(`Couldn't delete the running set: ${String((e as Error)?.message ?? e)}`) } finally { setRunningBusy(false) }
  }
  /** Mark a set the season's default (or clear it) — it then loads on open. */
  const setDefaultSet = async (id: number, isDefault: boolean) => {
    setRunningBusy(true)
    try {
      await setDefaultRunningSet(id, isDefault)
      // One default per season — clear the flag on the others locally.
      setRunningSetMetas((prev) => prev.map((m) => ({ ...m, isDefault: m.id === id ? isDefault : (isDefault ? false : m.isDefault) })))
      setSessionError(null)
    } catch (e) { setSessionError(`Couldn't set the default set: ${String((e as Error)?.message ?? e)}`) } finally { setRunningBusy(false) }
  }
  /**
   * Where the Brief bound on the LIVE design (Save on the Brief or Modelling
   * stage) — computed by the season build's own owner, scoreColourPlan →
   * colourIssues, on the live deps with their assigned rakes, so a hand-saved
   * design is checked against the Brief exactly as a built month is and the
   * Recommendation chip fires only for slices banked by an earlier version
   * (Chris, 2026-09-07).
   */
  const liveIssues = (): MonthIssue[] => {
    if (!evalResult || !selection || classesToday.length === 0) return []
    const parts = classesToday.map((c) => ({ dayClass: c.cls, days: Math.max(1, Math.round(c.weight)), cls: c, un: 0 }))
    const blended = parts.length === 1 ? { cls: parts[0].cls, un: 0 } : blendParts(parts)
    const gd: GroupDemand = {
      design: { cls: blended.cls, un: blended.un, basis: 'the live design' },
      parts, days: parts.reduce((t, p) => t + p.days, 0),
      mix: parts.map((p) => `${CLASS_LABELS[p.dayClass] ?? p.dayClass} x${p.days}`).join(' · '),
    }
    const env = buildEnv()
    const s = scoreColourPlan({
      net, deps: evalResult.applied, gd, pr: params, fareMult: farePct / 100, dl0: dieselLoco, formation, env,
      pre: { carriages: evalResult.plan.carriages, setsInTraffic: evalResult.plan.setsInTraffic, byDep: evalResult.plan.byDep },
    })
    const code = (baselineWtt?.selectedPattern ?? '').replace(/^TT-/i, '')
    const colour = code ? code.charAt(0).toUpperCase() + code.slice(1).toLowerCase() : (CLASS_LABELS[currentDc] ?? currentDc)
    return colourIssues(s, colour, env)
  }
  const saveCurrentSlice = async (id: number) => {
    const hh = scenario ? headline() : null
    // Both-side per-day snapshots power the year comparison (Compare stage +
    // exec PDF): each saved slice carries its modelled AND current-side numbers
    // so the season view is scored month-by-month against each month's own
    // pattern, never recomputed against the active one.
    const res = hh ? {
      contributionPerDay: contribPerDayNow, carried: hh.carried, unreturned: hh.unreturned, revenue: hh.revenue, services: servicesNow,
      snap: {
        mod: {
          services: servicesNow, steamN: costs?.steamN ?? 0, dieselN: (costs?.dieselN ?? 0) + (costs?.railcars ?? 0),
          carriedPerDay: hh.carried / Math.max(1, periodDaysNow),
          revenuePerDay: hh.revenue / Math.max(1, periodDaysNow),
          costsPerDay: hh.costs / Math.max(1, periodDaysNow),
          milesPerDay: costs ? costs.steamMiles + costs.dieselMiles : undefined,
          turnedAwayPerDay: (scenario?.lostSales ?? 0) / Math.max(1, periodDaysNow),
          ...(costs ? engineSnap(costs) : {}),
        },
        cur: baseline ? {
          services: baseline.services, steamN: baseline.steamN, dieselN: baseline.dieselN,
          carriedPerDay: baseline.carried / Math.max(1, periodDaysNow),
          revenuePerDay: (baseline.revenue + (baseline.services > 0 ? upliftNow : 0)) / Math.max(1, periodDaysNow),
          costsPerDay: baseline.costs / Math.max(1, periodDaysNow),
          milesPerDay: baseline.miles / Math.max(1, periodDaysNow),
          turnedAwayPerDay: baseline.lost / Math.max(1, periodDaysNow),
          ...(Object.keys(baseline.engineMiles).length > 0 ? { engineMilesPerDay: baseline.engineMiles, engineDaysPerDay: baseline.engineDays } : {}),
        } : null,
      },
      // A hand-saved live design carries its Brief check like a built month
      // (Chris, 2026-09-07) — the same owner as the build, scoreColourPlan →
      // colourIssues — so the Recommendation chip only ever fires for slices
      // banked by an earlier version.
      issues: liveIssues(),
    } : null
    const status: SliceStatus = sliceQualifies ? 'designed' : (deps.length > 0 ? 'in_progress' : 'draft')
    // A hand save KEEPS the month's banked plans when the design on screen is the
    // banked design — the planner saved a pin, a note or a setting, not a new
    // timetable. A saved slice without its dayPlans falls out of the per-date
    // season (2026-09-11: a Save on the Modelling stage after an engine swap made
    // July vanish from the roster, the workbook and the paper). A CHANGED design
    // still drops them, as before — that month is the live design until rebuilt.
    let bankedKeep: Record<string, unknown> = {}
    try {
      const existing = slices.find((s) => s.month_no === monthNo && s.mode === mode && s.day_class === currentDc)
      const prev = existing ? (JSON.parse(existing.state_json) as { dayPlans?: SeasonDayPlan[] }) : null
      const banked = monthKey ? bankedViewFor(monthKey) : null
      const sig = (ds: Departure[]) => ds.filter((x) => x.enabled && Number.isFinite(x.minutes)).map((x) => `${x.minutes}|${x.o}|${x.d}|${x.positioning ? 1 : 0}`).sort().join(';')
      if (prev?.dayPlans?.length && banked && sig(banked.deps) === sig(deps)) bankedKeep = { dayPlans: prev.dayPlans }
    } catch { /* no banked plans to keep */ }
    await saveSlice(id, { month_no: monthNo, day_class: currentDc, mode, state: { ...captureState(), ...bankedKeep }, result: res, status })
  }
  const saveSession = async () => {
    if (!data) return
    setSessionBusy(true)
    try {
      const months = [...new Set([...slices.map((s) => s.month_no), monthNo].filter(Boolean))]
      const modes = [...new Set([...slices.map((s) => s.mode), mode])]
      const meta = {
        name: sessionName, season: chosenSeason ?? data.season, question: sessionQuestion ?? undefined, status: sessionStatus,
        months, modes, active_stage: stage,
        cost_model: costLocked ? rates : undefined,
        running_set: runningSetId != null ? { id: runningSetId, name: runningSetName } : undefined,
        period: { mode: periodMode, from: rangeFrom || undefined, to: rangeTo || undefined },
        narrative: narrative || undefined,
        data_as_at: new Date().toISOString().slice(0, 10),
      }
      let id = sessionId
      if (id == null) { id = await createSession(meta); setSessionId(id) } else { await updateSession(id, meta) }
      await saveCurrentSlice(id)
      const loaded = await loadSession(id)
      setSlices(loaded.slices); setSavedAt('Saved just now'); setSessionError(null)
      { const p = latestSlicePins(loaded.slices); if (p) setEnginePins(p) }
    } catch (e) { setSessionError(`Couldn't save the session: ${String((e as Error)?.message ?? e)}`) } finally { setSessionBusy(false) }
  }
  // =========================================================================
  //  DESIGN THE WHOLE SEASON (Chris, 2026-08-20: "it needs to run the
  //  modelling for the entire season — the best timetable and running sets
  //  for each month, not just August"). For EVERY month: build that month's
  //  own demand classes, take ITS pattern's real workings for the operating
  //  window and path budget, run ★ Best fit under the planner's locked
  //  parameters, and save the result as that month's designed slice — so the
  //  season view, roll-up and season-timetable PDF carry the whole year.
  // =========================================================================
  const [seasonDesigning, setSeasonDesigning] = useState(false)
  const [seasonRunLog, setSeasonRunLog] = useState<string[]>([])
  // Where the Brief bound, after a build — the review panel's list (Chris,
  // 2026-09-04: "this list of fixes came up at the end of the modelling…
  // the user can click and fix, then re-build").
  /**
   * ★ Build Model. `months` limits a rebuild to the months a Brief fix
   * settles; `over` is that fix, applied to the build's environment (and the
   * saved state) directly — React state is updated too, but not in time for
   * this call to read it.
   */
  const designSeason = async (opts?: { months?: Set<string>; over?: MonthFixChange }) => {
    console.debug('[designSeason] enter', { data: !!data, avail: baselineWtt?.available, days: baselineWtt?.days?.length, workings: baselineWtt?.workings?.length, seasonDesigning, months: monthOptions.length })
    if (!data || !baselineWtt?.available || !baselineWtt.days?.length || !baselineWtt.workings || seasonDesigning) return
    setSeasonDesigning(true)
    setSeasonRunLog([opts?.months
      ? `Rebuilding ${[...opts.months].map((mk) => MONTHS[Number(mk.split('-')[1]) - 1]).join(', ')} with the Scope change applied…`
      : 'Designing the season month by month — ONE timetable per colour, built against the whole week that colour runs, then valued day by day on each day-type\u2019s own sales…'])
    const logLine = (s: string) => setSeasonRunLog((prev) => [...prev, s])
    try {
      // Ensure a session to save slices into.
      let id = sessionId
      if (id == null) {
        id = await createSession({
          name: sessionName, season: chosenSeason ?? data.season, question: sessionQuestion ?? undefined, status: sessionStatus,
          months: monthOptions.map((mo) => Number(mo.value.split('-')[1])), modes: [mode], active_stage: stage,
          cost_model: costLocked ? rates : undefined,
          running_set: runningSetId != null ? { id: runningSetId, name: runningSetName } : undefined,
          period: { mode: periodMode, from: rangeFrom || undefined, to: rangeTo || undefined },
          data_as_at: new Date().toISOString().slice(0, 10),
        })
        setSessionId(id)
      }
      for (const mo of monthOptions) {
        if (opts?.months && !opts.months.has(mo.value)) continue
        const [yy, mm] = mo.value.split('-').map(Number)
        // Yield to the browser between months — bestFit is heavy.
        await new Promise((r) => setTimeout(r, 30))
        try {
          // The month's own baseline, fetched for a date the railway ACTUALLY
          // RUNS (never the 15th — that returned the winter timetable for March
          // and lost seven Yellow days in silence).
          const mw = await ensureMonthWtt(mo.value)
          if (!mw || !mw.days?.length || !mw.workings) { logLine(`${MONTHS[mm - 1]}: thebigplan calendar unavailable — skipped (never guessed).`); continue }
          const info = monthDayGroups(mo.value)
          if (!info || info.runDays === 0) { logLine(`${MONTHS[mm - 1]}: no running days in the timetable calendar — skipped.`); continue }
          // THE BUILD ITSELF is pure and lives in studio/seasonBuild.ts, where a
          // headless gate runs the whole season against real data. This loop
          // only fetches, awaits and saves.
          const r = buildMonth({
            monthKey: mo.value,
            groups: info.groups,
            runDays: info.runDays,
            wtt: monthWtt(mw),
            formation: monthFormationFor(mm),
            demandFor: demandLookupFor(yy, mm),
            env: buildEnv(opts?.over),
          })
          for (const l of r.log) logLine(l)
          // `skipped` means the month could not be ATTEMPTED — nothing to save.
          // `emptyReason` means it WAS modelled and no train pays: that is an
          // answer, so it is saved and shown rather than left looking unmodelled.
          if (r.skipped || !r.mod) { logLine(`${MONTHS[mm - 1]}: not modelled — ${r.skipped ?? 'no month total could be formed'}.`); continue }
          // Every built month is banked as DESIGNED. Where the Brief bound is
          // carried in `issues`, in words, for every surface to print — never a
          // second status that reads as unfinished (Chris, 2026-09-04).
          await saveSlice(id, {
            month_no: mm, day_class: 'Week', mode,
            state: { ...captureState(opts?.over), monthKey: mo.value, dayClass: 'Week', deps: r.headlineDeps, dayOverrides: {}, dayPlans: r.dayPlans },
            result: {
              contributionPerDay: r.contributionPerDay,
              carried: r.carried,
              unreturned: r.unreturned,
              revenue: r.mod.revenuePerDay,
              services: r.services,
              snap: { mod: r.mod, cur: r.cur },
              issues: r.issues,
            },
            status: 'designed',
          })
        } catch (e) {
          logLine(`${MONTHS[mm - 1]}: failed — ${String((e as Error)?.message ?? e)}`)
        }
      }
      const loaded = await loadSession(id)
      setSlices(loaded.slices)
      setSavedAt('Saved just now')
      // The built timetable lands on screen by itself: the banked signature is
      // part of the deps-seeding key, so a finished build reseeds the month on
      // view. Just clear the author-edit flag so it is allowed to.
      depsTouched.current = false
      setClassView(null); setDayDraft(null); setDraftDirty(false)
      const builtN = loaded.slices.filter((x) => builtSlice(x)).length
      const bound = reviewMonthsFrom(loaded.slices)
      const pts = bound.reduce((t, m) => t + m.issues.length, 0)
      logLine(`Season build complete — ${builtN} month${builtN === 1 ? '' : 's'} built, valued and saved to the programme.${pts > 0
        ? ` The Scope bound on ${pts} point${pts === 1 ? '' : 's'} in ${bound.map((m) => m.monthLabel).join(', ')} — ⚠ Interventions on the stage bar lists each with the Scope change that settles it.`
        : ' Every month is inside the Scope.'} The working turns below show the built timetable for the month on screen; switch months with the pills to read the others.`)
      // The review panel no longer opens by itself (Chris, 2026-10-27: "you're
      // stopping me building a new model with this stupid pop up"). The points
      // are listed under ⚠ Interventions on the stage bar, opened by the planner.
    } catch (e) {
      logLine(`Season design stopped: ${String((e as Error)?.message ?? e)}`)
    } finally {
      setSeasonDesigning(false)
    }
  }

  // New session = a blank in-memory draft. Name + question are edited inline on
  // the Brief stage (no browser prompts); "Save" in the header persists it.
  //
  // A new session starts from the RAILWAY, never from whatever the previous
  // session left in the scratchpad (Chris, 2026-08-24: a new session was
  // opening on the last session's OPTIMISED workings, so current-vs-modelled
  // measured the same timetable). The design grid reseeds from the real WTT,
  // the operating window re-derives from it, and every author-owned artefact
  // — overrides, narrative, advisor log, formation choice — clears.
  // The full modelling reset — everything returns to how a brand-new session
  // starts: the design grid reseeds from the REAL working timetable, the
  // window re-derives from it, the sliders return to the saved railway
  // defaults, and every author-owned artefact clears. Shared by "New session"
  // and the Brief stage's "Reset to railway defaults" button (Chris,
  // 2026-08-24).
  const resetModelling = () => {
    depsTouched.current = false
    depsSeedKey.current = null
    setSeedNonce((n) => n + 1) // re-runs the seeding effect -> real WTT deps
    const savedWin = railwayEngine.current?.window
    windowTouched.current = !!savedWin
    if (savedWin) { setFirstTrain(savedWin.firstTrain); setLastTrain(savedWin.lastTrain) }
    else if (wttWindow) { setFirstTrain(wttWindow.first); setLastTrain(wttWindow.last) }
    // The planner's barriers back to the saved railway defaults (db/65) — or
    // the shipped values where no default has been saved yet.
    const re = railwayEngine.current
    setTolerance(re?.tolerance ?? 75)
    setRecapturePct(re?.recapturePct ?? 70)
    setFarePct(re?.farePct ?? 100)
    setDwellMin(re?.dwellMin ?? 180)
    setMaxStay(re?.maxStay ?? false)
    setForceEarly(re?.forceEarly ?? true)
    setSpillNearest(re?.spillNearest ?? false)
    setFirecon(normaliseFirecon(re?.firecon ?? DEFAULT_FIRECON))
    setEnginePins([])
    setReturnMode(re?.returnMode ?? 'latest')
    setMaxUnreturned(re?.maxUnreturned ?? 0)
    setMinRevPerDep(re?.minRevPerDep ?? 500)
    setMonthSources(re ? adoptSources(re) : { by: {}, reason: '' })
    setRates(railwayDefault.current ?? cloneCostModel(DEFAULT_COST_MODEL))
    setDayOverrides({})
    setNarrative('')
    setWhatsChanged('')
    setAdvisorLog(null)
    setFitLog(null)
    setFormationChoice(null)
    setDieselLoco(0)
    setTractionLocked(true)
    setFaresLocked(true)
    setCostLocked(false)
    setClassView(null)
    setDayDraft(null)
    setDraftDirty(false)
  }
  /** New session — named, and its question set, as it is created (the Brief
   *  no longer carries the two inputs; Chris, 2026-10-28). */
  const newSession = (name: string, question: string) => {
    setSessionId(null)
    setSessionName(name.trim() || 'Untitled session')
    setSessionQuestion(question.trim() || null)
    setSessionStatus('draft')
    setSlices([])
    setPeriodMode('season')
    setRangeFrom('')
    setRangeTo('')
    setSavedAt('Not saved yet')
    setSessionError(null)
    setStage('brief')
    resetModelling()
  }
  const openList = async () => {
    setOpenModal(true); setSessionError(null); setConfirmDeleteId(null); setEditRow(null)
    if (!data) return
    // EVERY season's sessions (Chris, 2026-10-28: sessions saved against 2027/28
    // vanished from the list while the Studio showed 2026/27). Opening one
    // switches the Studio to its season (openSessionById).
    try { setSessionsList(await listSessions()) } catch (e) { setSessionError(String((e as Error)?.message ?? e)) }
  }
  // Delete a saved session (cascades slices + versions server-side). Two-step
  // inline confirm — never a browser popup (law 7).
  const removeSession = async (id: number) => {
    setSessionBusy(true)
    try {
      await deleteSession(id)
      setSessionsList((prev) => prev.filter((x) => x.id !== id))
      setConfirmDeleteId(null)
      if (sessionId === id) { setSessionId(null); setSavedAt('Not saved yet') }
    } catch (e) {
      setSessionError(`Couldn't delete: ${String((e as Error)?.message ?? e)}`)
    } finally { setSessionBusy(false) }
  }
  // The pen in the Open… list: name and question only (PATCH), for any saved
  // session — the open one updates on the stage bar too.
  const saveRename = async () => {
    if (!editRow || !editRow.name.trim()) return
    setSessionBusy(true)
    try {
      const q = editRow.question.trim() || null
      await renameSession(editRow.id, editRow.name.trim(), q)
      setSessionsList((prev) => prev.map((x) => x.id === editRow.id ? { ...x, name: editRow.name.trim(), question: q } : x))
      if (sessionId === editRow.id) { setSessionName(editRow.name.trim()); setSessionQuestion(q) }
      setEditRow(null)
    } catch (e) {
      setSessionError(`Couldn't rename: ${String((e as Error)?.message ?? e)}`)
    } finally { setSessionBusy(false) }
  }
  const openSessionById = async (id: number) => {
    setSessionBusy(true)
    try {
      const { session, slices: sl } = await loadSession(id)
      setSessionId(session.id); setSessionName(session.name); setSessionStatus(session.status); setSessionQuestion(session.question)
      setSlices(sl)
      if (session.season) setChosenSeason(session.season)
      // Sessions saved before Design and Compare merged carry the retired
      // stage keys — land them on Build rather than a blank screen.
      if (session.active_stage) {
        const st = session.active_stage === 'design' || session.active_stage === 'compare' ? 'build' : session.active_stage
        setStage(st as StageKey)
      }
      // The session's frozen model still locks the cost stage, but the LIVE
      // rates are the railway default when one is saved (default outranks).
      if (session.cost_model_json) { try { const cm = JSON.parse(session.cost_model_json) as CostModel; if (cm && Array.isArray(cm.locos)) { setRates(railwayDefault.current ?? cm); setCostLocked(true) } } catch { /* ignore */ } }
      if (session.running_set_json) { try { const rs = JSON.parse(session.running_set_json) as { id?: number }; if (rs?.id) await applyRunningSet(rs.id) } catch { /* ignore */ } }
      if (session.period_json) { try { const pd = JSON.parse(session.period_json) as { mode?: 'season' | 'range'; from?: string; to?: string }; setPeriodMode(pd.mode === 'range' ? 'range' : 'season'); setRangeFrom(pd.from ?? ''); setRangeTo(pd.to ?? '') } catch { /* ignore */ } }
      if (sl.length > 0) { try { applyState(JSON.parse(sl[0].state_json) as Record<string, unknown>) } catch { /* ignore */ } }
      setOpenModal(false); setSavedAt('Loaded'); setSessionError(null)
    } catch (e) { setSessionError(`Couldn't open: ${String((e as Error)?.message ?? e)}`) } finally { setSessionBusy(false) }
  }
  const selectSliceRow = (idStr: string) => {
    const s = slices.find((x) => String(x.id) === idStr)
    if (s) { try { applyState(JSON.parse(s.state_json) as Record<string, unknown>) } catch { /* ignore */ } }
  }

  /** Did the season build write this slice (it carries an issues list, even an
   *  empty one)? A hand-saved live design or a pre-2026-09-04 slice does not.
   *  Read raw: parseSliceResult nulls a zero-service result on purpose (D4). */
  const briefChecked = (s: SessionSlice): boolean => {
    try { const r = s.result_json ? JSON.parse(s.result_json) as { issues?: unknown } : null; return !!r && Array.isArray(r.issues) } catch { return false }
  }
  /** The months where the Brief bound, from the banked slices — ONE reader. */
  const reviewMonthsFrom = (list: SessionSlice[]): ReviewMonth[] => list
    .filter((s) => builtSlice(s) && s.mode === mode)
    .map((s) => ({ s, issues: parseSliceResult(s)?.issues ?? [] }))
    .filter((x) => x.issues.length > 0)
    .sort((a, b) => a.s.month_no - b.s.month_no)
    .map(({ s, issues }) => {
      const mk = monthOptions.find((o) => Number(o.value.split('-')[1]) === s.month_no)?.value ?? `${data?.season.slice(0, 4) ?? ''}-${String(s.month_no).padStart(2, '0')}`
      return { monthKey: mk, monthLabel: MONTHS[s.month_no - 1] ?? mk, issues }
    })
  const reviewMonths = reviewMonthsFrom(slices)

  /** Apply a Brief change from the review panel and rebuild the months it settles. */
  const applyFixAndRebuild = async (change: MonthFixChange, monthKeys: string[]) => {
    if (change.maxUnreturned != null) setMaxUnreturned(change.maxUnreturned)
    if (change.ecsDiesel != null) setEcsDiesel(change.ecsDiesel)
    if (change.maxSteam != null || change.maxDiesel != null || change.maxEcsDiesel != null) setFleet((prev) => fleetWith(prev, change))
    await designSeason({ months: new Set(monthKeys), over: change })
  }

  // The last saved session loads on open (Chris, 2026-09-04: "whatever the
  // last session is auto-load it") — once, and only when nothing is open yet.
  const autoLoaded = useRef(false)
  useEffect(() => {
    if (!data || autoLoaded.current || sessionId != null) return
    autoLoaded.current = true
    void (async () => {
      try {
        const list = await listSessions(data.season)
        const last = [...list].sort((a, b) => String((b as { updated_at?: string }).updated_at ?? '').localeCompare(String((a as { updated_at?: string }).updated_at ?? '')))[0]
        if (last) await openSessionById(last.id)
      } catch (e) {
        setSessionError(`Couldn't open the last session: ${String((e as Error)?.message ?? e)}`)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData])
  const refreshBaseline = async (pattern?: string) => {
    // Every pattern's workings are already in the payload — switching pattern
    // is a local re-selection, no round trip.
    if (pattern && baselineWtt?.available && baselineWtt.workings?.some((w) => w.pattern_code === pattern)) {
      setBaselineWtt({ ...baselineWtt, selectedPattern: pattern })
      return
    }
    setBaselineLoading(true)
    try {
      const b = await loadBaseline(pattern, monthKey ? probeDateFor(monthKey) : undefined, dayClass)
      setBaselineWtt((prev) => (b.available || !prev?.available ? b : prev))
    }
    finally { setBaselineLoading(false) }
  }

  const savedSetRows: SavedSetRow[] = runningSetMetas.map((m) => ({
    id: m.id, name: m.name,
    detail: m.id === runningSetId ? `${appliedDays?.filter((d) => d.running).length ?? 0} running days · applied` : 'Apply to roll up real running-days',
    applied: m.id === runningSetId,
    isDefault: m.isDefault,
  }))

  // ---- stage content ----
  const notReady = (
    <div className="sh-empty">Loading demand… this stage populates once <code>agg.ResearchServiceDemand</code> is in for the selected month.</div>
  )

  /** What the month on screen has banked — drives the turns panel's label and
   *  its "this day-type built nothing" callout. */
  const bankedView = monthKey ? bankedViewFor(monthKey) : null

  /** The season's months as a pill row — the Build stage's only scope control.
   *  The Brief sets the season and window and the running calendar sets the
   *  days; picking a month here just says WHICH month you want to look at
   *  (Chris, 2026-08-24: the old Month + Day-type selects were a second,
   *  competing source of truth). */
  const seasonIsBuilt = slices.some((sl) => builtSlice(sl))
  const pillStyle = (on: boolean, built: boolean): CSSProperties => ({
    borderRadius: 999, cursor: 'pointer', padding: '3px 11px', fontSize: '0.75rem', fontWeight: on ? 700 : 500,
    border: `${on ? 2 : 1}px solid ${on ? '#0b3d2e' : built ? '#17654a' : '#c89b3c'}`,
    background: built ? '#dcefe3' : '#fbefd2', color: built ? '#0b3d2e' : '#6b4f12',
  })
  const monthPills = (
    <div className="stu-bar" style={{ flexWrap: 'wrap', gap: 5 }}>
      {/* SEASON first — the default view, so the season's total gains are the
          first thing read (Chris, 2026-10-28). Amber = not built or loaded
          yet; green = built. The months follow the same colours. */}
      <button type="button" onClick={() => setModelView('season')} style={{ ...pillStyle(modelView === 'season', seasonIsBuilt), letterSpacing: '0.04em' }}
        title={seasonIsBuilt ? 'The season, month by month — current vs modelled with the grand totals' : 'The season has not been built yet — press ★ Build Model on the stage bar'}>
        SEASON
      </button>
      <span className="stu-note" style={{ margin: '0 2px 0 6px' }}>Month</span>
      {monthOptions.map((mo) => {
        const on = modelView === 'month' && mo.value === monthKey
        const mmP = Number(mo.value.split('-')[1])
        const designed = slices.some((sl) => sl.month_no === mmP && builtSlice(sl))
        const bound = reviewMonths.find((m) => Number(m.monthKey.split('-')[1]) === mmP)
        return (
          <button key={mo.value} type="button" onClick={() => { setMonthKey(mo.value); setModelView('month'); setMonthTab('fin') }}
            title={`Show ${mo.label}${designed ? (bound ? ` — built; the Scope bound: ${bound.issues.map((i) => i.text).join('; ')}` : ' — built') : ' — not built yet'}`}
            style={pillStyle(on, designed)}>
            {mo.label.replace(/ 20\d\d$/, '')}{designed && bound ? ' !' : ''}
          </button>
        )
      })}
      <span className="stu-bar-spacer" />
      <span className="stu-note">{runningDays.toFixed(1)} running days / week</span>
    </div>
  )

  /**
   * The settings the model runs under — running mode, minimum profit per
   * working and the Model settings drawer — composed here and shown on the
   * BRIEF (Chris, 2026-09-04: "they are lost here on this build tab").
   */
  const renderModelSettings = () => (
    <>
      <div className="stu-bar">
        <PillGroup
          value={mode}
          title="Connect at Grosmont keeps steam on our own metals and runs a diesel shuttle over Network Rail."
          options={[
            { value: 'through', label: 'Through running', title: 'One train, Pickering to Whitby — what the railway does today.' },
            { value: 'grosmont', label: 'Connect at Grosmont', title: 'Main line to Grosmont, then a shuttle over Network Rail to Whitby. Passengers change.' },
          ]}
          onChange={(v) => setMode(v as StudioMode)}
        />
        <span className="stu-bar-spacer" />
        <span className="stu-note" title="Best fit maximises PROFIT — every decision is charged its marginal engine, carriage, crew and fuel cost. The railway does not optimise for revenue.">optimised for profit</span>
        <NumberField
          label="Minimum profit per working, per day"
          value={minRevPerDep} min={0} step={100} onChange={setMinRevPerDep} width={78}
          prov="assumed"
          hint="PROFIT, not revenue: a working must earn at least this much MORE than it costs to run — its own fares less the extra engine, carriage, crew and fuel cost it adds — or Best fit will not run it and will withdraw it if it is already there. The railway's day-fixed costs (P-way, signalling, overheads) are not charged to it. Raise it to cut harder; £0 keeps every working that washes its own face."
        />
      </div>
      <SettingsDrawer
        sections={[
          {
            key: 'demand', label: 'Demand',
            body: (
              <div className="stu-grid">
                <RangeField label="Tolerance" value={tolerance} min={30} max={120} step={15} prov="assumed" format={(v) => `±${v} min`} onChange={setTolerance} hint="How far a passenger will shift their desired outbound departure." />
                <RangeField label="Recapture" value={recapturePct} min={0} max={100} step={5} prov="assumed" format={(v) => `${v}%`} onChange={setRecapturePct} hint="How much spilled demand survives to the next departure. The honest uncertainty knob." />
                <RangeField label="Time at destination" value={dwellMin} min={60} max={360} step={30} prov="assumed" disabled={maxStay} format={(v) => `≥ ${Math.floor(v / 60)}h${v % 60 ? ` ${v % 60}m` : ''}`} onChange={setDwellMin} hint={maxStay ? 'Ignored while Max stay is on.' : 'Before a passenger wants the return train.'} />
                <RangeField label="Fares" value={farePct} min={80} max={120} step={5} prov="measured" format={(v) => `${v}%`} onChange={setFarePct} hint={`Yield is measured; demand responds with elasticity ${ELASTICITY}.`} />
                <Toggle label="Lock fares — no price changes" checked={faresLocked} onChange={setFaresLocked} hint="On by default: the fare level above is YOURS. Claude's Explore candidates are all scored at it and cannot recommend a rise — gains must come from running the same service with fewer engines, turns and crews. It may still quantify what a fare change would be worth, clearly labelled, but never adopt one." />
                <SelectField label="Returns" value={returnMode} options={[{ value: 'latest', label: 'Last train home' }, { value: 'observed', label: 'Observed spread' }]} onChange={(v) => setReturnMode(v as 'latest' | 'observed')} hint="Default: everyone aims for the last train serving their journey, spilling earlier only as it fills." />
                <SubHead>TTI — on-train ticket sales</SubHead>
                <Toggle label="TTI modelling" checked={tti.enabled} onChange={(v) => setTti((t) => ({ ...t, enabled: v }))}
                  hint="Tickets the Travelling Ticket Inspectors sell on board never reach MERAC's departure sales. On, the Studio adds them back to every day's passengers and fares — on BOTH sides, because they travel today too — following the day's own sales curve and ticket split." />
                {tti.enabled && (
                  <>
                    <SelectField label="TTI basis" value={tti.mode}
                      options={[{ value: 'pct', label: '% of each day' }, { value: 'iplicit', label: 'iPlicit — unclassified ticket sales' }]}
                      onChange={(v) => setTti((t) => ({ ...t, mode: v === 'iplicit' ? 'iplicit' : 'pct' }))}
                      hint="% adds X% to every day's passengers and £, pro rata. iPlicit takes the month's unclassified ticket sales from the budget nominal ÷ the standard fare." />
                    {tti.mode === 'pct' ? (
                      <RangeField label="TTI uplift" value={tti.pct} min={0} max={30} step={1} prov="assumed" format={(v) => `+${v}%`}
                        onChange={(v) => setTti((t) => ({ ...t, pct: v }))}
                        hint="A 10,000-passenger day at 10% gains 1,000 passengers and its fares rise in proportion." />
                    ) : (
                      <label className="stu-field">
                        <span className="stu-field-label">iPlicit nominal</span>
                        <input type="text" value={tti.nominal} placeholder="from Simon" maxLength={40}
                          onChange={(e) => setTti((t) => ({ ...t, nominal: e.target.value }))} />
                        <span className="stu-note" style={{ color: 'var(--warn)' }}>Not wired yet — no uplift is applied until the nominal's monthly figures are linked.</span>
                      </label>
                    )}
                  </>
                )}
                <SubHead>Getting everyone home</SubHead>
                <RangeField label="Passengers a day allowed without a seat home" value={maxUnreturned} min={0} max={500} step={5} format={(v) => (v === 0 ? 'ZERO — everyone home' : `${v} a day`)} onChange={setMaxUnreturned} hint="Judged on the busiest day-type of the week. People who today travel on a train they cannot get home from are one-way already — they keep their sale and never count here. Nobody is moved onto a train they cannot get home from; they take an earlier train or the sale is lost. Only people left because every train home is full count, and the build adds a train home when they exceed this." />
                <SubHead>Behaviour</SubHead>
                <Toggle label="Max stay (last train back)" checked={maxStay} onChange={setMaxStay} hint="Everyone stays as long as possible; the minimum-stay slider is ignored." />
                <Toggle label="A return booking always comes home" checked={forceEarly} disabled={maxStay} onChange={setForceEarly} hint="The railway's rule, on by default: when every later train home is full, they board an earlier one and cut the visit short — a late outbound with a short stay is the passenger's own booking choice. Unreturned then means literally no seat on any train home." />
                <Toggle label="Place unmet demand on the nearest departure" checked={spillNearest} onChange={setSpillNearest} hint="Demand no train within the tolerance can take moves to the nearest earlier/later departure instead of being lost — discounted by the Recapture rate for every move, the first one included, and never onto a train they cannot get home from." />
                <Toggle label="Empty-stock (ECS) moves run diesel" checked={ecsDiesel} onChange={setEcsDiesel} hint="On by default: a positioning / light-engine move to reposition a rake runs as its own DIESEL turn, costed on its actual mileage, rather than waking or borrowing a steam engine. Turn off to let positioning moves share whatever engine repositions the stock." />
                <Toggle label="Correct demand for censoring" checked={censoring} onChange={setCensoring} prov="measured" hint="Un-truncates sold-out departures using low-load analogue days." />
                <Toggle label={`Compare last season${data?.prev_season ? ` (${data.prev_season})` : ''}`} checked={showPrev} disabled={!selection?.hasPrev} onChange={setShowPrev} hint={selection?.hasPrev ? 'Wireframe overlay on the demand chart.' : 'No prior-season data for this month.'} />
              </div>
            ),
          },
          {
            key: 'operating', label: 'Operating window',
            body: (
              <div className="stu-grid">
                <TimeField label="First train" value={firstTrain} onChange={(v) => { windowTouched.current = true; setFirstTrain(v) }} prov={wttWindow && firstTrain === wttWindow.first ? 'measured' : 'assumed'} hint={wttWindow ? `The real WTT's first departure is ${wttWindow.first} — that's the default. No working departs before this.` : 'No working departs before this — Best fit and rescue trains respect it.'} />
                <TimeField label="Last train" value={lastTrain} onChange={(v) => { windowTouched.current = true; setLastTrain(v) }} prov={wttWindow && lastTrain === wttWindow.last ? 'measured' : 'assumed'} hint={wttWindow ? `The real WTT's last arrival is ${wttWindow.last} — that's the default. Also decides who is genuinely one-way.` : "Also decides who is genuinely one-way: only 'overnight' if NO legal train inside the window could serve them."} />
              </div>
            ),
          },
          {
            key: 'interchange', label: 'Interchange', hidden: mode !== 'grosmont',
            body: (
              <div className="stu-grid">
                <RangeField label="Connection time at Grosmont" value={mctMin} min={10} max={60} step={5} prov="measured" format={(v) => `${v} min each way`} onChange={setMctMin} hint="30 minutes is the railway's figure for moving a full trainload between trains. Lowering it is an optimistic assumption." />
                <NumberField label="Platforms usable at once" value={platforms} min={1} max={4} prov="measured" onChange={setPlatforms} hint="Grosmont has two roads and both can be occupied for a transfer." />
                <RangeField label="Refuse to change trains" value={aversionPct} min={0} max={40} step={1} prov="assumed" format={(v) => `${v}%`} onChange={setAversionPct} hint="Share of through demand that simply won't make the change. The most uncertain number in the model." />
                <RangeField label="Interchange penalty" value={penaltyMin} min={0} max={30} step={1} prov="assumed" format={(v) => `${v} min equiv.`} onChange={setPenaltyMin} hint="Generalised-time cost of the change itself, on top of the wait." />
                <RangeField label="Wait weighting" value={waitWeight} min={1} max={3} step={0.1} prov="assumed" format={(v) => `×${v.toFixed(1)}`} onChange={setWaitWeight} hint="Waiting on a platform feels worse than sitting on a train. Rail norm 1.5–2.5." />
                <SubHead>The shuttle</SubHead>
                <NumberField label="Shuttle coaches" value={shuttleCoaches} min={0} max={8} prov="assumed" onChange={setShuttleCoaches} hint="0 = the Autocar railcar (48 seats). Otherwise 64 seats a coach." />
                <NumberField label="Shuttle turnaround at Whitby" value={shuttleTurn} min={5} max={40} unit="min" prov="assumed" onChange={setShuttleTurn} hint="A short unit turns far quicker than a full rake." />
                <NumberField label="Network Rail paths / day" value={nrPaths} min={0} max={40} prov={nrPaths > 0 ? 'assumed' : 'measured'} onChange={setNrPaths} hint={`0 = hold Mode B to what we run today (${observedPaths} traversals observed).`} />
                <Toggle label="Permit steam over Network Rail" checked={steamOverNR} onChange={setSteamOverNR} prov="assumed" hint="Off by default: diesel-only removes mainline steam certification and lineside fire risk." />
              </div>
            ),
          },
          {
            key: 'firecon', label: 'Firecon days',
            body: (() => {
              const nDays = Math.max(3, ...FIRECON_MONTHS.map((m) => firecon.days[String(m)] ?? 0))
              const red = (i: number) => firecon.reductions[Math.min(i, firecon.reductions.length - 1)] ?? 0
              const setRed = (i: number, v: number) => {
                const r = Array.from({ length: nDays }, (_, k) => red(k))
                r[i] = Math.min(100, Math.max(0, v))
                setFirecon({ ...firecon, reductions: r })
              }
              return (
                <div className="stu-grid">
                  <Toggle label="Steam stood down — Firecon days" checked={firecon.enabled} onChange={(v) => setFirecon({ ...firecon, enabled: v })}
                    hint="Fire risk: coal-fired steam is stood down and the timetable runs DIESEL on the days below (an oil-fired steam engine in the register may still run). Modelled side only — today's side is what the railway actually ran. The days are marked F on the Weekly Service Schedule, the board paper and the workbook, and the season table values them day for day. No rebuild needed." />
                  <SubHead>Consecutive non-steam days, by month</SubHead>
                  {FIRECON_MONTHS.flatMap((mNo) => [
                    <NumberField key={`fc-days-${mNo}`} label={`${MONTHS[mNo - 1]} — consecutive days`} value={firecon.days[String(mNo)] ?? 0} min={0} max={31} disabled={!firecon.enabled}
                      onChange={(v) => setFirecon({ ...firecon, days: { ...firecon.days, [String(mNo)]: Math.max(0, Math.floor(v)) } })}
                      hint={`Running days stood down, one after another. ${firecon.starts?.[String(mNo)] ? `From ${firecon.starts[String(mNo)]}.` : 'From the month\'s first running day unless you set a start.'}`} />,
                    <label key={`fc-start-${mNo}`} className="stu-field" style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
                      <span>{MONTHS[mNo - 1]} — starts on</span>
                      <input type="date" disabled={!firecon.enabled} value={firecon.starts?.[String(mNo)] ?? ''}
                        onChange={(e) => { const starts = { ...(firecon.starts ?? {}) }; if (e.target.value) starts[String(mNo)] = e.target.value; else delete starts[String(mNo)]; setFirecon({ ...firecon, starts }) }} />
                      <span className="stu-field-hint">Blank = the month's first running day. The run starts at the first running day on or after the date.</span>
                    </label>,
                  ])}
                  <SubHead>Sales against the previous year, by day of the stand-down</SubHead>
                  {Array.from({ length: nDays }, (_, i) => (
                    <NumberField key={`fc-red-${i}`} label={`Day ${i + 1} — % down`} value={red(i)} min={0} max={100} unit="%" disabled={!firecon.enabled}
                      onChange={(v) => setRed(i, v)}
                      hint={i === 0 ? 'Day one: normally 0 — the day trades as its like day did.' : i === nDays - 1 ? 'Any later day of a run takes this figure.' : 'Down on the like day\'s actual sales.'} />
                  ))}
                  <div style={{ gridColumn: '1 / -1' }} className="sh-card-note">
                    {firecon.enabled
                      ? `${fireconTotalDays(firecon)} Firecon day${fireconTotalDays(firecon) === 1 ? '' : 's'} across May–August. Every steam working turn on those days is designated diesel — the Scope's diesels must cover them; a turn no engine can take is reported. Save model settings as railway default to keep this for every session.`
                      : 'Off — every day runs as designed.'}
                  </div>
                </div>
              )
            })(),
          },
          {
            key: 'fleet', label: 'Fleet & sets',
            body: (
              <>
                <FleetPanel fleet={fleet} onChange={setFleet} costs={costs!} />
                <div className="sh-mt">
                  <Toggle
                    label="Lock the steam:diesel mix"
                    checked={tractionLocked}
                    onChange={setTractionLocked}
                    hint={`Steam is the railway's primary tourist draw. While locked, Claude's Explore candidates must keep exactly ${dieselLoco} diesel-worked diagram${dieselLoco === 1 ? '' : 's'} (the slider above) — it may quantify what more diesel would save, but it cannot recommend it or change your mix.`}
                  />
                </div>
                <div className="sh-mt"><DiagramsPanel net={net} deps={evalResult!.applied} dieselLoco={dieselLoco} fleet={fleet} /></div>
                <div className="sh-mt"><SetRoster net={net} sets={setsRoster} plan={plan} formation={formation} onFormation={setFormationChoice} onChange={setSetsRoster} onReset={() => setSetsRoster(modeSets(mode))} /></div>
              </>
            ),
          },
          {
            key: 'pullman', label: 'Pullman',
            body: (
              <>
                <p className="stu-note">
                  Pullman dining traffics THROUGH the designed timetable: its real paths are checked against the design
                  for crossings, listed in the timetables and the workbook, and each modelled date carries the covers and
                  takings of its like-for-like day in the sales-basis season. Pullman £ sits OUTSIDE the daily-services
                  P&amp;L (its own bookings), so it never inflates the timetable&apos;s revenue.
                </p>
                <Toggle
                  label="Daytime Running"
                  checked={pullmanDay}
                  onChange={setPullmanDay}
                  hint="Model Pullman dining departing before 17:00 (lunch services), wherever the basis season ran them."
                />
                <div className="sh-mt">
                  <Toggle
                    label="Night Time Running"
                    checked={pullmanNight}
                    onChange={setPullmanNight}
                    hint="Model evening Pullman dining (departures 17:00 and later). With Daytime OFF, evening Pullmans are modelled in June, July and August only."
                  />
                </div>
                <p className="stu-note sh-mt">
                  {pullmanDay && pullmanNight ? 'Both on — Pullman dining is modelled across the day and night, as the basis season ran it.'
                    : pullmanDay ? 'Daytime only — evening Pullmans are excluded from the model.'
                      : pullmanNight ? 'Night only — evening Pullman dining is modelled in June, July and August only.'
                        : 'Both off — no Pullman dining is modelled; the timetable listings and workbook will show none.'}
                  {pullmanErr ? ` ⚠ ${pullmanErr}` : ''}
                </p>
              </>
            ),
          },
        ]}
        actions={(() => {
          // Save sits in the header, left of Reset, and lights up after ANY
          // change on these tabs (Chris, 2026-10-28). It stores every model
          // setting — the operating window included — as the railway default.
          const dirty = savedSig == null || settingsSig() !== savedSig
          return (
            <>
              <Btn kind={dirty ? 'gold' : 'quiet'} small disabled={defaultsBusy || !dirty} onClick={() => void saveDefaults()}
                title="Store every model setting — demand, operating window, Firecon days, fleet & sets, Pullman — with the cost model as the railway default. Every session loads them; opening a slice never changes them.">
                {defaultsBusy ? 'Saving…' : dirty ? 'Save model settings' : '✓ Saved'}
              </Btn>
              <Btn kind="quiet" small onClick={() => { setRates(cloneCostModel(DEFAULT_COST_MODEL)); setFleet({ ...DEFAULT_FLEET }); setSetsRoster(modeSets(mode)) }}>Reset fleet &amp; sets</Btn>
            </>
          )
        })()}
      />
    </>
  )

  const renderBuild = () => (
    <>
      <div className="stu-bar">
        <span className="stu-bar-spacer" />
        {hasFine && (
          <span className="stu-note" title="School-holiday and term weekdays are modelled separately wherever the data splits them — this is data, not a setting.">
            term / school-holiday weekdays modelled separately
          </span>
        )}
      </div>
      {monthPills}
      {modelView === 'season' ? renderSeasonView() : (<>
      {/* A month's tabs (Chris, 2026-10-28): Financial first and the default —
          "an immediate sense of the financial changes and impact" — then the
          Weekly Service Schedule, the Working Turns, the Timetable. */}
      {subTabs([['fin', 'Financial'], ['wss', 'Weekly Service Schedule'], ['turns', 'Working Turns'], ['tt', 'Timetable']] as const, monthTab, setMonthTab)}
      {monthTab === 'fin' ? (
        // The month as BUILT, valued date by date — the same figures as its
        // SEASON row (one owner: yearTotals over this month's rows).
        <MonthFinancialPanel rows={yearRows.filter((r) => r.monthNo === monthNo)} monthLabel={monthLabel}
          scopeNote={wttObserved && wttObserved.excluded.length > 0
            ? `Daily services only — the WTT's ${wttObserved.excluded.join(' & ')} (Pullman dining) are a separate P&L and are not in these figures.`
            : undefined} />
      ) : monthTab === 'wss' ? renderSchedule('month') : monthTab === 'tt' ? renderSchedule('timetable') : (<>
      <div className="stu-actions">
        <span className="stu-note">The settings this model runs under — running mode, minimum profit per working and the Model settings — are on the <strong>Scope</strong> stage.</span>
        {/* The season build (★ Build Model) now lives on the Results
            bar (Chris, 2026-09-02) — MOVED off this stage so the loop is
            build → read the results, with no stage-hopping. The advisor stays a
            SEPARATE optional tool on the Explore stage; it does NOT run the
            season build or write the board paper. The "This month only" build
            below stays here as the quiet secondary for tuning a single month
            before committing the lot. */}
        <Btn disabled={baselineLoading || seasonDesigning} onClick={() => runBestFit()}
          title={baselineLoading ? 'Waiting for the real working timetable (path budget, operating window)…' : 'Rebuild only the month on screen, to try a setting before building the whole season'}>
          {`This month only${viewClass != null ? ` (${viewClass})` : ''}`}
        </Btn>
      </div>

      {fitLog?.some((l) => l.startsWith('⚠')) && (
        <Callout kind="error" title="Best fit finished with a problem it could not solve">
          {fitLog.filter((l) => l.startsWith('⚠')).map((l, i) => <div key={i}>{l}</div>)}
          {maxUnreturned > 0.5 && scenario!.unreturned > maxUnreturned + 0.5 && (
            <div className="sh-mt"><Btn kind="gold" onClick={() => { setMaxUnreturned(0); runBestFit(0) }}>Fix it: require everyone to get home and rebuild</Btn></div>
          )}
        </Callout>
      )}

      <Panel title="Demand by departure time" sub={`avg passengers per running ${period} · ${dayLabel}, ${monthLabel}${showPrev && selection!.hasPrev ? ` · wireframe = ${data?.prev_season}` : ''}`} collapsible>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={demandChart} margin={{ top: 4, right: 12, bottom: 0, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
            <XAxis dataKey="time" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={36} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Northbound" name="Northbound (up the line)" fill="#0b3d2e" radius={[2, 2, 0, 0]} />
            <Bar dataKey="Southbound" name="Southbound (return)" fill="#c89b3c" radius={[2, 2, 0, 0]} />
            {showPrev && selection!.hasPrev && (
              <>
                <Bar dataKey="N last season" fill="rgba(0,0,0,0)" stroke="#0b3d2e" strokeWidth={1.5} strokeDasharray="4 2" radius={[2, 2, 0, 0]} />
                <Bar dataKey="S last season" fill="rgba(0,0,0,0)" stroke="#c89b3c" strokeWidth={1.5} strokeDasharray="4 2" radius={[2, 2, 0, 0]} />
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      {isWeek && (
        <div className="stu-bar">
          <span className="stu-dow">
            <button type="button" aria-pressed={classView == null} onClick={() => setClassView(null)}>Full week</button>
            {selection!.classes.map((c) => (
              <button key={c.cls} type="button" aria-pressed={classView === c.cls} className={dayOverrides[c.cls] ? 'stu-dow--custom' : undefined} title={`${CLASS_LABELS[c.cls] ?? c.cls} — ${c.weight.toFixed(1)} days/week`} onClick={() => setClassView(c.cls)}>{c.cls}{dayOverrides[c.cls] ? ' •' : ''}</button>
            ))}
          </span>
          {classView != null && (
            <>
              <Btn kind="gold" small disabled={!draftDirty} onClick={() => { if (dayDraft) { setDayOverrides((p) => ({ ...p, [classView]: dayDraft.map((d) => ({ ...d })) })); setDraftDirty(false) } }}>Save {classView}</Btn>
              {draftDirty && <Btn kind="quiet" small onClick={() => { setDayDraft((dayOverrides[classView] ?? deps).map((d) => ({ ...d }))); setDraftDirty(false) }}>Discard</Btn>}
              {dayOverrides[classView] && <Btn kind="ghost" small onClick={() => { setDayOverrides((p) => { const n = { ...p }; delete n[classView]; return n }); setDayDraft(deps.map((d) => ({ ...d }))); setDraftDirty(false) }}>Use the shared weekly timetable</Btn>}
            </>
          )}
          {classView == null && overriddenClasses.length > 0 && <span className="stu-note" style={{ color: 'var(--warn)' }}>Customised: {overriddenClasses.join(', ')}.</span>}
        </div>
      )}

      <Panel
        title="Working turns"
        sub={turnsView === 'current'
          ? 'what the railway runs today — the real working timetable'
          : classView != null ? CLASS_LABELS[classView] ?? classView
          : bankedView?.label ? `the built timetable — ${bankedView.label}` : 'the timetable for the month on screen'}
        actions={
          <>
            <PillGroup
              value={turnsView}
              title="Compare the timetable you have built against the one the railway runs today."
              options={[
                { value: 'modelled', label: 'New', title: 'The timetable built by the modelling for the month on screen.' },
                { value: 'current', label: 'Current', title: "What the railway runs today — thebigplan's real working timetable, read only." },
              ]}
              onChange={(v) => setTurnsView(v as 'modelled' | 'current')}
            />
            {turnsView === 'modelled' && <>
            <input type="time" value={addTime} onChange={(e) => setAddTime(e.target.value)} />
            <select value={addO} onChange={(e) => setAddO(Number(e.target.value))}>{net.stations.map((s, i) => <option key={s.code} value={i}>{s.name}</option>)}</select>
            <span className="stu-note">→</span>
            <select value={addD} onChange={(e) => setAddD(Number(e.target.value))}>{net.stations.map((s, i) => <option key={s.code} value={i}>{s.name}</option>)}</select>
            <Btn kind="primary" small onClick={() => {
              const min = toMin(addTime)
              if (!Number.isFinite(min) || addO === addD) return
              const dir = addD > addO ? 'Northbound' : 'Southbound'
              updateActive((prev) => [...prev, { id: `${dir}-${addTime}-x${prev.length}`, direction: dir, time: addTime, minutes: min, enabled: true, coaches: 7, o: addO, d: addD }].sort((a, b) => a.minutes - b.minutes))
            }}>Add working</Btn>
            </>}
          </>
        }
      >
        {turnsView === 'current' ? (
          <CurrentTurns net={net0} deps={observedDeps} />
        ) : (<>
        {bankedView && bankedView.empty.length > 0 && (
          <Callout kind="warn" title={`${bankedView.empty.length} timetable${bankedView.empty.length === 1 ? '' : 's'} in this month built no train that pays`}>
            <div>{bankedView.empty.join(' · ')}</div>
            <div className="sh-mt">
              Under the current minimum of {gbp(minRevPerDep)} profit per working, nothing on {bankedView.empty.length === 1 ? 'that timetable' : 'those timetables'} covers
              the extra cost of running it. That is a real finding for a shoulder month, not a failure — but it is a judgement:
              lower the minimum on the Build bar and rebuild if the railway would run those days anyway.
              {bankedView.label ? ` The turns below are the ${bankedView.label} design.` : ''}
            </div>
          </Callout>
        )}
        {pickEngine && seasonRoster && (
          <Modal title={`Choose the engine for the ${pickEngine.label} turn`} width={760} onClose={() => setPickEngine(null)}
            note={`The engine you choose takes this turn from ${liveMonthDates[0] ?? 'this month'} on, for as long as it is available — its washout weeks, exams and certificate still stand, and on those dates the roster's order fills in. The season's miles and costs re-flow at once. Whitby turns still need a Whitby-enabled engine; a coal engine cannot take a Firecon day.`}>
            <div className="stu-scroll">
              <table className="stu-table">
                <thead><tr><th>Engine</th><th>Traction</th><th className="num" title="Running days of this month the engine is available">Available this month</th><th className="num">Minimum</th><th className="num">Miles rostered</th><th>Whitby</th><th /></tr></thead>
                <tbody>
                  {(fleet.locos ?? []).filter((l) => l.inSimulation && l.traction !== 'railcar').map((l) => {
                    const avail = liveMonthDates.filter((dt) => !engineOutOn(l, dt)).length
                    const e = seasonRoster.engines[String(l.id)]
                    const pinnedHere = enginePins.some((p) => p.turnKey === pickEngine.turnKey && p.engineId === l.id)
                    return (
                      <tr key={l.id} style={avail === 0 ? { opacity: 0.5 } : undefined}>
                        <td><strong>{engineLabel(l)}</strong>{pinnedHere && <span className="sh-dim"> ◆ pinned</span>}</td>
                        <td className="sh-dim">{l.traction}{l.fuel === 'oil' ? ' · oil' : ''}</td>
                        <td className="num sh-mono">{avail} of {liveMonthDates.length}</td>
                        <td className="num sh-mono">{l.minContractMiles ? l.minContractMiles.toLocaleString('en-GB') : '—'}</td>
                        <td className="num sh-mono">{e ? Math.round(e.miles).toLocaleString('en-GB') : '—'}</td>
                        <td>{l.whitbyEnabled ? 'yes' : '—'}</td>
                        <td><Btn kind="gold" small disabled={avail === 0} onClick={() => {
                          const from = liveMonthDates[0]
                          if (!from) return
                          setEnginePins((prev) => [...prev.filter((p) => !(p.turnKey === pickEngine.turnKey && p.fromDate === from)), { turnKey: pickEngine.turnKey, engineId: l.id, fromDate: from }])
                          setPickEngine(null)
                        }}>Use from {liveMonthDates[0] ?? '—'}</Btn></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="sh-mt" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Btn kind="quiet" small disabled={!enginePins.some((p) => p.turnKey === pickEngine.turnKey)} onClick={() => { setEnginePins((prev) => prev.filter((p) => p.turnKey !== pickEngine.turnKey)); setPickEngine(null) }}>Clear the pins on this turn — back to the roster&apos;s order</Btn>
              <span className="sh-card-note">Pins are kept with this session (Save in the header).</span>
            </div>
          </Modal>
        )}
        <DepEditor
          net={net} deps={activeDeps} scenario={scenario!} plan={plan} sets={setsRoster} locoBy={rosterLocoBy ?? locoBy} chainNext={chainNext}
          onPickEngine={rosterLocoBy ? (id) => {
            const enabled = activeDeps.filter((d) => d.enabled && Number.isFinite(d.minutes))
            const byId = new Map(enabled.map((d) => [d.id, d]))
            const g = estimateDiagrams(net, enabled).find((x) => x.legIds.includes(id))
            const first = g ? byId.get(g.legIds[0]) : undefined
            if (!first) return
            setPickEngine({ depId: id, turnKey: turnKeyOf(net, liveColour, first), label: `${fmtMin(first.minutes)} ${net.stations[first.o]?.name ?? ''} → ${net.stations[first.d]?.name ?? ''}` })
          } : undefined}
          onToggle={(d) => {
            if (d.enabled) {
              const chain: string[] = []
              let cur: string | null | undefined = chainNext[d.id]
              while (cur) { chain.push(cur); cur = chainNext[cur] }
              if (chain.length > 0) { setPendingDisable({ id: d.id, chain }); return }
            }
            updateActive((prev) => prev.map((x) => (x.id === d.id ? { ...x, enabled: !x.enabled } : x)))
          }}
          onEdit={(d) => setEditDep({ id: d.id, time: d.time, o: d.o, d: d.d })}
          onPinSet={(id, code) => updateActive((prev) => prev.map((x) => (x.id === id ? { ...x, setPin: code } : x)))}
          onCycleTraction={(id) => updateActive((prev) => prev.map((x) => (x.id === id ? { ...x, tractionPin: x.tractionPin === undefined ? 'diesel' : x.tractionPin === 'diesel' ? 'steam' : undefined } : x)))}
          onOpenService={setSvcOpen}
          onAddBalancing={(d, X) => {
            if (!Number.isFinite(X) || X === d.d) return
            const arr = d.minutes + journeyMin(net, d.o, d.d)
            const depMin = Math.ceil((arr + turnaroundAt(net, d.d)) / 5) * 5
            const dir = X > d.d ? 'Northbound' : 'Southbound'
            const t = fmtMin(depMin)
            updateActive((prev) => [...prev, { id: `bal-${dir}-${t}-${d.d}-${X}-${prev.length}`, direction: dir, time: t, minutes: depMin, enabled: true, coaches: 7, o: d.d, d: X }].sort((a, b) => a.minutes - b.minutes))
          }}
        />
        </>)}
      </Panel>
      {fitLog && fitLog.length > 0 && (
        <Panel title="★ Best fit — optimised for profit" collapsible defaultOpen={false}><ul className="stu-log">{fitLog.map((l, i) => <li key={i}>{l}</li>)}</ul></Panel>
      )}

      <Panel title="Diagnostics" sub="why a timetable is or isn't operable — the disqualification detail" badge={fatalCount + imbalances.length + (costs?.overCap.length ?? 0) + paths.filter((p) => p.over).length} collapsible defaultOpen={false}>
        <DiagnosticsPanel net={net} conflicts={conflicts} imbalances={imbalances} paths={paths} transfers={scenario!.transfers} costs={costs!} fitLog={null} lostOutbound={scenario!.lostOutbound} period={period} />
      </Panel>
      </>)}
      </>)}
    </>
  )

  /** The Modelling stage's SEASON view — the default (Chris, 2026-10-28). */
  const renderSeasonView = () => (
    <>
      {!seasonIsBuilt && (
        <Callout kind="warn" title="The season has not been built yet">
          Press <strong>★ Build Model</strong> at the end of the stage bar — every month is built and valued, and the SEASON and month pills turn green.
        </Callout>
      )}
      {seasonRunLog.length > 0 && (
        <Panel title="Build log — every month, day by day" collapsible defaultOpen={false}>
          <ul className="stu-log">{seasonRunLog.map((l, i) => <li key={i}>{l}</li>)}</ul>
        </Panel>
      )}
      {subTabs([['fin', 'Financial'], ['tt', 'Timetable']] as const, seasonTab, setSeasonTab)}
      {seasonTab === 'fin'
        ? renderSchedule('season')
        : (
          <SeasonPlanPanel plan={seasonPlan} detailFor={seasonPlanDetail} splitFor={seasonSplitFor}
            setCodes={setsRoster.filter((x) => x.active).map((x) => x.code)}
            applyEdit={applySeasonEdit} />
        )}
    </>
  )

  /** The Weekly Service Schedule, one part at a time: the season roll-up, the
   *  month's schedule, or the month's working timetable (Modelling sub-tabs,
   *  Chris, 2026-10-28). The per-day Optimised/Unoptimised calendar with the
   *  ticket-price panel and the thebigplan-format WTT + Excel export; the
   *  modelling SESSION (slices + versions) is the record. */
  const renderSchedule = (part: 'season' | 'month' | 'timetable') => (
    <>
        {data && monthKey && (
          <div className="sh-mt">
            <ResearchSchedule
              data={scheduleSales?.data ?? data} salesYear={scheduleSales?.year} monthKey={monthKey} dayClass={dayClass === 'WkdyH' || dayClass === 'WkdyT' ? 'Wkdy' : dayClass}
              deps={deps}
              dayOverrides={isWeek ? Object.fromEntries(Object.entries(dayOverrides).flatMap(([cls, d]) => (CLASS_DOWS[cls] ?? []).map((dow) => [dow, d]))) : {}}
              sets={setsRoster} objective={objective} returnMode={returnMode} maxUnreturned={maxUnreturned} engineOpts={params.opts}
              tolerance={tolerance} recapture={recapturePct / 100} fareMult={farePct / 100} dwellMin={dwellMin} rates={rates} dieselLoco={dieselLoco} net={net} fleet={fleet}
              baselineTimetable={wttObserved?.deps}
              dayCalendar={monthCalendarForSchedule}
              salesBasis={wssOverlay?.basis}
              dateActuals={wssOverlay?.dateActuals}
              oneWayToday={wssOverlay?.oneWayToday}
              pullmanByDate={wssOverlay?.pullmanByDate}
              dayFixedShareFor={pnlCost ? (side) => pnlDayFixed(side, monthNo) : undefined}
              dayVariableFor={pnlCost ? pnlDayVar : undefined}
              yearRows={yearRows}
              seasonLabel={data.season}
              part={part}
            />
          </div>
        )}
    </>
  )

  const renderExplore = () => {
    const bestId = advisorLog?.recommendation?.candidateId ?? advisorLog?.bestCandidateId ?? null
    const bestRes = advisorLog ? findResult(advisorLog, bestId) : null
    const best = bestId && bestRes
      ? { id: bestId, contribLabel: gbp(bestRes.contribution), sub: `${bestId} · ${bestRes.mode} · ${num0.format(Math.round(bestRes.unreturned))} unreturned` }
      : null
    return (
      <ExploreChat
        log={advisorLog} running={advisorRunning}
        steer={userSteer} onSteer={setUserSteer}
        onRun={() => void startAdvisor()} onStop={stopAdvisor}
        onApply={applyCandidate}
        narrative={narrative} onNarrative={setNarrative}
        best={best}
      />
    )
  }

  const renderResults = () => {
    const err = backtest ? Math.max(0.05, backtest.mape) : 0.15
    const lo = extraContrib * (1 - err), hi = extraContrib * (1 + err)
    return (
      <>
        <div className="sh-rtabs">
          {([['money', 'Financial Impact'], ['ops', 'Operational Risk & Reliability'], ['case', 'The case in one picture'], ['appx', 'Appendices'], ['edit', 'Edit PDF Summaries']] as const).map(([k, l]) => (
            <button key={k} type="button" className={`sh-rtab${resultTab === k ? ' sh-rtab--active' : ''}`} onClick={() => setResultTab(k)}>{l}</button>
          ))}
          <label className="sh-rtab-report" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 8, fontSize: '0.8rem' }} title="Report A is the full board paper (season table, Costs & Modelling, per-month appendix). Report B is the original.">
            <span className="sh-dim">Report</span>
            <select value={reportVariant} onChange={(e) => setReportVariant(e.target.value as 'A' | 'B')} style={{ font: 'inherit', padding: '2px 6px', borderRadius: 5, border: '1px solid #ddd6c4' }}>
              <option value="A">A — full board paper</option>
              <option value="B">B — original</option>
            </select>
          </label>
          {(() => {
            // Export is possible when a SEASON is built (banked slices), or the
            // on-screen slice has a design. After a season build the live grid
            // for the viewed month is often empty — gating on it alone left the
            // button dead even with a full banked season (Chris, 2026-08-27).
            const canExport = slices.length > 0 || servicesNow > 0
            return (
              <>
                <button type="button" className="sh-rtab-export" style={{ marginRight: 8 }} onClick={() => void exportWorkbook()} disabled={xlsxBusy || !canExport}
                  title={!canExport ? 'Build the season before exporting the workbook.' : 'Season workbook: Summary, each month’s Weekly Service Schedule day by day, and each month’s timetables — optimised and unoptimised.'}>
                  {xlsxBusy ? 'Building…' : 'Export workbook (Excel) →'}
                </button>
                <button type="button" className="sh-rtab-export" onClick={exportExecReport} disabled={pdfBusy || !canExport}
                  title={!canExport ? 'Build the season (or design a month) before exporting a board paper.' : undefined}>
                  {pdfBusy ? 'Building…' : !canExport ? 'Build a season to export' : `Export Report ${reportVariant} (PDF) →`}
                </button>
              </>
            )
          })()}
        </div>

        {resultTab === 'money' && (() => {
          // ONE owner of "is there a designed season": the banked slices — the
          // same rows the season table and the board paper read. The live
          // scratchpad (servicesNow) is only the fallback when nothing is
          // banked. Gating this banner on the live slice printed "No timetable
          // designed" over a fully built season (Chris, 2026-09-02).
          // ONE owner of "is there a built season": the banked slices — the same
          // rows the season table and the board paper read. Every built month is
          // a designed month; where the Brief bound is a LIST (the slices'
          // issues), never a second status that reads as unfinished (Chris,
          // 2026-09-04: "to a human this message says… it's only half finished").
          const builtN = slices.filter((s) => builtSlice(s)).length
          const boundPts = reviewMonths.reduce((t, m) => t + m.issues.length, 0)
          // Months banked by an earlier version carry no issue list yet — say so
          // rather than read as inside the Brief (law 8: never silently favourable).
          const staleN = slices.filter((s) => builtSlice(s) && s.mode === mode && !briefChecked(s)).length
          const tot = yearTotals(yearRows)
          const seasonBuilt = builtN > 0 && !!tot.mod
          const seasonChange = seasonBuilt && tot.cur && tot.mod ? tot.mod.profit - tot.cur.profit : null
          return (
            <>
            <section className="sh-card sh-card--pad">
              <div className="sh-section-label">Financial &amp; Operating Summary · {data?.season}</div>
              {/* The headline, big (Chris, 2026-10-28: "make the headline figure
                  more prominent") — the same season figure as the table. */}
              {seasonBuilt && seasonChange != null && tot.cur && tot.mod && (
                <div className="sh-fin-headline">
                  {ttiLabel(tti) && <div className="sh-prov sh-prov--assumed" style={{ width: '100%' }}>{ttiLabel(tti)}</div>}
                  <div className={`sh-fin-headline-value ${seasonChange >= 0 ? 'is-good' : 'is-bad'}`}>{seasonChange >= 0 ? '+' : '−'}{gbp(Math.abs(seasonChange))}</div>
                  <div className="sh-fin-headline-sub">
                    change in season contribution{pnlCost ? ' (accounts basis)' : ''} · <strong>{gbp(tot.cur.profit)}</strong> today → <strong>{gbp(tot.mod.profit)}</strong> modelled · {tot.runningDays} running days
                  </div>
                </div>
              )}
              <p className="sh-question">{seasonBuilt
                ? `The season is modelled: ${builtN} month${builtN === 1 ? '' : 's'} built and valued, every figure computed from the build. The board paper prints the same numbers.`
                : servicesNow === 0
                  ? 'No timetable designed — there is nothing to recommend or conclude. Press ★ Build Model on the stage bar, or run Best fit and read its log.'
                  : (extraContrib >= 0 ? `The modelled ${modeLabel.toLowerCase()} timetable improves contribution against the timetable as drawn.` : 'The modelled timetable does not beat the timetable as drawn — keep searching.')}</p>
              <div className="sh-mt sh-chips">
                {seasonBuilt
                  ? (boundPts === 0
                    ? (staleN > 0
                      ? <span className="sh-prov sh-prov--assumed">{staleN} month{staleN === 1 ? '' : 's'} saved without a check against the Scope (a live design saved by hand, or built by an earlier version) — press ★ Build Model</span>
                      : <span className="sh-tag-good">✓ season built — every month inside the Scope</span>)
                    : <>
                      <span className="sh-prov sh-prov--assumed">the Scope bound on {boundPts} point{boundPts === 1 ? '' : 's'} in {reviewMonths.map((m) => m.monthLabel).join(', ')} — settle them under ⚠ Interventions on the stage bar</span>
                    </>)
                  : sliceQualifies
                    ? <span className="sh-tag-good">✓ operable — can be recommended</span>
                    : <span className="sh-prov sh-prov--assumed">not yet a recommendation — {costLocked ? '' : 'lock the cost model; '}{appliedDays == null ? 'apply a running set; ' : ''}{designClean ? '' : 'resolve the disqualifiers in Diagnostics'}</span>}
                {seasonBuilt && seasonChange != null && tot.cur && tot.mod ? (
                  <span className="stu-note">Season contribution{pnlCost ? ' (accounts basis)' : ''}: <strong>{gbp(tot.cur.profit)}</strong> today → <strong>{gbp(tot.mod.profit)}</strong> modelled, a change of <strong>{seasonChange >= 0 ? '+' : ''}{gbp(seasonChange)}</strong> over {tot.runningDays} running days.</span>
                ) : (
                  <span className="stu-note">Headline quoted as a range: <strong>{gbp(Math.min(lo, hi))} to {gbp(Math.max(lo, hi))} / {period}</strong> (±{Math.round(err * 100)}% calibration).</span>
                )}
                {/* Turned away by timing (Chris, 2026-09-07) — the same season figure
                    the season table, the workbook Summary and the board paper print. */}
                {seasonBuilt && tot.runningDays > 0 && tot.mod?.turnedAway != null && tot.cur?.turnedAway != null ? (
                  <span className="stu-note"><strong>{num0.format(Math.round(tot.mod.turnedAway / tot.runningDays))}</strong> passengers a day turned away by timing, against <strong>{num0.format(Math.round(tot.cur.turnedAway / tot.runningDays))}</strong> today — people in the like day&apos;s demand with no train within 75 minutes of their time once the Recapture discount applies, or whose nearest train had no way home. They are not in the passengers carried.</span>
                ) : seasonBuilt ? (
                  <span className="stu-note">Turned away by timing: not yet computed for every month — press ★ Build Model.</span>
                ) : null}
              </div>
            </section>

            {/* The season in four boxes, TODAY and MODELLED in each (Chris,
                2026-10-28) — the same rows as the season table and the
                headline (yearTotals), never the month on screen. */}
            {tot.cur && (
              <div className="sh-fin-boxes">
                {([
                  { label: 'Revenue', a: tot.cur.revenue, b: tot.mod?.revenue, fmt: gbp, upGood: true },
                  { label: 'Costs', a: tot.cur.costs, b: tot.mod?.costs, fmt: gbp, upGood: false },
                  { label: 'Contribution', a: tot.cur.profit, b: tot.mod?.profit, fmt: gbp, upGood: true },
                  { label: 'Passengers carried', a: tot.cur.carried, b: tot.mod?.carried, fmt: (n: number) => num0.format(Math.round(n)), upGood: true },
                ] as const).map((x) => {
                  const d = x.b != null ? x.b - x.a : null
                  const good = d != null && (x.upGood ? d >= 0 : d <= 0)
                  return (
                    <div key={x.label} className="sh-fin-box">
                      <div className="sh-fin-box-label">{x.label} · season</div>
                      <div className="sh-fin-box-row"><span>Today</span><strong className="sh-mono">{x.fmt(x.a)}</strong></div>
                      <div className="sh-fin-box-row sh-fin-box-row--mod"><span>Modelled</span><strong className="sh-mono">{x.b != null ? x.fmt(x.b) : '—'}</strong></div>
                      <div className={`sh-fin-box-delta${d == null ? '' : good ? ' is-good' : ' is-bad'}`}>
                        {d == null ? 'not built yet' : `${d > 0 ? '+' : d < 0 ? '−' : ''}${x.fmt(Math.abs(d))} modelled vs today`}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            </>
          )
        })()}

        {resultTab === 'edit' && (
          // Edit PDF Summaries (Chris, 2026-10-28): the two editable pages —
          // What's changed and the planner's notes — on one page with a Save
          // button, so it is clear what is kept.
          <section className="sh-card sh-card--pad">
            <div className="sh-card-title">Edit PDF summaries</div>
            <div className="sh-mt" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Btn kind="gold" disabled={sessionBusy || !data} onClick={() => void saveSession()}>{sessionBusy ? 'Saving…' : 'Save'}</Btn>
              <span className="stu-note">Saves both with the session ({savedAt ?? 'not saved yet'}).</span>
            </div>
            <div className="sh-section-label sh-mt">What&apos;s changed — original vs new, in plain terms</div>
            <p className="stu-note sh-mt">
              Optional executive bullets for the board paper (one per line). Leave this blank and the report
              derives its own plain-English &quot;what&apos;s changed&quot; from the season build — the mechanical
              differences below update live and are a starting point you can copy in. They print on the
              recommendation page of the PDF.
            </p>
            <div className="sh-mt">
              <div className="sh-section-label">What&apos;s changed — optional manual notes (blank = the report writes its own)</div>
              <textarea
                className="sh-save-input" style={{ minHeight: 160, fontFamily: 'inherit' }}
                value={whatsChanged}
                placeholder={'Optional — one executive bullet per line. Leave blank and the board paper uses the season build\'s own summary; the mechanical differences below are a starting point.'}
                onChange={(e) => setWhatsChanged(e.target.value)}
              />
            </div>
            {!whatsChanged && evalResult && (
              <div className="sh-mt">
                <div className="sh-section-label">The mechanical differences, meanwhile</div>
                <ul className="stu-log">
                  {(() => {
                    const curSvcN = observedDeps.filter((d) => d.enabled && !d.positioning).length
                    const modSvcN = servicesNow
                    const items: string[] = []
                    if (modSvcN !== curSvcN) items.push(`Passenger workings per day: ${curSvcN} today → ${modSvcN} modelled.`)
                    if (costs && baseline) items.push(`Engines: ${baseline.steamN} steam + ${baseline.dieselN} diesel/railcar today → ${costs.steamN} steam + ${costs.dieselN + costs.railcars} modelled.`)
                    if (cardCtx) items.push(`Contribution${pnlCost ? ' (accounts basis)' : ''}: ${gbpK(todayContrib)}/${period} today → ${gbpK(proposedContrib)}/${period} modelled.`)
                    return items.map((x, i) => <li key={i}>{x}</li>)
                  })()}
                </ul>
              </div>
            )}
            <div className="sh-mt">
              <div className="sh-section-label">Planner&apos;s notes — kept with this session, never printed in the board paper</div>
              <textarea className="sh-save-input" style={{ minHeight: 140, fontFamily: 'inherit' }} value={narrative} placeholder="Your own working notes on this season. They save with the session and stay here — the board paper prints computed figures only, never free-form conclusions." onChange={(e) => setNarrative(e.target.value)} />
            </div>
          </section>
        )}

        {resultTab === 'case' && (
          <>
            {/* NOW (modelled) and BEFORE (current), one under the other —
                same demand, each with ITS timetable's seats (Chris, 2026-08-20). */}
            {([
              { label: 'Now — the modelled timetable', deps: evalResult?.applied ?? [], accent: '#0b3d2e' },
              { label: 'Before — the current timetable', deps: observedDeps, accent: '#6b6b6b' },
            ] as const).map((side) => {
              const seatsAt = (dir: string, t: string) => Math.round(
                side.deps.filter((d) => d.enabled && !d.positioning && d.direction === dir && d.time === t)
                  .reduce((x, d) => x + depSeats(d), 0))
              const times = [...new Set([...demandChart.map((r) => r.time), ...side.deps.filter((d) => d.enabled && !d.positioning).map((d) => d.time)])]
                .sort((a, b) => toMin(a) - toMin(b))
              const rows = times.map((t) => {
                const base = demandChart.find((r) => r.time === t)
                return {
                  time: t,
                  Northbound: base?.Northbound ?? 0,
                  Southbound: base?.Southbound ?? 0,
                  'Seats N': seatsAt('Northbound', t),
                  'Seats S': seatsAt('Southbound', t),
                }
              })
              return (
                <section key={side.label} className="sh-card sh-card--pad sh-mt">
                  <div className="sh-card-title">{side.label} — demand vs seats, by time of day</div>
                  <p className="stu-note sh-mt">Wanted journeys (solid) against the seats this timetable provides (outlined), both directions.</p>
                  <ResponsiveContainer width="100%" height={210}>
                    <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                      <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={36} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Northbound" fill="#0b3d2e" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Southbound" fill="#c89b3c" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Seats N" fill="#9fc4b5" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Seats S" fill="#e6d4a3" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </section>
              )
            })}
          </>
        )}

        {resultTab === 'money' && (
          <div className="sh-mt" />
        )}
        {resultTab === 'money' && (
          // The rate-card cost lines of the MONTH on screen — labelled as such,
          // so it never reads as the season (its per-week cards are gone).
          <section className="sh-card sh-card--pad">
            <div className="sh-section-label">Rate-card cost lines · {monthLabel} (the month on screen) · per running day</div>
            <CostPanel hideCards costs={costs!} rates={rates} periodCosts={periodCosts} periodRevenue={scenario!.revenue + upliftNow} period={period} runningDays={isWeek && classView == null ? runningDays : 1} />
          </section>
        )}

        {resultTab === 'ops' && (
          <>
            <DiagnosticsPanel net={net} conflicts={conflicts} imbalances={imbalances} paths={paths} transfers={scenario!.transfers} costs={costs!} fitLog={null} lostOutbound={scenario!.lostOutbound} period={period} />
            <Callout kind={sliceQualifies ? 'ok' : 'warn'} title="Quality checks — what this box is">
              Before a design may be put forward, the Studio checks it could genuinely be operated: no non-workable
              crossing (two trains meeting where the line has no loop to pass), every carriage set starting and ending
              somewhere it can stable overnight, no more engines than the fleet owns, no more Network Rail paths than we
              run today, and every passenger able to get home. {sliceQualifies
                ? 'This design passes every check — it can go in the board paper.'
                : 'This design does not yet pass — the diagnostics above name exactly what to fix.'}
            </Callout>
          </>
        )}

        {resultTab === 'appx' && data && (
          <>
            <BacktestPanel result={backtest} busy={backtestBusy} season={data.prev_season ?? data.season} onRun={() => runCalibration(false)} />
            <Callout kind="ok" title="Weekly Service Schedule has moved">
              The per-day Optimised/Unoptimised calendar and the ticket-price panel are on the <strong>Modelling</strong> stage —
              pick a month, tab <strong>Weekly Service Schedule</strong>; its thebigplan-format working timetable is the
              <strong> Timetable</strong> tab, and the season roll-up is the <strong>SEASON</strong> pill.
            </Callout>
          </>
        )}
      </>
    )
  }

  /** A stage's sub-tab bar (Timetable, Cost model, a Modelling month). */
  const subTabs = <K extends string>(tabs: readonly (readonly [K, string])[], on: K, set: (k: K) => void) => (
    <div className="sh-subtabs" role="tablist">
      {tabs.map(([k, l]) => (
        <button key={k} type="button" role="tab" aria-selected={on === k}
          className={`sh-subtab${on === k ? ' sh-subtab--active' : ''}`} onClick={() => set(k)}>{l}</button>
      ))}
    </div>
  )

  const calendarBody = data ? (
    <>
      <CalendarStage
        season={data.season} dayTypes={data.day_types ?? []} days={baselineWtt?.days}
        monthKeys={monthOptions.map((o) => o.value)} modelledNotRun={modelledNotRun}
        initialMonth={monthKey} savedSets={savedSetRows} busy={runningBusy}
        appliedDays={appliedDays} appliedSetId={runningSetId} appliedSetName={runningSetName}
        onSaveSet={saveRunningSet} onApplySet={applyRunningSet} onDeleteSet={removeRunningSet}
        onSetDefault={setDefaultSet}
      />
      {/* The Season programme lives HERE now (Chris, 2026-08-20) — it is
          calendar-shaped work (which month×day-type slices make the season
          case), not something to hold rail space on every stage. */}
      <div className="sh-mt" style={{ maxWidth: 420 }}>
        <ProgrammeNav
          seasonTag={data.season}
          slices={programmeRows}
          onSelect={selectSliceRow}
          onAddSlice={() => void saveSession()}
          addLabel={slices.some((s) => s.month_no === monthNo && s.day_class === currentDc && s.mode === mode) ? 'Update this slice from the current design' : '+ Add this month / day-type to the programme'}
          addDisabled={!dataReady}
          rollup={{ value: seasonProfitPnl != null && rollup.designedCount > 0 ? `${seasonProfitPnl >= 0 ? '+' : ''}${gbpK(seasonProfitPnl)}` : rollupValue, note: rollupNote, partial: rollup.partial }}
        />
      </div>
    </>
  ) : notReady

  const baselineBody = dataReady ? (
    <BaselineStage
      net={net0} deps={observedDeps} wtt={baselineWtt}
      onPickPattern={(code) => void refreshBaseline(code)}
      onTuneDep={(id, hhmm) => {
        // Tunes the DESIGN copy only — the baseline stays the real WTT.
        const mins = toMin(hhmm)
        if (!Number.isFinite(mins) || mins <= 0) return
        depsTouched.current = true
        setDeps((prev) => prev.map((d) => {
          if (d.id !== id) return d
          const delta = mins - d.minutes
          return { ...d, time: hhmm, minutes: mins, arrMin: d.arrMin != null ? d.arrMin + delta : undefined }
        }))
      }}
    />
  ) : notReady

  const stageContent =
    stage === 'brief' ? (
      <BriefStage
        seasons={data?.seasons ?? (data ? [data.season] : [])}
        season={chosenSeason ?? data?.season ?? ''}
        onSeasonChange={(s) => setChosenSeason(s)}
        periodMode={periodMode}
        onPeriodModeChange={setPeriodMode}
        rangeFrom={rangeFrom}
        rangeTo={rangeTo}
        onRangeFromChange={setRangeFrom}
        onRangeToChange={setRangeTo}
        engineMode={engineMode}
        onEngineModeChange={setEngineMode}
        monthSourceRows={monthSourceRows}
        sourceSeasons={(data?.seasons ?? []).filter((s) => s !== data?.season)}
        onMonthSourceChange={(monthKey, season) => setMonthSources((prev) => {
          const by = { ...prev.by }
          if (season) by[monthKey] = season; else delete by[monthKey]
          return { ...prev, by }
        })}
        sourceReason={monthSources.reason}
        onSourceReasonChange={(v) => setMonthSources((prev) => ({ ...prev, reason: v }))}
        disruptedSave={
          <>
            <Btn small onClick={() => void saveDefaults()} disabled={defaultsBusy}>
              {defaultsBusy ? 'Saving…' : 'Save as the railway default'}
            </Btn>
            <span className="stu-note"> Every session from now on starts with this exception applied. A rebuild (★ Build Model) is needed for it to reach the figures.</span>
          </>
        }
        scope={{
          line: 'Pickering → Whitby · 24.0 mi',
          modes: [{ label: 'Through', kind: 'through' }, ...((mode === 'grosmont' || slices.some((s) => s.mode === 'grosmont')) ? [{ label: 'Grosmont', kind: 'grosmont' as const }] : [])],
        }}
        governance={{
          owner: upn ? upn.split('@')[0] : 'Reports',
          dataAsAt: new Date().toISOString().slice(0, 10),
          baseline: baselineWtt?.available ? { label: 'thebigplan WTT', kind: 'info' } : { label: 'sales-derived (fallback)', kind: 'warn' },
          // When the season was last built — the newest built month — so the
          // planner knows how current the modelling is (Chris, 2026-10-28).
          tti: ttiLabel(tti),
          lastBuilt: (() => {
            const last = slices.filter((s) => builtSlice(s)).map((s) => s.updated_at).filter(Boolean).sort().pop()
            if (!last) return null
            const d = new Date(last)
            return Number.isNaN(d.getTime()) ? null : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
          })(),
          calibration: backtest ? { label: `±${Math.round(backtest.mape * 100)}% error${backtest.mape > 0.2 ? ' — high, treat £ as indicative' : ''}`, kind: backtest.mape > 0.2 ? 'warn' : 'good' } : null,
        }}
        calibrationDetail={data ? <Panel title="Model accuracy — how the calibration is measured" sub="the engine is replayed against known sales; this ± error is how far it lands out. It checks the model, not your timetable." collapsible defaultOpen={false}><BacktestPanel result={backtest} busy={backtestBusy} season={data.prev_season ?? data.season} onRun={() => runCalibration(false)} /></Panel> : undefined}
        modelSettings={dataReady && evalResult && costs
          ? <section className="sh-card sh-card--pad"><div className="sh-section-label">How the model runs</div>{renderModelSettings()}</section>
          : <section className="sh-card sh-card--pad"><div className="sh-section-label">How the model runs</div><p className="stu-note">The model settings appear once the season's demand has loaded.</p></section>}
        resetCard={
          <section className="sh-card sh-card--pad">
            <div className="sh-section-label">Start from the railway</div>
            <p className="stu-note">
              Puts everything back to how a brand-new session begins: the design grid reloads the real working timetable,
              the operating window re-derives from it, the sliders return to the saved railway defaults, and any design,
              overrides, narrative and advisor work in progress are cleared. The session itself — its name, saved slices
              and history — is untouched.
            </p>
            <div className="sh-mt">
              {resetConfirm ? (
                <>
                  <Btn kind="danger" small onClick={() => { resetModelling(); setResetConfirm(false) }}>Reset — clear the working design</Btn>{' '}
                  <Btn kind="quiet" small onClick={() => setResetConfirm(false)}>Keep working</Btn>
                </>
              ) : (
                <Btn small onClick={() => setResetConfirm(true)}>Reset to railway defaults</Btn>
              )}
            </div>
          </section>
        }
      />
    )
    : stage === 'cost' ? (
      // Rolling stock in use and Fleet availability moved here from the Brief,
      // as the first sub-tabs (Chris, 2026-10-28: "it makes no sense on the
      // Brief page").
      <>
        {subTabs([['rolling', 'Rolling stock in use'], ['fleet', 'Fleet availability'], ['pre', 'Costs (Pre-Modelling)'], ['results', 'Costs (Results)']] as const, costTab, setCostTab)}
        {costTab === 'rolling'
          ? <RollingStockPanel locos={locos} loading={locosLoading} err={locosErr} onReload={reloadLocos} />
          : costTab === 'fleet'
          ? <FleetPlannerPanel locos={locos} cover={fleetCoverByMonth} roster={seasonRoster}
          months={monthOptions.map((mo) => ({ mk: mo.value, label: `${MONTHS[Number(mo.value.split('-')[1]) - 1] ?? '?'} ${mo.value.slice(0, 4)}`, dates: (monthDayGroups(mo.value)?.groups ?? []).filter((g) => !unscorableColour(g.colour)).flatMap((g) => g.dates) }))} />
          : (
            <CostModelStage
              rates={rates} onChange={setRates} onMakeDefault={saveDefaults} defaultsBusy={defaultsBusy} defaultsNote={defaultsNote}
              locked={costLocked} onToggleLocked={() => setCostLocked((v) => !v)}
              costs={costs}
              monthDays={costMonthDays}
              onPersist={(m) => void saveDefaults(m)}
              basis={costBasis} basisOptions={basisOptions} onBasisChange={setCostBasis}
              costActivity={costActivity} costConfig={costConfig} onCostConfigChange={setCostConfig}
              costData={studioCostsData} costLoading={studioCostsLoading} costErr={studioCostsErr} legacySeed={costLegacySeed}
              rolling={rollingInput}
              view={costTab}
            />
          )}
      </>
    )
    : stage === 'calendar' ? (
      // Timetable = the running calendar and the baseline, one stage with two
      // sub-tabs (Chris, 2026-10-28; they were stages 3 and 4).
      <>
        {subTabs([['calendar', 'Running calendar'], ['baseline', 'Baseline']] as const, ttTab, setTtTab)}
        {ttTab === 'calendar' ? calendarBody : baselineBody}
      </>
    )
    : stage === 'build' ? (dataReady ? renderBuild() : notReady)
    : stage === 'explore' ? renderExplore()
    : (dataReady ? renderResults() : notReady)

  const shellBanner = error
    ? <Callout kind="error" title="Couldn't load Studio data">{error}</Callout>
    : (!error && data && monthOptions.length === 0)
      ? <Callout kind="warn" title="No demand data yet"><code>agg.ResearchServiceDemand</code> rebuilds nightly with the reports refresh.</Callout>
      : sessionError ? <Callout kind="error" title="Session">{sessionError}</Callout> : undefined

  return (
    <>
      <StudioShell
        sessionName={sessionName}
        status={STATUS_LABEL[sessionStatus]}
        scope={scope}
        savedLabel={savedAt ?? 'Not saved yet'}
        onOpen={() => void openList()}
        onNew={() => setNewDraft({ name: '', question: '' })}
        onSave={() => void saveSession()}
        saving={sessionBusy}
        saveDisabled={!data}
        stages={stageDefs}
        activeStage={stage}
        onSelectStage={setStage}
        interventions={
          // Points where the Brief bound — after Results on the stage bar,
          // opened only by the planner (Chris, 2026-10-28).
          <InterventionsMenu months={reviewMonths} busy={seasonDesigning}
            onContinue={(change, mks) => void applyFixAndRebuild(change, mks)} />
        }
        buildAction={
          // The season build sits at the END of the stage bar, reachable from
          // every stage (Chris, 2026-10-28: on the Results page "it makes no
          // sense where it is").
          <button type="button" className="sh-sbtn sh-sbtn--build"
            disabled={seasonDesigning || baselineLoading || !baselineWtt?.available}
            onClick={() => void designSeason()}
            title="Build the modelling for the whole season — every running day on its own colour/day-type/capacity/paths — then the Modelling and Results figures update.">
            {seasonDesigning ? 'Building…' : baselineLoading ? 'Loading baseline…' : '★ Build Model'}
          </button>
        }
        banner={shellBanner}
      >
        {stageContent}
      </StudioShell>


      {newDraft && (
        <Modal title="New modelling session" width={560} onClose={() => setNewDraft(null)}
          note="Name the session and the question it answers. Starting it clears the working design back to the railway defaults; saved sessions are untouched. Edit both later with the pen in Open….">
          <div className="sh-section-label">Session name</div>
          <input className="sh-brief-name" type="text" value={newDraft.name} autoFocus placeholder="Name this session…"
            onChange={(e) => setNewDraft({ ...newDraft, name: e.target.value })} />
          <div className="sh-section-label sh-mt">The question this session answers</div>
          <textarea className="sh-brief-q" value={newDraft.question}
            placeholder="e.g. What should we run across the 2027/28 peak — and does splitting the Whitby leg at Grosmont beat rebuilding the through timetable?"
            onChange={(e) => setNewDraft({ ...newDraft, question: e.target.value })} />
          <div className="sh-mt" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn kind="quiet" onClick={() => setNewDraft(null)}>Cancel</Btn>
            <Btn kind="primary" disabled={!newDraft.name.trim()} onClick={() => { newSession(newDraft.name, newDraft.question); setNewDraft(null) }}>Start the session</Btn>
          </div>
        </Modal>
      )}

      {openModal && (
        <Modal title="Open a modelling session" width={560} onClose={() => setOpenModal(false)} note="Every saved session, newest first. Opening one switches the Studio to its season and loads its slices, cost model and running set; ✎ edits a session's name and question.">
          {sessionsList.length === 0 ? (
            <p className="stu-note">No saved sessions yet.</p>
          ) : (
            <table className="stu-table">
              <thead><tr><th>Name</th><th>Season</th><th>Status</th><th>Updated</th><th /></tr></thead>
              <tbody>
                {sessionsList.map((s) => editRow?.id === s.id ? (
                  <tr key={s.id}>
                    <td colSpan={5}>
                      <div className="sh-section-label">Session name</div>
                      <input className="sh-brief-name" type="text" value={editRow.name} autoFocus
                        onChange={(e) => setEditRow({ ...editRow, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') void saveRename(); if (e.key === 'Escape') setEditRow(null) }} />
                      <div className="sh-section-label sh-mt">The question this session answers</div>
                      <textarea className="sh-brief-q" value={editRow.question} onChange={(e) => setEditRow({ ...editRow, question: e.target.value })} />
                      <div className="sh-mt">
                        <Btn kind="primary" small disabled={sessionBusy || !editRow.name.trim()} onClick={() => void saveRename()}>{sessionBusy ? 'Saving…' : 'Save'}</Btn>{' '}
                        <Btn kind="quiet" small onClick={() => setEditRow(null)}>Cancel</Btn>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={s.id}>
                    <td><strong>{s.name}</strong>{s.question ? <div className="stu-note">{s.question}</div> : null}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{s.season}{s.season === data?.season ? '' : <div className="stu-note">opens in {s.season}</div>}</td>
                    <td>{STATUS_LABEL[s.status]}</td>
                    <td className="stu-note">{s.updated_at?.slice(0, 10)}</td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <Btn kind="quiet" small title="Edit this session's name and question" onClick={() => { setConfirmDeleteId(null); setEditRow({ id: s.id, name: s.name, question: s.question ?? '' }) }}>✎</Btn>{' '}
                      <Btn kind="primary" small onClick={() => void openSessionById(s.id)}>Open</Btn>{' '}
                      {confirmDeleteId === s.id ? (
                        <>
                          <Btn kind="danger" small disabled={sessionBusy} onClick={() => void removeSession(s.id)}>Delete for good</Btn>{' '}
                          <Btn kind="quiet" small onClick={() => setConfirmDeleteId(null)}>Keep</Btn>
                        </>
                      ) : (
                        <Btn kind="quiet" small title="Deletes the session, its slices and its version history" onClick={() => setConfirmDeleteId(s.id)}>Delete</Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {selection && scenario && costs && baseline && cardCtx && (
        <>
          {/* ---- modals ------------------------------------------------- */}
          {strandedOpen && (
            <Modal
              title={`Unreturned passengers — where and why (${dayLabel}, ${monthLabel})`}
              width={700}
              onClose={() => setStrandedOpen(false)}
              note={`Figures are per ${period}. A "trains full" row can be fixed with a bigger set, another return working, or by placing people on earlier services. A "no train" row means nothing serving that journey runs after they arrive — only a new departure helps.`}
            >
              {scenario.stranded.length === 0 ? (
                <p style={{ color: '#2e7d32' }}>Everyone carried out gets home under this timetable.</p>
              ) : (
                <table className="stu-table">
                  <thead>
                    <tr><th>Went out on</th><th>Journey home</th><th className="num">Stranded</th><th>Earliest return</th><th>Why</th></tr>
                  </thead>
                  <tbody>
                    {scenario.stranded.map((g, i) => (
                      <tr key={i}>
                        <td><strong>{g.time}</strong></td>
                        <td>{g.route}</td>
                        <td className="num" style={{ color: '#b23b2e', fontWeight: 700 }}>{num0.format(Math.round(g.count))}</td>
                        <td>{fmtMin(g.earliestMin)}</td>
                        <td className="stu-note">{g.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {scenario.oneWay.length > 0 && (
                <>
                  <div className="stu-subhead">One-way / overnight travellers (not counted as unreturned)</div>
                  <p className="stu-modal-note">
                    These passengers arrive so late that <strong>no legal train inside the operating window</strong> (last
                    train {lastTrain}) could ever take them home — by construction they are singles, overnight visitors or
                    locals going home. Anyone a new train COULD serve counts as unreturned instead.
                  </p>
                  <table className="stu-table">
                    <tbody>
                      {scenario.oneWay.map((o, i) => (
                        <tr key={i}><td style={{ width: 70 }}><strong>{o.time}</strong></td><td>{o.route}</td><td className="num">{num0.format(Math.round(o.count))}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {scenario.stranded.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Btn kind="primary" onClick={() => {
                    const cfg = fitConfig()
                    if (!cfg) return
                    const r = rescueTrains({ ...cfg }, activeDeps, true, 0.5)
                    updateActive(() => r.deps)
                    setFitLog(r.log)
                    setStrandedOpen(false)
                  }}>
                    Add return workings to bring everyone home
                  </Btn>
                  <span className="stu-note">
                    Adds departures (biggest rescue first) within {firstTrain}–{lastTrain}, respecting headway, the
                    single-line crossing rule and the path budget.
                  </span>
                </div>
              )}
            </Modal>
          )}

          {staysOpen && (
            <Modal
              title={`Time at destination — by service (${dayLabel}, ${monthLabel})`}
              width={740}
              onClose={() => setStaysOpen(false)}
              note={`Each row is one outward service paired with the return its passengers came home on; counts are per ${period}. Red is under an hour at the destination, amber under two — check those services are worth running.`}
            >
              <table className="stu-table">
                <thead>
                  <tr><th>Went out on</th><th>Outward journey</th><th>Returned on</th><th className="num">Passengers</th><th className="num">Time at destination</th></tr>
                </thead>
                <tbody>
                  {scenario.stays.filter((st) => st.count >= 0.5).map((st, i) => {
                    const col = st.stayMin < 60 ? '#b23b2e' : st.stayMin < 120 ? '#b45309' : '#2e7d32'
                    return (
                      <tr key={i}>
                        <td><strong>{st.time}</strong></td>
                        <td>{st.outRoute}</td>
                        <td><strong>{st.retTime}</strong></td>
                        <td className="num">{num0.format(Math.round(st.count))}</td>
                        <td className="num" style={{ color: col, fontWeight: 700 }}>{stayFmt(st.stayMin)}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Returned in total</td>
                    <td className="num">{num0.format(Math.round(scenario.stays.reduce((t, st) => t + st.count, 0)))}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </Modal>
          )}

          {svcOpen && (() => {
            const dep = activeDeps.find((x) => x.id === svcOpen)
            const res = scenario.perDep.find((x) => x.id === svcOpen)
            if (!dep || !res) return null
            const mixCls = viewClass ?? (isWeek ? 'all' : dayClass)
            const mix = mixes?.[mixCls] ?? mixes?.all ?? []
            const tot = mix.reduce((t, r) => ({ qty: t.qty + r.qty, net: t.net + r.net }), { qty: 0, net: 0 })
            const util = res.capacity > 0 ? Math.round((res.allocated / res.capacity) * 100) : 0
            return (
              <Modal
                title={`${dep.time} ${routeLabel(net, dep.o, dep.d)} — capacity breakdown`}
                width={640}
                onClose={() => setSvcOpen(null)}
                note={`Set ${plan?.byDep[dep.id] ?? '—'} · ${locoBy[dep.id]?.label ?? '—'} · figures are the average running day. Originating journeys pay the fare; returns travel on the same ticket.`}
              >
                <div className="stu-cards">
                  <StatCard label="Capacity" value={String(res.capacity)} />
                  <StatCard label="Originating" value={String(Math.round(res.outAlloc))} tone="good" />
                  <StatCard label="Returns" value={String(Math.round(res.retAlloc))} tone="bad" />
                  <StatCard label="Free seats" value={String(Math.max(0, Math.round(res.capacity - res.allocated)))} />
                  <StatCard label="Utilisation" value={`${util}%`} />
                  <StatCard label="Originating revenue" value={gbp(res.outRevenue)} tone="good" />
                </div>
                <div className="stu-subhead">Estimated ticket split — originating journeys</div>
                {mix.length === 0 || tot.net <= 0 || res.outAlloc < 0.5 ? (
                  <p className="stu-note">{mixes ? 'No originating passengers (a pure return working), or no observed ticket mix for this day type.' : 'Loading ticket mix…'}</p>
                ) : (
                  <table className="stu-table">
                    <thead><tr><th>Ticket / item</th><th className="num">Est. tickets</th><th className="num">Est. net £</th></tr></thead>
                    <tbody>
                      {mix.filter((r) => r.net > 0).slice(0, 15).map((r) => (
                        <tr key={r.item}>
                          <td>{r.item}</td>
                          <td className="num">{tot.qty > 0 ? Math.round((r.qty / tot.qty) * res.outAlloc) : 0}</td>
                          <td className="num">{gbp((r.net / tot.net) * res.outRevenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td>Originating total</td><td className="num">{Math.round(res.outAlloc)}</td><td className="num">{gbp(res.outRevenue)}</td></tr>
                      <tr><td style={{ color: '#b23b2e' }}>Returns (same ticket, no extra fare)</td><td className="num" style={{ color: '#b23b2e' }}>{Math.round(res.retAlloc)}</td><td className="num" style={{ color: '#b23b2e' }}>£0</td></tr>
                    </tfoot>
                  </table>
                )}
              </Modal>
            )
          })()}

          {pendingDisable && (() => {
            const dep = activeDeps.find((x) => x.id === pendingDisable.id)
            const followers = pendingDisable.chain.map((id) => activeDeps.find((x) => x.id === id)).filter((x): x is Departure => !!x)
            if (!dep) return null
            return (
              <Modal
                title="This train forms later workings"
                width={520}
                onClose={() => setPendingDisable(null)}
                note={`Removing only the ${dep.time} leaves ${followers.length === 1 ? 'that train' : 'those trains'} with no stock at ${net.stations[dep.d].name} — the stock-balance check will flag it and the set plan may re-chain or disable them.`}
              >
                <p style={{ fontSize: '0.82rem' }}>
                  The <strong>{dep.time} {routeLabel(net, dep.o, dep.d)}</strong> delivers its engine and rake for:
                </p>
                <ul className="stu-log">
                  {followers.map((f) => <li key={f.id}><strong>{f.time}</strong> {routeLabel(net, f.o, f.d)}</li>)}
                </ul>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <Btn kind="danger" onClick={() => {
                    const ids = new Set([pendingDisable.id, ...pendingDisable.chain])
                    updateActive((prev) => prev.map((x) => (ids.has(x.id) ? { ...x, enabled: false } : x)))
                    setPendingDisable(null)
                  }}>
                    Remove this + {followers.length} following turn{followers.length === 1 ? '' : 's'}
                  </Btn>
                  <Btn kind="ghost" onClick={() => {
                    updateActive((prev) => prev.map((x) => (x.id === pendingDisable.id ? { ...x, enabled: false } : x)))
                    setPendingDisable(null)
                  }}>
                    Remove just this one
                  </Btn>
                  <Btn kind="quiet" onClick={() => setPendingDisable(null)}>Cancel</Btn>
                </div>
              </Modal>
            )
          })()}

          {editDep && (
            <Modal
              title="Change this working"
              width={400}
              onClose={() => setEditDep(null)}
              note="Applying re-runs everything downstream: loads, carriage sets, engine diagrams, costs, the schedule and the timetable."
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="time" value={editDep.time} onChange={(e) => setEditDep({ ...editDep, time: e.target.value })} />
                <select value={editDep.o} onChange={(e) => setEditDep({ ...editDep, o: Number(e.target.value) })}>
                  {net.stations.map((s, i) => <option key={s.code} value={i}>{s.name}</option>)}
                </select>
                <span className="stu-note">→</span>
                <select value={editDep.d} onChange={(e) => setEditDep({ ...editDep, d: Number(e.target.value) })}>
                  {net.stations.map((s, i) => <option key={s.code} value={i}>{s.name}</option>)}
                </select>
                <Btn kind="gold" onClick={() => {
                  const min = toMin(editDep.time)
                  if (Number.isFinite(min) && editDep.o !== editDep.d) {
                    const dir = editDep.d > editDep.o ? 'Northbound' : 'Southbound'
                    updateActive((prev) => prev
                      .map((x) => (x.id === editDep.id ? { ...x, time: editDep.time, minutes: min, o: editDep.o, d: editDep.d, direction: dir, group: undefined } : x))
                      .sort((a, b) => a.minutes - b.minutes))
                  }
                  setEditDep(null)
                }}>Apply</Btn>
                <Btn kind="quiet" onClick={() => setEditDep(null)}>Cancel</Btn>
              </div>
            </Modal>
          )}
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

/** The Mode A geometry — used for the baseline and for anything purely spatial. */
const net0: Network = buildNetwork('through')

/** Merge two weighted class results into one period total. */
function mergeScenarios(a: Scenario, b: Scenario): Scenario {
  const byId = new Map(a.perDep.map((p) => [p.id, { ...p }]))
  for (const p of b.perDep) {
    const cur = byId.get(p.id)
    if (cur) {
      cur.allocated += p.allocated
      cur.outAlloc += p.outAlloc
      cur.retAlloc += p.retAlloc
      cur.outRevenue += p.outRevenue
    } else byId.set(p.id, { ...p })
  }
  const lost = new Map(a.lostOutbound.map((l) => [`${l.direction ?? ''}|${l.time}`, { ...l }]))
  for (const l of b.lostOutbound) {
    const k = `${l.direction ?? ''}|${l.time}`
    const cur = lost.get(k)
    if (cur) cur.lost += l.lost
    else lost.set(k, { ...l })
  }
  return {
    revenue: a.revenue + b.revenue,
    carried: a.carried + b.carried,
    lostSales: a.lostSales + b.lostSales,
    unreturned: a.unreturned + b.unreturned,
    lostInterchange: a.lostInterchange + b.lostInterchange,
    connected: a.connected + b.connected,
    perDep: [...byId.values()].sort((x, y) => toMin(x.time) - toMin(y.time)),
    lostOutbound: [...lost.values()].sort((x, y) => toMin(x.time) - toMin(y.time)),
    stranded: [...a.stranded, ...b.stranded],
    earlyReturns: [...a.earlyReturns, ...b.earlyReturns],
    stays: [...a.stays, ...b.stays],
    oneWay: [...a.oneWay, ...b.oneWay],
    transfers: [...a.transfers, ...b.transfers],
    slotHome: (() => {
      const out: Scenario['slotHome'] = { ...a.slotHome }
      for (const [k, v] of Object.entries(b.slotHome)) {
        const cur = out[k]
        out[k] = cur ? { pax: cur.pax + v.pax, unreturned: cur.unreturned + v.unreturned, oneWay: cur.oneWay + v.oneWay } : { ...v }
      }
      return out
    })(),
  }
}

/** Saved engine pins made safe. */
function cleanPins(raw: unknown): EnginePin[] {
  return (Array.isArray(raw) ? (raw as EnginePin[]) : []).filter((p) => p && typeof p.turnKey === 'string' && Number.isFinite(p.engineId) && /^\d{4}-\d{2}-\d{2}$/.test(String(p.fromDate)))
}
/** The planner's engine pins as last saved with the session: the most recently
 *  saved slice that carries a list (every hand save writes the whole live list). */
function latestSlicePins(rows: SessionSlice[]): EnginePin[] | null {
  const withPins = rows
    .map((s) => { try { return { at: s.updated_at, pins: (JSON.parse(s.state_json) as { enginePins?: unknown }).enginePins } } catch { return null } })
    .filter((x): x is { at: string; pins: unknown } => !!x && Array.isArray(x.pins))
    .sort((a, b) => String(b.at).localeCompare(String(a.at)))
  return withPins.length > 0 ? cleanPins(withPins[0].pins) : null
}

/** The fleet without its register engines — what a session and the railway
 *  default store (the register is its own table, never copied into either). */
function stripLocos(f: FleetCaps): FleetCaps {
  const rest = { ...f }
  delete rest.locos
  return rest
}

/** A day's named-engine figures for a month snapshot: each engine's timetabled
 *  miles (the day IS the per-day figure) and 1 for every engine that worked. */
function engineSnap(c: CostBreakdown): Pick<YearSideSnap, 'engineMilesPerDay' | 'engineDaysPerDay'> {
  if (Object.keys(c.engineMiles).length === 0) return {}
  return { engineMilesPerDay: c.engineMiles, engineDaysPerDay: Object.fromEntries(c.engineWorked.map((k) => [k, 1])) }
}

/** A fleet with a Brief change applied — the same merge the build and the saved state use. */
function fleetWith(f: FleetCaps, over?: MonthFixChange): FleetCaps {
  if (!over) return f
  return {
    ...f,
    maxSteam: over.maxSteam ?? f.maxSteam,
    maxDiesel: over.maxDiesel ?? f.maxDiesel,
    maxEcsDiesel: over.maxEcsDiesel ?? f.maxEcsDiesel,
  }
}

/** Unused re-exports kept so the module surface is obvious to future readers. */
export type { CarriageSet, Departure, Network, SetPlan, StudioMode, Traction }
export { capOf, setLabel }
